'use client';
// Non-conformités (design isNC) — « Tout ce qui ne va pas, on l'écrit. »
// Sous-onglets : Registre & traitement (master-detail + déclaration) et
// Statistiques (StatistiquesNC.tsx : période, tout le laboratoire ou un correspondant, envoi au correspondant).
import React, { useEffect, useMemo, useState } from 'react';
import { useGed, userName } from '@/lib/store';
import { actionProposee } from '@/lib/proposition-nc';
import {
  NC_SOURCES, NC_GRAVITES, NC_TYPES, NC_STATES, CAUSES_PANNE, IMPACTS_RESULTATS, ACTION_STATES, AC_BLOQUANTES,
  vocabMeta, graviteMeta, fmtDate, fmtDateTime, joursDepuis, SEUILS_DEFAUT, DEFAULT_PROCESSUS,
  MOTIF_DEROGATION_MIN,
  canauxPour, libelleCanal, NC_CANAUX, processusSMQ, aujourdhuiIso,
  NC_NATURES, natureMeta, natureDe, REC_CANAUX, secteurDeNature, processusDeNature, processusDetectionDe, type ObjetNC } from '@/lib/meta';
import { jourIso } from '@/lib/dates';
import { telechargerExport } from '@/lib/export-registre';
import { useRemonteEnHaut, VOILE,
  TitrePage, I, card, inputStyle,
  SkeletonTiles, EmptyState, FilterBar, FieldError, RequiredHint, Help, useBusy, useEscape, Selecteur,
  useConfirmation, Modale, COUCHES, ConfirmButton,
} from '@/components/ui';
import { Liens } from '@/components/Liens';
import { Tableau, ligneOuvrable, CelluleLien } from '@/shared/Tableau';
import { depuisNc, sontEgales, ACTION_CORRECTIVE, type AnalyseCapa } from '@/lib/analyse-capa';
import { AnalyseCauseEditeur, AnalyseCauseLecture } from '@/components/AnalyseCause';
import { PiecesNC, lireEnBase64 } from '@/components/PiecesNC';
import { fetchEcriture } from '@/lib/ecrire';
import { styleBouton } from '@/shared/boutonStyles';
import { FriseEtapes, ConsigneEtape, NavEtapes, FRISE_COLLEE, PIED_COLLE, type OngletParcours } from '@/components/ParcoursNC';
import { prochainGeste } from '@/lib/parcours-action';
import { ChoixCorrespondant, useNomsContacts } from '@/components/ChoixCorrespondant';
import { ChoixProduit, useProduits } from '@/components/ChoixProduit';
import { resteAFaire, etapeCourante } from '@/lib/parcours-nc';
import { groupesProblemesDuProcessus, groupesParNature, GROUPE_DEMANDE, type ProblemeNC } from '@/lib/problemes-nc';
import { ajouterDossiers, retirerDossier, phraseDoublons, phraseNoms, TEXTE_MAX as DOSSIER_TEXTE_MAX,
  type LigneDossier } from '@/lib/dossiers-saisie';
import { ExplicationNiveaux } from '@/components/ExplicationNC';
import { cheminDe } from '@/lib/adresse';
import { useOngletAdresse } from '@/lib/useAdresse';
import { LienFiche } from '@/components/LienFiche';
import { PointDeRegle } from '@/components/PointDeRegle';
import { EntretienDeclarationNC, type FicheEntretien, type Echange as EchangeEntretien } from '@/components/EntretienDeclarationNC';
import { ConversationNC } from '@/components/ConversationNC';
import { BoutonJournal } from '@/components/BoutonJournal';
import { JournalFiche } from '@/components/JournalFiche';
import { useFormulaire } from '@/components/Formulaire';
import { PanneauDossier } from '@/components/Dossier';
import { useSortieFenetre, useRefusVoile, AlerteSortie } from '@/shared/fenetreModale';
import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { ActionRedaction } from '@/components/ActionRedaction';
// La règle de clôture et son bandeau, partagés avec l'audit et la réclamation.
import { BlocageCloture } from '@/components/Cloture';
// Le panneau « dire ce que le geste emporte », déjà utilisé par les retraits.
import { SuppressionRaisonnee } from '@/components/SuppressionRaisonnee';
// Le dossier d'action, réutilisé TEL QUEL dans le panneau superposé (N2-4).
// `Actions.tsx` n'importe rien d'ici : la dépendance reste à sens unique.
import { ActionDossier } from '@/components/screens/Actions';
import { ChampDuree } from '@/components/screens/ressources/ChampsMaintenance';
import { SaisirMaintenance } from '@/components/screens/ressources/SaisirMaintenance';
import { peutTrancherNC, peutOuvrirNCNonRetenue, tientLeRegistreNC, peutDecloturerNC } from '@/lib/droits-nc';
import type { GedAction, GedNC } from '@/lib/types';
import { Norme } from '@/components/Norme';

import { StatistiquesNC } from './StatistiquesNC';
import { Segment } from '@/shared/Segment';
/** Une non-conformité non soldée. Source unique — le tableau de bord s'en sert
 *  aussi, pour ne pas diverger du registre.
 *
 *  Une NC ANNULÉE n'est pas ouverte : c'est une saisie erronée, retirée des
 *  compteurs. Elle n'est pas non plus « clôturée » — rien n'a été traité. */
/* ⚠️ UNE NC SOUMISE N'EST PAS ENCORE UN ÉCART (30/08/2026). Elle attend d'être
   retenue ou rejetée : la compter parmi les non-conformités ouvertes ferait
   monter le compteur avant que quiconque ait dit si c'en est une — c'est
   exactement ce que le triage existe pour empêcher. Elle a son propre compteur,
   « à trancher ». */
export const ncOuverte = (n: GedNC) => !['cloturee', 'annulee', 'soumise'].includes(n.statut);
export const ncSoumise = (n: GedNC) => n.statut === 'soumise';

/* ⚠️ LES TROIS PRÉDICATS DU REGISTRE ONT DÉMÉNAGÉ le 08/10/2026 vers
   `@/lib/droits-nc` : `Reclamations.tsx` en a besoin, et lui faire importer
   cet écran entier pour deux comparaisons de chaînes était un cycle
   d'import en puissance. Ils sont RÉEXPORTÉS ici — une quinzaine de sites
   les importent déjà depuis `./NC`, et les réécrire serait autant
   d'occasions de se tromper sur un droit. */
export { peutTrancherNC, peutOuvrirNCNonRetenue, tientLeRegistreNC, peutDecloturerNC } from '@/lib/droits-nc';

function Pill({ meta }: { meta: { label: string; bg: string; fg: string } }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{meta.label}</span>;
}
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', marginBottom: 5 };

/** Un intitulé ET son « i » sur la MÊME ligne (RQ, 08/10/2026 : « pas besoin
 *  du saut de ligne »). `lbl` est en bloc : posé après lui, le « i » tombait
 *  sous l'intitulé, et le champ sous le « i ». */
const ENTETE_I: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 2, marginBottom: 4 };

const AIDE_CAPA = 'CAPA : Corrective And Preventive Action — l’action qui traite la cause de la non-conformité, et non seulement son effet immédiat (lequel relève de la « correction »).';
/** Au-delà de ce délai, une NC encore ouverte est signalée : sans repère
 *  d'ancienneté, une NC de six mois se lit comme celle d'hier. Le délai
 *  acceptable dépend du laboratoire — il vit donc dans le paramétrage, la
 *  constante ne servant que de repli. */
function useSeuilAnciennete() {
  const { settings } = useGed();
  const v = settings?.notif?.ncAncienneteJ;
  return typeof v === 'number' && v > 0 ? v : SEUILS_DEFAUT.ncAncienneteJ;
}

/** Ancienneté d'une NC ouverte, virant à l'ambre au-delà du seuil. */
/**
 * Rang de tri des gravités — DÉRIVÉ du vocabulaire, jamais recopié.
 *
 * `NC_GRAVITES` va de la moins grave à la plus grave ; trier « par gravité »
 * doit faire remonter les CRITIQUES en premier. On inverse donc la position,
 * plutôt que d'écrire une table à la main qui cesserait d'être vraie le jour où
 * une gravité s'ajoute. Trier sur le libellé donnerait « critique, majeure,
 * mineure » par pur hasard alphabétique — et l'inverse en anglais.
 */
const RANG_GRAVITE: Record<string, number> = Object.fromEntries(
  NC_GRAVITES.map((g, i) => [g.key, NC_GRAVITES.length - 1 - i]),
);

/**
 * Une action au plan est-elle ATTENDUE sur cette non-conformité ?
 *
 * ⚠️ DEUX CONDITIONS, et la seconde compte autant que la première : la NC doit
 * être encore OUVERTE. Un dossier clôturé sans action l'a été par une clôture
 * motivée — le verdict de clôture exige une action corrective écrite, et une
 * dérogation du RQ pour s'en passer. Lui laisser une pastille « attendu » pour
 * toujours en ferait un reproche sur une décision déjà prise et tracée, et
 * c'est ainsi qu'on apprend à ne plus lire les pastilles.
 *
 * ⚠️ `RANG_GRAVITE` DÉCROÎT avec la sévérité (critique = 0) : comparer à
 * `majeure` couvre donc « majeure » ET « critique » sans retaper la liste des
 * niveaux concernés, et une gravité ajoutée au vocabulaire se range d'elle-même.
 */
function planAttendu(nc: GedNC): boolean {
  if (!ncOuverte(nc)) return false;
  return (RANG_GRAVITE[String(nc.gravite)] ?? 99) <= RANG_GRAVITE.majeure;
}

/** Jours d'ouverture, pour le TRI de la colonne « Ancienneté ».
 *  Une NC close ou annulée ne vieillit pas : elle vaut −1 et se range donc
 *  après toutes les ouvertes, ce qui est l'ordre qu'on cherche en triant. */
function joursOuverts(nc: GedNC): number {
  if (!ncOuverte(nc)) return -1;
  return joursDepuis(nc.dateDeclaration) ?? -1;
}

function Anciennete({ nc }: { nc: GedNC }) {
  const seuil = useSeuilAnciennete();
  // Même règle que les compteurs : une NC annulée n'est pas ouverte, elle ne
  // « vieillit » donc pas. L'afficher ferait mentir la seule mesure de retard
  // que porte la fiche.
  if (!ncOuverte(nc)) return null;
  const j = joursDepuis(nc.dateDeclaration);
  if (j === null) return null;
  /* ⚠️ UNE CELLULE VIDE SE LIT « DONNÉE MANQUANTE », PAS « ZÉRO » (relevé
     MAJ-12 du 26/08/2026). Une NC du jour même n'affichait rien du tout dans la
     colonne Ancienneté, à côté de « ouverte depuis 2 j » : on ne pouvait pas
     distinguer « déclarée il y a une heure » de « date de déclaration absente ».
     Zéro jour est une mesure, et elle se dit. */
  if (j < 1) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: 'var(--surface-sunken)', color: 'var(--text-muted)' }}>
        ouverte aujourd’hui
      </span>
    );
  }
  const vieille = j > seuil;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 9px', borderRadius: 'var(--radius-pill)', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', background: vieille ? 'var(--amber-50)' : 'var(--surface-sunken)', color: vieille ? 'var(--amber-700)' : 'var(--text-muted)' }}>
      ouverte depuis {j} j
    </span>
  );
}

/** Critères d'échantillon ACTIFS — la liste exhaustive des situations
 *  pré-analytiques. Chargée une fois, au premier affichage du formulaire. */
function useCriteresPreana(actif: boolean): ProblemeNC[] {
  const [criteres, setCriteres] = useState<ProblemeNC[]>([]);
  React.useEffect(() => {
    if (!actif || criteres.length) return;
    let vivant = true;
    void fetch('/api/ged/critere-echantillons?fields[0]=code&fields[1]=libelle&fields[2]=actif&pagination[pageSize]=100&sort=code:asc', { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => {
        if (!vivant) return;
        setCriteres(((d?.data as { code: string; libelle: string; actif?: boolean }[]) || [])
          .filter((c) => c.actif !== false)
          .map((c) => ({ code: String(c.code), libelle: String(c.libelle) })));
      })
      .catch(() => {});
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif]);
  return criteres;
}

/** LA LISTE DES PROBLÈMES DÉPEND DU PROCESSUS (RQ, 13/10/2026), pas de la source.
 *  Les listes restent tenues par secteur ; le processus choisit lesquelles
 *  s'affichent (`lib/problemes-nc.ts`). Partagée par la déclaration et la
 *  correction de la fiche, pour qu'elles proposent la même chose. */
/** LA LISTE « QUEL PROBLÈME ? » SELON LA NATURE (13/10/2026) — `groupesParNature`.
 *  `natureDu` dit, dans la liste complète, à quelle nature appartient chaque
 *  problème : le choisir renseigne la nature. Une réclamation ajoute sa liste. */
function useListeProblemesNature(nature: string, canal: string, criteres: ProblemeNC[]) {
  return useMemo(() => {
    const libelleDe = (secteur: string) => NC_SOURCES.find((x) => x.key === secteur)?.label || secteur;
    const groupes = groupesParNature(nature, NC_NATURES, libelleDe, criteres, canal === 'reclamation' ? ['reclamation'] : []);
    const natureDu = new Map<string, string>();
    for (const g of groupes) for (const it of g.items) natureDu.set(it.code, g.nature);
    return { groupes: groupes as ReturnType<typeof groupesProblemesDuProcessus>, problemes: groupes.flatMap((g) => g.items), natureDu };
  }, [nature, canal, criteres]);
}


/** LE CORRESPONDANT À QUI L'ERREUR EST IMPUTÉE (RQ, 13/10/2026 : « à l'origine
 *  du signalement, ou de l'erreur ? »). C'est l'ERREUR : la NC entre dans le
 *  relevé qualité mensuel qu'on lui adresse. Celui qui a seulement SIGNALÉ (une
 *  réclamation) ne se met pas ici — on lui imputerait une faute qu'il n'a pas
 *  commise, dans un document qu'il reçoit. Question et aide communes à la
 *  déclaration et à la correction. */
const QUESTION_CORRESPONDANT = 'L’erreur vient-elle d’un correspondant extérieur ?';
const AIDE_CORRESPONDANT = 'À renseigner si c’est le correspondant qui a commis l’écart — par exemple une clinique '
  + 'qui oublie un prélèvement ou envoie une prescription incomplète. La non-conformité lui est alors imputée et '
  + 'figure dans son relevé qualité mensuel. Ne pas le renseigner s’il a seulement signalé le problème '
  + '(réclamation) : laisser vide.';


/** « DE QUOI S'AGIT-IL ? » — la nature, en tuiles (RQ, 13/10/2026). La première
 *  question : elle commande le reste du formulaire. */
function ChoixNature({ id, valeur, onChange }: { id: string; valeur: string; onChange: (v: string) => void }) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <div style={ENTETE_I}><span id={`${id}-lbl`} style={{ ...lbl, marginBottom: 0 }}>De quoi s’agit-il ? *</span><PointDeRegle ancre="nc.nature" /></div>
      <div id={id} role="radiogroup" aria-labelledby={`${id}-lbl`}
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 6 }}>
        {/* Une ancienne valeur (« Réclamation ») reste affichée, cochée, tant qu'on ne la change pas. */}
        {[...(valeur && !NC_NATURES.some((n) => n.key === valeur) && natureMeta(valeur) ? [natureMeta(valeur)!] : []), ...NC_NATURES].map((n) => {
          const on = valeur === n.key;
          return (
            <button key={n.key} type="button" role="radio" aria-checked={on} onClick={() => onChange(n.key)} title={n.aide}
              style={{ textAlign: 'start', fontFamily: 'var(--font-sans)', cursor: 'pointer', borderRadius: 'var(--radius-md)', padding: '8px 10px',
                border: `1.5px solid ${on ? 'var(--color-primary)' : 'var(--border-default)'}`,
                background: on ? 'var(--cyan-50)' : 'var(--surface-card)', display: 'grid', gap: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: on ? 'var(--color-primary-active)' : 'var(--text-strong)' }}>{n.label}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', lineHeight: 1.35 }}>{n.aide}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** « COMMENT L'AVEZ-VOUS DÉTECTÉ ? » — UNE question, en deux groupes (RQ,
 *  13/10/2026). Elle remplace « Qui a relevé l'écart ? », qui s'en déduit :
 *  chaque canal n'appartient qu'à un côté. */
function ChoixDetection({ id, valeur, onChange, requis }: { id: string; valeur: string; onChange: (v: string) => void; requis?: boolean }) {
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={lbl} htmlFor={id}>Comment l’écart a-t-il été détecté ?{requis ? ' *' : ''}</label>
      <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle}>
        <option value="">—</option>
        <optgroup label="Relevé par le laboratoire">
          {canauxPour('interne').map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </optgroup>
        <optgroup label="Relevé par l’extérieur">
          {canauxPour('externe').map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </optgroup>
      </Selecteur>
    </div>
  );
}

const LIBELLE_OBJET: Record<ObjetNC, string> = {
  equipement: 'Équipement concerné', produit: 'Produit concerné (catalogue)', lot: 'Lot concerné', fournisseur: 'Fournisseur concerné',
  analyte: 'Paramètre concerné', document: 'Document concerné', audit: 'Audit concerné', emplacement: 'Local concerné',
};
/** Les locaux du plan du laboratoire — lus seulement si la nature en a besoin.
 *  Lecture refusée (rubrique fermée) : liste vide, le champ reste facultatif. */
function useLocaux(actif: boolean) {
  const [locaux, setLocaux] = useState<{ code: string; libelle: string }[]>([]);
  React.useEffect(() => {
    if (!actif || locaux.length) return;
    let vivant = true;
    void fetch('/api/ged/ged/emplacements', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!vivant || !j) return;
        setLocaux(((j.emplacements || []) as { code: string; libelle: string; actif?: boolean }[])
          .filter((e) => e.actif !== false).map((e) => ({ code: e.code, libelle: e.libelle })));
      })
      .catch(() => {});
    return () => { vivant = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actif]);
  return locaux;
}
/** L'OBJET À RELIER, selon la nature (13/10/2026) — l'équipement en panne, le
 *  lot, le document… Relié à la NC à l'enregistrement : la NC apparaît alors
 *  sur la fiche de l'objet. Choisi dans les listes du laboratoire, jamais tapé.
 *
 *  ⚠️ LE RÉACTIF SE CHOISIT EN CASCADE (RQ, 16/10/2026) : « on choisit dans la
 *  liste, puis on short-liste le lot et les fournisseurs ». Le PRODUIT du
 *  catalogue d'abord ; les lots proposés sont alors les siens (`produitCode`),
 *  et le fournisseur proposé celui du produit au carnet — ou ceux de ses lots,
 *  quand un lot a été livré par un autre. Un lot choisi sans produit désigne le
 *  sien ; un produit changé retire le lot qui n'est pas à lui. Le fournisseur
 *  unique est PROPOSÉ (pré-rempli), jamais imposé : « Autre fournisseur » rouvre
 *  le carnet entier. */
function ChoixObjets({ idBase, nature, objets, onChange }: {
  idBase: string; nature: string; objets: Record<string, string>; onChange: (type: ObjetNC, ref: string) => void;
}) {
  const { equips, lots, analytes, audits } = useGed();
  const types = natureMeta(nature)?.objets || [];
  const locaux = useLocaux(types.includes('emplacement'));
  const cascade = types.includes('produit');
  const { lignes: produits } = useProduits(cascade);
  const [tousFournisseurs, setTousFournisseurs] = useState(false);
  if (!types.length) return null;

  const produit = cascade ? produits.find((p) => p.code === objets.produit) || null : null;
  const lotChoisi = lots.find((l) => l.ref === objets.lot) || null;
  /* Les lots du produit choisi ; tous les lots sinon. Le lot déjà choisi reste
     proposé même s'il n'est plus de la liste : on ne retire pas en silence. */
  const lotsOfferts = objets.produit ? lots.filter((l) => l.produitCode === objets.produit || l.ref === objets.lot) : lots;
  /* Les fournisseurs CANDIDATS : celui du produit, et ceux de ses lots. */
  const candidats = cascade ? [...new Set([
    produit?.fournisseurCode || '',
    ...(objets.produit ? lotsOfferts : lotChoisi ? [lotChoisi] : []).map((l) => l.fournisseurCode || ''),
  ].filter(Boolean))] : [];

  const choisirProduit = (code: string) => {
    onChange('produit', code);
    if (objets.lot && code && lotChoisi && lotChoisi.produitCode !== code) onChange('lot', '');
    const p = produits.find((x) => x.code === code);
    const seuls = [...new Set([p?.fournisseurCode || '', ...lots.filter((l) => l.produitCode === code).map((l) => l.fournisseurCode || '')].filter(Boolean))];
    if (!objets.fournisseur && seuls.length === 1) onChange('fournisseur', seuls[0]);
    setTousFournisseurs(false);
  };
  const choisirLot = (ref: string) => {
    onChange('lot', ref);
    const l = lots.find((x) => x.ref === ref);
    if (cascade && l?.produitCode && !objets.produit) onChange('produit', l.produitCode);
    if (cascade && l?.fournisseurCode && !objets.fournisseur) onChange('fournisseur', l.fournisseurCode);
  };
  const statutLot: Record<string, string> = { quarantaine: 'quarantaine', accepte: 'accepté', en_service: 'en service', epuise: 'épuisé', retire: 'retiré' };

  return (
    <>
      {types.map((t) => {
        const id = `${idBase}-objet-${t}`;
        const v = objets[t] || '';
        return (
          <div key={t}>
            <label style={lbl} htmlFor={id}>{LIBELLE_OBJET[t]}{t === 'equipement' && nature === 'equipement' ? ' *' : ''}</label>
            {t === 'document' ? <ChoixDocument id={id} valeur={v} onChange={(x) => onChange(t, x)} />
              : t === 'produit' ? <ChoixProduit id={id} valeur={v} onChange={choisirProduit} />
                : t === 'fournisseur' ? (
                  <>
                    <ChoixCorrespondant id={id} valeur={v} onChange={(x) => onChange(t, x)} familles={['fournisseurs']} vide="—"
                      inclure={tousFournisseurs ? undefined : candidats}
                      aide={candidats.length && !tousFournisseurs
                        ? <>Proposé : {candidats.length === 1 ? 'le fournisseur' : 'les fournisseurs'} du produit et de ses lots.{' '}
                            <button type="button" onClick={() => setTousFournisseurs(true)}
                              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
                              Autre fournisseur
                            </button></>
                        : undefined} />
                  </>
                )
                  : (
                    <Selecteur id={id} value={v} onChange={(x) => (t === 'lot' ? choisirLot(x) : onChange(t, x))} style={inputStyle}>
                      <option value="">—</option>
                      {t === 'equipement' && equips.filter((e) => !e.sandbox && e.code).map((e) => <option key={e.code} value={e.code as string}>{`${e.code} · ${e.nom}`}</option>)}
                      {t === 'lot' && lotsOfferts.map((l) => <option key={l.documentId} value={l.ref}>{`${l.ref} · ${l.designation}${l.numeroLot ? ` · n° ${l.numeroLot}` : ''}${statutLot[l.statut] ? ` · ${statutLot[l.statut]}` : ''}`}</option>)}
                      {t === 'analyte' && analytes.filter((a) => a.actif).map((a) => <option key={a.code} value={a.code}>{`${a.code} · ${a.nom}`}</option>)}
                      {t === 'audit' && audits.map((a) => <option key={a.ref} value={a.ref}>{`${a.ref} · ${a.intitule}`}</option>)}
                      {t === 'emplacement' && locaux.map((l) => <option key={l.code} value={l.code}>{`${l.code} · ${l.libelle}`}</option>)}
                    </Selecteur>
                  )}
            {/* Un produit sans lot au registre : on le DIT, plutôt que de montrer un menu réduit à « — ». */}
            {t === 'lot' && objets.produit && !lotsOfferts.length && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 4 }}>
                Aucun lot de ce produit n’est au registre des lots : déclarez sans lot, ou réceptionnez-le d’abord dans « Réactifs ».
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

/** LA MISE HORS SERVICE, PROPOSÉE ET NON IMPOSÉE (RQ, 13/10/2026). Déclarer une
 *  panne ne change pas le statut de l'appareil : on dit où il en est, et on
 *  ouvre sa fiche — dans un nouvel onglet, pour ne pas perdre la déclaration —
 *  où la mise hors service se fait (geste du responsable qualité, motivé). */
function LienHorsService({ code }: { code: string }) {
  const { equips } = useGed();
  const e = equips.find((x) => x.code === code);
  if (!code || !e) return null;
  const hs = e.statut === 'hors_service';
  return (
    <div style={{ marginTop: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 12px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>
      {hs
        ? <>L’appareil <strong style={{ color: 'var(--text-strong)' }}>{code}</strong> est déjà hors service.</>
        : <>L’appareil <strong style={{ color: 'var(--text-strong)' }}>{code}</strong> reste en service. Faut-il l’arrêter ?{' '}
            <a href={cheminDe('equip', code)} target="_blank" rel="noreferrer" style={{ fontWeight: 700, color: 'var(--color-primary-active)' }}>
              Ouvrir sa fiche pour le mettre hors service ↗
            </a></>}
    </div>
  );
}


/** « Processus concerné » — même champ, même aide, à la déclaration et à la correction. */
function ChoixProcessus({ id, valeur, onChange, requis, procs, libelle = 'Processus concerné par la NC', aide, verrouille }: {
  id: string; valeur: string; onChange: (v: string) => void; requis?: boolean;
  procs: { name: string; codif?: string }[];
  libelle?: string; aide?: React.ReactNode;
  /** Imposé (le SMQ pour une réclamation) : affiché, pas modifiable. */
  verrouille?: boolean;
}) {
  return (
    <div><div style={ENTETE_I}><label style={{ ...lbl, marginBottom: 0 }} htmlFor={id}>{libelle}{requis ? ' *' : ''}</label><PointDeRegle ancre="nc.processus" /></div>
      <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle} disabled={verrouille}>
        <option value="">—</option>
        {/* Le code A→L de la cartographie aide à retrouver le processus ;
            la VALEUR stockée reste le nom (clé de rapprochement des NC). */}
        {procs.map((p) => <option key={p.name} value={p.name}>{p.codif ? `${p.codif} · ${p.name}` : p.name}</option>)}
      </Selecteur>
      {aide && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{aide}</div>}</div>
  );
}

/** « Quel problème ? » — la liste déroulante des propositions, pour le processus choisi. */
function ChoixProbleme({ id, groupes, valeur, onChange }: {
  id: string; groupes: ReturnType<typeof groupesProblemesDuProcessus>; valeur: string; onChange: (v: string) => void;
}) {
  if (!groupes.length) return null;
  return (
    <div style={{ gridColumn: '1 / -1' }}>
      <label style={lbl} htmlFor={id}>Quel problème ?</label>
      <Selecteur id={id} value={valeur} onChange={onChange} style={inputStyle}>
        <option value="">— choisir dans la liste…</option>
        {groupes.map((g) => (g.titre
          ? <optgroup key={g.titre} label={g.titre}>
              {g.items.map((c) => <option key={c.code} value={c.code}>{c.libelle}</option>)}
            </optgroup>
          : g.items.map((c) => <option key={c.code} value={c.code}>{c.libelle}</option>)))}
        <option value="__autre">Autre problème — je le décris ci-dessous</option>
      </Selecteur>
      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
        {groupes.some((g) => g.titre === GROUPE_DEMANDE)
          ? 'Deux listes : le défaut de la demande (prescription), puis les critères d’acceptation d’échantillon — ces derniers se complètent au paramétrage du référentiel. '
          : 'Situations courantes, rangées par processus. '}
        Choisir un problème remplit l’intitulé : vous pouvez le préciser. Si le vôtre n’y est pas, « Autre problème ».
      </div>
    </div>
  );
}

/** Les trois étapes du formulaire de NC (RQ, 13/10/2026), dans l'ordre où l'on
 *  sait les choses. Communes à la déclaration et à la correction de la fiche :
 *  on retrouve, en modifiant, exactement l'ordre dans lequel on a déclaré. */
export type EtapeNC = 'fait' | 'consequences' | 'priseEnCharge';
const ETAPES_NC: readonly [EtapeNC, string, string][] = [
  ['fait', '1', 'Ce qui s’est passé'],
  ['consequences', '2', 'Conséquences'],
  ['priseEnCharge', '3', 'Prise en charge'],
];
/** L'étape suivante et la précédente — pour les boutons du pied de formulaire. */
const voisines = (e: EtapeNC) => {
  const k = ETAPES_NC.findIndex(([c]) => c === e);
  return { avant: ETAPES_NC[k - 1], apres: ETAPES_NC[k + 1] };
};
/** Les boutons « ← étape précédente / étape suivante → » du pied. */
function navigationEtapes(etape: EtapeNC, aller: (e: EtapeNC) => void) {
  const { avant, apres } = voisines(etape);
  return [
    ...(avant ? [{ libelle: `← ${avant[2]}`, onClick: () => aller(avant[0]) }] : []),
    ...(apres ? [{ libelle: `${apres[2]} →`, onClick: () => aller(apres[0]) }] : []),
  ];
}

function EtapesFormulaire({ etape, onChange, label, alertes }: {
  etape: EtapeNC; onChange: (e: EtapeNC) => void; label: string; alertes?: Partial<Record<EtapeNC, boolean>>;
}) {
  return (
    <div role="tablist" aria-label={label} style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid var(--border-default)', margin: '0 0 14px' }}>
      {ETAPES_NC.map(([k, n, l]) => {
        const on = etape === k;
        return (
          <button key={k} type="button" role="tab" aria-selected={on} onClick={() => onChange(k)} className="ged-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', cursor: 'pointer',
              padding: '8px 14px 8px 6px', marginBottom: -1, fontFamily: 'var(--font-sans)', fontSize: 13.5, fontWeight: on ? 700 : 600,
              color: on ? 'var(--color-primary-active)' : 'var(--text-muted)', borderBottom: `2px solid ${on ? 'var(--color-primary)' : 'transparent'}` }}>
            <span aria-hidden style={{ display: 'grid', placeItems: 'center', width: 22, height: 22, borderRadius: '50%', fontSize: 12, fontWeight: 800,
              border: '2px solid var(--color-primary)', background: on ? 'var(--color-primary)' : 'var(--surface-card)', color: on ? 'var(--surface-card)' : 'var(--color-primary-active)' }}>{n}</span>
            {l}
            {alertes?.[k] && <span title="Des champs à compléter" style={{ color: 'var(--amber-600)', fontWeight: 800 }}>!</span>}
          </button>
        );
      })}
    </div>
  );
}

/** Les rubriques du registre de maintenance d'une PANNE — appareil, code cause,
 *  durée d'arrêt. Communes à la déclaration et à la correction. */
function RubriquesPanne({ idBase, valeurs, onChange, sansCode }: {
  idBase: string;
  /** L'appareil est déjà choisi comme OBJET relié : ne pas redemander son code. */
  sansCode?: boolean;
  valeurs: { equipementCode: string; causeEquip: string; dureeArret: string };
  onChange: (champ: 'equipementCode' | 'causeEquip' | 'dureeArret', v: string) => void;
}) {
  return (
    <div style={{ marginTop: 14, display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
      <span style={{ ...lbl, marginBottom: 0 }}>Rubriques de la fiche de vie (panne d’équipement)</span>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: sansCode ? '1fr 1fr' : '1fr 1fr 1fr', gap: '8px 14px' }}>
        {!sansCode && <div><label style={lbl} htmlFor={`${idBase}-eq`}>Code équipement</label>
          <input id={`${idBase}-eq`} value={valeurs.equipementCode}
            onChange={(e) => onChange('equipementCode', e.target.value.toUpperCase())}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono)' }} /></div>}
        <div><label style={lbl} htmlFor={`${idBase}-cause`}>Code cause</label>
          <select id={`${idBase}-cause`} value={valeurs.causeEquip} onChange={(e) => onChange('causeEquip', e.target.value)} style={inputStyle}>
            <option value="">— non déterminée</option>
            {Object.entries(CAUSES_PANNE).map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
          </select></div>
        <div><label style={lbl} htmlFor={`${idBase}-arret`}>Durée d’arrêt</label>
          {/* Même saisie que la curative qu'elle pré-remplit (03/11/2026). */}
          <ChampDuree id={`${idBase}-arret`} valeur={valeurs.dureeArret} onChange={(v) => onChange('dureeArret', v)} /></div>
      </div>
    </div>
  );
}

/** Masqué et non démonté : un clic dans la liste ne doit pas effacer une
 *  déclaration à moitié saisie (9 champs).
 *
 *  ⚠️ EXPORTÉ depuis le 05/10/2026 : la gommette flottante — le point d'entrée
 *  le plus utilisé du signalement — ouvre CE formulaire en fenêtre au lieu de
 *  ses deux champs d'avant (cf. `shared/ui/GommetteNC.tsx`). C'est donc le
 *  seul formulaire de déclaration de l'application ; tout champ ajouté ici est
 *  ajouté aux deux endroits, et aucun n'a besoin d'être recopié.
 *  Ne pas le démonter de son écran : `visible` le masque, cf. ci-dessus. */
export function DeclareForm({ visible = true, onDone, onDirty, onOuvrir, contexte, encadre = true, reclamationRef = '', initial }: {
  visible?: boolean; onDone: (ref?: string) => void; onDirty?: (d: boolean) => void;
  /** Ouvrir la fiche qu'on vient de créer — geste DISTINCT de « fermer le
   *  formulaire » : les confondre ferait ouvrir une fiche à chaque annulation.
   *  Absent, le toast d'enregistrement n'offre pas le lien plutôt qu'un lien
   *  qui ne mènerait nulle part. */
  onOuvrir?: (ref: string) => void;
  /** Ce que l'écran d'où l'on déclare sait de lui-même (gommette flottante).
   *  PROPOSÉ, jamais appliqué — voir le bloc « Rattachement » plus bas. */
  contexte?: { source?: string; libelle?: string; equipementCode?: string };
  /** Faux dans une fenêtre, qui porte déjà son cadre : deux bordures blanches
   *  l'une dans l'autre se lisent comme deux formulaires imbriqués. */
  encadre?: boolean;
  /** NC NÉE D'UNE RÉCLAMATION (13/10/2026) : sa référence — les deux fiches
   *  seront reliées, et la détection revient au SMQ. */
  reclamationRef?: string;
  /** Un formulaire PRÉREMPLI par l'écran d'origine (le rectificatif d'un CR MoMent, 15/10/2026) : ces valeurs sont le point de départ, le déclarant
   *  les relit et les change. Elles comptent comme l'état « vide » : fermer sans rien toucher ne demande pas de confirmation. */
  initial?: Partial<Record<'titre' | 'description' | 'nature' | 'canal' | 'gravite' | 'impactResultats' | 'dateDetection' | 'echeanceTraitement', string>>;
}) {
  const { smq, smqData, showToast, settings, session, recls } = useGed();
  /* ⚠️ NI SOURCE NI GRAVITÉ PRÉ-CHOISIES (relevé SYS-14 du 26/08/2026). Le
     formulaire partait sur « Pré-analytique » et « Mineure » : deux champs de
     CLASSIFICATION dont la valeur par défaut biaise les statistiques sans que
     personne ne l'ait décidé — on ne revient pas sur une case qui paraît déjà
     remplie. On part vide, et le serveur exige un choix.
     `impactResultats: 'inconnu'` reste, lui : « je ne sais pas encore » est une
     réponse JUSTE au moment de déclarer, pas un défaut commode. */
  const vide = {
    titre: '', description: '', source: '', processus: '', gravite: '', nature: '',
    ncType: reclamationRef ? 'externe' : 'interne', canal: reclamationRef ? 'reclamation' : '',
    processusDetection: '', reclamationRef, correspondantSignaleur: '', docLie: '', correction: '', responsable: '',
    dateDetection: '', echeanceTraitement: '',
    impactResultats: 'inconnu', nbResultatsConcernes: '', modaliteRappel: '',
    /* Le tiers à l'origine — facultatif, et proposé pour les seuls secteurs où
       un tiers peut l'être. C'est lui qui fait entrer la NC dans le relevé
       mensuel du correspondant. */
    correspondantCode: '',
    // Rubriques de panne — saisies seulement si la source est « équipement ».
    equipementCode: '', causeEquip: '', dureeArret: '',
    ...(initial || {}),
  };
  /* Prérempli par l'écran d'origine : la nature propose, comme au choix à la main, le processus concerné. */
  const [f, setF] = useState(() => (initial?.nature
    ? { ...vide, processus: processusDeNature(initial.nature, (settings?.processus?.length ? settings.processus : DEFAULT_PROCESSUS).filter((p) => p.active)) }
    : vide));
  /** Les objets à relier (13/10/2026) : type → référence. Hors de `f` : ce sont des liens. */
  const [objets, setObjets] = useState<Record<string, string>>({});
  /** RÉCLAMATION : associer une existante, ou en ENREGISTRER une nouvelle avec la NC (13/10/2026). */
  const [modeRecl, setModeRecl] = useState<'existante' | 'nouvelle'>('existante');
  const [nouvelleRecl, setNouvelleRecl] = useState({ canal: 'patient', plaignant: '', objet: '', correspondantCode: '' });
  const [erreur, setErreur] = useState('');
  /* TROIS ÉTAPES (RQ, 13/10/2026 ; deux depuis le 03/10) — voir `EtapesFormulaire`.
     La déclaration peut s'enregistrer depuis n'importe laquelle. */
  const [etape, setEtape] = useState<EtapeNC>('fait');
  /** LES PIÈCES CHOISIES AVANT LA CRÉATION (RQ, 08/10/2026 : « le chargement
   *  d'une pièce jointe doit être possible dès la déclaration »). Elles restent
   *  dans le navigateur jusqu'au clic « Créer » : la fiche n'existe pas encore,
   *  et une pièce ne se range que sur une fiche. Elles partent ensuite une à
   *  une, et ce qui n'a pas pu partir est NOMMÉ dans le message. */
  const [pieces, setPieces] = useState<{ nom: string; fichier: Blob }[]>([]);
  const ajouterPiece = (fichier: Blob, nom: string) => setPieces((l) => (l.length >= 10 ? l : [...l, { nom, fichier }]));
  /** Le problème choisi dans la liste (secteur pré-analytique), ou '__autre'. */
  const [probleme, setProbleme] = useState('');
  /** LES DOSSIERS CONCERNÉS, DÈS LA DÉCLARATION (RQ, 11/10/2026 : « je tape un
   *  numéro et j'appuie sur entrée, puis un autre »). Ils existaient déjà, mais
   *  seulement sur la fiche ouverte — donc après le triage, et pour le seul RQ :
   *  celui qui déclare est pourtant le seul à avoir les numéros sous les yeux.
   *  Hors de `f` comme les pièces : ce sont des lignes, pas un champ. */
  const [dossiers, setDossiers] = useState<LigneDossier[]>([]);
  /** Ce qui est tapé et pas encore validé par « Entrée ». Reversé dans la liste
   *  au moment de créer (voir `submit`) : sinon il partirait à la poubelle sans
   *  un mot, et le dossier manquerait au relevé sans que personne ne le sache. */
  const [saisieDossier, setSaisieDossier] = useState('');
  const criteresPreana = useCriteresPreana(visible);
  const { run, estOccupe } = useBusy();
  // Taxonomie des processus (Paramétrage Qualité → cartographie), repli sur le
  // défaut : c'est CETTE liste que la cartographie compte, pas les disciplines.
  const procs = (settings?.processus?.length ? settings.processus : DEFAULT_PROCESSUS).filter((p) => p.active);

  const modifie = JSON.stringify(f) !== JSON.stringify(vide) || pieces.length > 0 || Object.values(objets).some(Boolean)
    // Des dossiers listés, ou un numéro en cours de frappe : la fenêtre ne se
    // ferme pas sans le dire (`fenetreModale`).
    || dossiers.length > 0 || !!saisieDossier.trim();
  useEffect(() => { onDirty?.(modifie); }, [modifie, onDirty]);

  /* Socle partagé : le rappel en tête NOMME ce qui manque et EMMÈNE au champ.
     La déclaration se fait depuis n'importe quel écran, souvent en bas d'une
     page longue — un message sous le bouton n'y était jamais lu. */
  /* ⚠️ L'exigence dépend de la SOURCE. « À qualifier » est une réponse
     complète : le déclarant dit qu'il ne classe pas, et le RQ reprendra. Toute
     autre source engage à aller au bout — sinon la NC a l'air rangée sans
     l'être, et c'est le seul cas réellement nuisible (cf. `nc-declaration.ts`). */
  /* ── LA NATURE COMMANDE LE FORMULAIRE (RQ, 13/10/2026) ──────────────────
     Elle donne le SECTEUR (`source`, encore lu par la revue, l'assistant et la
     fiche de vie), PROPOSE le processus concerné — tant qu'on ne l'a pas changé
     à la main —, dit quels objets relier et quelle liste de problèmes montrer.
     « Je ne sais pas » laisse la NC à qualifier. */
  const secteur = f.nature ? secteurDeNature(f.nature, f.canal) : 'aqualifier';
  const classe = !!f.nature && f.nature !== 'aqualifier';
  const nat = natureMeta(f.nature);
  /** Le processus concerné suit la nature tant qu'on ne l'a pas choisi à la main. */
  const [processusManuel, setProcessusManuel] = useState(false);
  /** Le processus de DÉTECTION se calcule (`processusDetectionDe`) — sauf choix explicite. */
  const [detectionManuelle, setDetectionManuelle] = useState(false);
  const processusDetection = detectionManuelle ? f.processusDetection : processusDetectionDe(f.canal, f.processus, procs);
  const declaration = React.useMemo(() => [
    { id: 'nc-nature', label: 'De quoi s’agit-il', valeur: f.nature, requis: true },
    { id: 'nc-canal', label: 'Comment l’écart a été détecté', valeur: f.canal, requis: true },
    { id: 'nc-titre', label: 'Intitulé', valeur: f.titre, requis: true },
    { id: 'nc-gravite', label: 'Gravité', valeur: f.gravite, requis: true },
    { id: 'nc-processus', label: 'Processus concerné par la NC', valeur: f.processus, requis: classe },
    // Une panne se déclare SUR un appareil : c'est ce qui la fait apparaître dans sa fiche de vie.
    { id: 'nc-objet-equipement', label: 'Équipement concerné', valeur: objets.equipement || '', requis: f.nature === 'equipement' },
  ], [f.nature, f.canal, f.titre, f.gravite, f.processus, classe, objets.equipement]);
  const { ref: refFormulaire, verifier, Bandeau } = useFormulaire(declaration);
  /** L'étape de chaque champ obligatoire — pour y emmener et pour marquer l'onglet. */
  const ETAPE_DU_CHAMP: Record<string, EtapeNC> = {
    'nc-nature': 'fait', 'nc-canal': 'fait', 'nc-titre': 'fait', 'nc-gravite': 'consequences', 'nc-processus': 'priseEnCharge',
    'nc-objet-equipement': 'fait',
  };
  const manque = (id: string) => declaration.some((d) => d.id === id && d.requis && !String(d.valeur || '').trim());
  const alertes: Partial<Record<EtapeNC, boolean>> = {};
  for (const d of declaration) if (manque(d.id)) alertes[ETAPE_DU_CHAMP[d.id]] = true;

  const { groupes, problemes, natureDu } = useListeProblemesNature(f.nature, f.canal, criteresPreana);
  /** Choisir la nature : elle propose le processus (s'il n'a pas été choisi à la main). */
  const choisirNature = (v: string) => {
    if (v !== f.nature) setProbleme('');
    setF((x) => ({ ...x, nature: v, ...(!processusManuel ? { processus: processusDeNature(v, procs) } : {}) }));
  };
  /* « Rattacher à cet écran » : l'écran d'origine dit un SECTEUR ; il propose la
     nature qui lui correspond et, s'il en est un (CQI, EEQ, réclamation), le
     canal — jamais par-dessus un choix fait. */
  const natureContexte = contexte?.source ? natureDe({ source: contexte.source }) : '';
  const choisirSource = (v: string) => {
    const canalDuSecteur = NC_CANAUX.find((c) => c.secteur === v)?.key;
    const n = natureDe({ source: v });
    if (n && !f.nature) choisirNature(n);
    // Depuis la fiche d'un appareil : l'appareil devient l'objet relié (13/10/2026).
    if (contexte?.equipementCode) setObjets((o) => ({ ...o, equipement: o.equipement || (contexte.equipementCode as string) }));
    if (canalDuSecteur && !f.canal) setF((x) => ({ ...x, canal: canalDuSecteur, ncType: NC_CANAUX.find((c) => c.key === canalDuSecteur)?.releve || x.ncType }));
  };
  const choisirProcessus = (v: string) => { setProcessusManuel(true); setF((x) => ({ ...x, processus: v })); };
  /** La phrase qui dit pourquoi un champ vient d'être vidé. */
  const [avisSource, setAvisSource] = useState('');
  /* Le canal DIT qui a relevé l'écart (`ncType`). Un CQI ou une EEQ proposent la
     nature « Résultat d'analyse » si elle n'est pas choisie. */
  const choisirCanal = (v: string) => {
    setAvisSource('');
    const releve = NC_CANAUX.find((c) => c.key === v)?.releve;
    if ((v === 'cqi' || v === 'eeq') && !f.nature) choisirNature('resultat');
    setF((x) => ({ ...x, canal: v, ...(releve ? { ncType: releve } : {}), ...(v !== 'reclamation' && !reclamationRef ? { reclamationRef: '' } : {}) }));
  };
  /* Choisir un problème écrit l'INTITULÉ (s'il est vide ou déjà celui de la liste),
     jamais la description ; choisi dans la liste COMPLÈTE, il renseigne la nature. */
  const choisirProbleme = (v: string) => {
    const ancien = problemes.find((c) => c.code === probleme);
    const c = problemes.find((x) => x.code === v);
    setProbleme(v);
    const n = !f.nature || f.nature === 'aqualifier' ? natureDu.get(v) : undefined;
    if (n) choisirNature(n);
    setF((prev) => {
      const titreAuto = !prev.titre.trim() || (!!ancien && prev.titre === ancien.libelle);
      return { ...prev, titre: titreAuto ? (c ? c.libelle : '') : prev.titre };
    });
  };

  /** La déclaration a-t-elle été rédigée avec l'assistant ? DIT au journal de la NC. */
  const [aideAssistant, setAideAssistant] = useState(false);
  /** La conversation qui a mené à la fiche, et la fiche telle que l'assistant
   *  l'a remplie : elles partent avec la déclaration (RQ, 03/11/2026). */
  const entretien = React.useRef<{ echanges: EchangeEntretien[]; fiche: FicheEntretien } | null>(null);
  /** Ce que l'assistant a posé dans chaque champ au dernier remplissage : un
   *  nouveau remplissage remplace SA valeur, jamais ce que le déclarant a tapé. */
  const poseParAssistant = React.useRef<Record<string, string>>({});
  const appliquerFiche = (fiche: FicheEntretien, echanges: EchangeEntretien[]) => {
    entretien.current = { echanges, fiche };
    const textes = ['titre', 'description', 'gravite', 'impactResultats', 'correction', 'dateDetection'] as const;
    const pose = poseParAssistant.current;
    setF((prev) => {
      const suite = { ...prev };
      for (const k of textes) {
        const v = String(fiche[k] || '');
        const libre = !String(prev[k] || '').trim() || prev[k] === pose[k] || (k === 'impactResultats' && prev[k] === 'inconnu');
        if (v && libre) { suite[k] = v; pose[k] = v; }
      }
      return suite;
    });
    // Nature et canal passent par leurs gestes, qui en tirent le processus et
    // « qui a relevé » — jamais par-dessus un choix fait à la main.
    if (fiche.nature && (!f.nature || f.nature === pose.nature)) { choisirNature(fiche.nature); pose.nature = fiche.nature; }
    if (fiche.canal && (!f.canal || f.canal === pose.canal)) { choisirCanal(fiche.canal); pose.canal = fiche.canal; }
    if (fiche.dossiers?.length) setDossiers((l) => ajouterDossiers(l, fiche.dossiers.join('\n')).lignes);
    setAideAssistant(true);
  };

  const submit = () => run('creer', async () => {
    /* ⚠️ LE NUMÉRO TAPÉ SANS « ENTRÉE » ENTRE QUAND MÊME, et il entre AVANT
       toute vérification : si un champ obligatoire manque, on repart sur le
       formulaire avec la ligne déjà posée — visible — au lieu de la retrouver
       dans un champ qu'on croira avoir validé. C'est la faute la plus probable
       de cette saisie, et la seule qui se perdrait sans un mot. */
    const flush = ajouterDossiers(dossiers, saisieDossier);
    const listeDossiers = flush.lignes;
    if (flush.ajoutes.length) setDossiers(listeDossiers);
    if (saisieDossier) setSaisieDossier('');
    /* Un champ obligatoire peut être dans l'autre étape : on y va d'abord, puis le bandeau
       du socle le nomme et y emmène. */
    const premier = declaration.find((d) => d.requis && !String(d.valeur || '').trim());
    if (premier) {
      const cible = ETAPE_DU_CHAMP[premier.id] || 'fait';
      if (cible !== etape) { setEtape(cible); requestAnimationFrame(() => requestAnimationFrame(() => verifier())); return; }
    }
    if (!verifier()) return;
    setErreur('');
    /* ⚠️ LA RÉFÉRENCE ATTRIBUÉE SE DIT (relevé MAJ-01 du 26/08/2026). Le
       formulaire disparaissait, la ligne se rangeait en 13ᵉ position, et
       « référence générée » ne disait pas LAQUELLE : il fallait la chercher
       dans la liste pour savoir ce qu'on venait de créer. Or c'est cette
       référence qu'on note sur la paillasse et qu'on cite en réunion.
       `smqData` au lieu de `smq` : on a besoin de ce que le moteur a écrit. */
    const rep = await smqData<{ nc?: { ref?: string; documentId?: string }; actionRef?: string }>(
      'ged/nc', {
        ...f, source: secteur, processusDetection,
        objets: Object.entries(objets).filter(([, ref]) => ref).map(([type, ref]) => ({ type, ref })),
        // L'appareil d'une panne et le document d'une NC documentaire remplissent aussi leur champ.
        ...(f.nature === 'equipement' && objets.equipement ? { equipementCode: objets.equipement } : {}),
        ...(f.nature === 'document' && objets.document ? { docLie: objets.document } : {}),
        ...(f.canal === 'reclamation' && !reclamationRef && modeRecl === 'nouvelle'
          ? { reclamationRef: '', nouvelleReclamation: { ...nouvelleRecl, objet: nouvelleRecl.objet.trim() || f.titre } } : {}),
        dossiersConcernes: listeDossiers, detectePar: session.key,
        /* Le CODE de l'intitulé choisi dans la liste : il n'était jamais envoyé,
           et le paramétrage ne pouvait donc pas compter l'emploi d'un intitulé. */
        probleme: probleme && probleme !== '__autre' ? probleme : '',
        redigeeAvecAssistant: aideAssistant,
        ...(aideAssistant && entretien.current ? { entretien: entretien.current } : {}),
      }, undefined,
      'Déclaration de la non-conformité',
    );
    if (!rep) return;
    const ref = String(rep.nc?.ref || '');
    /* Les pièces partent APRÈS la fiche, une à une, et l'échec se dit pièce
       par pièce : une déclaration créée sans sa capture vaut mieux qu'aucune
       déclaration, mais le déclarant doit savoir laquelle manque. */
    const docId = String(rep.nc?.documentId || '');
    const ratees: string[] = [];
    let jointes = 0;
    for (const p of docId ? pieces : []) {
      try {
        const r = await fetchEcriture(`/api/ged/ged/nc/${docId}/pieces`, {
          method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ fichierNom: p.nom, fichierData: await lireEnBase64(p.fichier) }),
        });
        if (r.ok) jointes += 1; else ratees.push(p.nom);
      } catch { ratees.push(p.nom); }
    }
    if (ratees.length) showToast(`Pièce(s) non jointe(s) à ${ref} : ${ratees.join(', ')}. Vous pourrez les rejoindre depuis la fiche.`, 'erreur');
    /* L'action ouverte d'office (résultats patients concernés) se dit dans le
       même souffle : elle a été créée sans que personne ne la demande, et une
       tâche qui apparaît sans explication se lit comme un bug. */
    showToast(
      `${ref || 'Non-conformité'} enregistrée.`
      + (jointes ? ` ${jointes} pièce(s) jointe(s).` : '')
      + (rep.actionRef ? ` Action ${rep.actionRef} ouverte d’office — des résultats patients sont concernés.` : ''),
      'info',
      /* ⚠️ PAS DE RACCOURCI VERS UN DOSSIER QUI NE S'OUVRE PAS (07/10/2026).
         Une déclaration humaine entre en « soumise », et depuis cette date son
         dossier est réservé au RQ et au CQ. Laisser le bouton aurait offert au
         déclarant, dans le message qui le remercie, un geste refusé — la pire
         place pour un refus. Le RQ et le CQ, eux, le gardent : pour eux, le
         dossier s'ouvre vraiment. */
      ref && onOuvrir && peutOuvrirNCNonRetenue(session.roles)
        ? { label: 'Ouvrir la fiche', faire: () => onOuvrir(ref) }
        : undefined,
    );
    setF(vide); setObjets({}); setModeRecl('existante'); setNouvelleRecl({ canal: 'patient', plaignant: '', objet: '', correspondantCode: '' }); setProcessusManuel(false); setDetectionManuelle(false); setPieces([]); setProbleme(''); setDossiers([]); setSaisieDossier(''); setEtape('fait'); setAvisSource(''); setAideAssistant(false); poseParAssistant.current = {}; entretien.current = null; onDone(ref);
  });

  return (
    <div ref={refFormulaire} style={{ ...(encadre ? card : null), padding: 20, display: visible ? 'block' : 'none' }}>
      <Bandeau />
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: '0 0 4px', fontSize: 15.5, fontWeight: 700, color: 'var(--text-strong)' }}>
          {/* « Pourquoi fait-on ça ? », à l'endroit où on le fait (RQ, 06/09/2026).
              §8.7 — maîtrise des non-conformités du système de management — et non
              §7.5, qui ne vise que le résultat d'examen produit hors conditions :
              une NC déclarée ici peut porter sur un tube, une porte de congélateur
              ou un courrier égaré. */}
          Déclarer une non-conformité<Norme art="8.7" />
        </h2>
        <RequiredHint />
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: '0 0 14px' }}>Une référence BIO24-NC est générée automatiquement.</p>
      <EtapesFormulaire etape={etape} onChange={setEtape} label="Étapes de la déclaration" alertes={alertes} />

      {/* ── TROIS ÉTAPES, DANS L'ORDRE OÙ L'ON SAIT LES CHOSES (RQ, 13/10/2026) ──
          Elles remplacent « Questions de base / Précisions (si utile) », qui
          rangeait la description et le responsable OBLIGATOIRE dans une étape
          dite facultative, et demandait la gravité avant l'impact dont elle
          découle. 1. ce qui s'est passé ; 2. ce que ça a touché, puis la gravité ;
          3. qui le prend en charge. Le formulaire de correction suit le même
          ordre (`CorrigerFiche`). */}
      {etape === 'fait' && (
        <>
      {/* L'ENTRETIEN AVEC L'ASSISTANT (19/10/2026) : raconter, répondre à ses
          questions, et la fiche se remplit dessous — à relire avant « Créer ».
          Absent si le serveur n'a pas de clé : on remplit à la main. */}
      <EntretienDeclarationNC onFiche={appliquerFiche} />
      {/* L'INTITULÉ EN PREMIER (RQ, 14/10/2026) : on dit d'abord ce qui ne va pas,
          en quelques mots ; le classement vient ensuite. « Quel problème ? »
          peut encore le proposer — jamais par-dessus ce qui a été tapé. */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl} htmlFor="nc-titre">Intitulé — le problème, en quelques mots <span style={{ color: 'var(--red-600)' }}>*</span></label>
          <input id="nc-titre" value={f.titre} onChange={(e) => { setF({ ...f, titre: e.target.value }); if (erreur) setErreur(''); }}
            aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
          <FieldError>{erreur}</FieldError>
        </div>
      <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        {/* ── LA NATURE D'ABORD (RQ, 13/10/2026) : elle commande le reste. Puis
            COMMENT l'écart a été détecté — une seule question, qui dit aussi qui
            l'a relevé. Les processus (concerné, de détection) se règlent en
            « Prise en charge », pré-remplis. */}
        <ChoixNature id="nc-nature" valeur={f.nature} onChange={choisirNature} />
        <ChoixDetection id="nc-canal" valeur={f.canal} onChange={choisirCanal} requis />
        {/* QUI L'A SIGNALÉ (14/10/2026) — relié au carnet. Distinct de « l'erreur
            vient-elle d'un correspondant ? » (étape 2), qui seul alimente le relevé. */}
        {f.canal === 'correspondant' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl} htmlFor="nc-signaleur">Quel correspondant l’a signalé ?</label>
            <ChoixCorrespondant id="nc-signaleur" valeur={f.correspondantSignaleur} onChange={(v) => setF({ ...f, correspondantSignaleur: v })}
              aide="Celui qui a prévenu le laboratoire. S’il est aussi à l’origine de l’erreur, dites-le à l’étape 2." />
          </div>
        )}
        {avisSource && <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12.5, color: 'var(--amber-700)' }}>{avisSource}</p>}
        {/* LA RÉCLAMATION À L'ORIGINE (RQ, 13/10/2026) — associer une réclamation
            DÉJÀ au registre, ou en ENREGISTRER une nouvelle avec la NC. Dans les
            deux cas, les deux fiches sont reliées à la création. */}
        {f.canal === 'reclamation' && (
          <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 8, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
            <span style={{ ...lbl, marginBottom: 0 }}>La réclamation à l’origine</span>
            {reclamationRef ? (
              <span style={{ fontSize: 13 }}>Réclamation <strong style={{ fontFamily: 'var(--font-mono)' }}>{reclamationRef}</strong> — les deux fiches seront reliées.</span>
            ) : (
              <>
                <div role="radiogroup" aria-label="Réclamation existante ou nouvelle" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {([['existante', 'Associer une réclamation existante'], ['nouvelle', 'Enregistrer une nouvelle réclamation']] as const).map(([k, l]) => {
                    const on = modeRecl === k;
                    return (
                      <button key={k} type="button" role="radio" aria-checked={on} onClick={() => setModeRecl(k)}
                        style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer',
                          border: `1.5px solid ${on ? 'var(--color-primary)' : 'var(--border-default)'}`,
                          background: on ? 'var(--cyan-50)' : 'var(--surface-card)', color: on ? 'var(--color-primary-active)' : 'var(--text-muted)' }}>{l}</button>
                    );
                  })}
                </div>
                {modeRecl === 'existante' ? (
                  <div>
                    <Selecteur id="nc-reclamation" value={f.reclamationRef} onChange={(v) => setF({ ...f, reclamationRef: v })} style={inputStyle}>
                      <option value="">— choisir dans le registre des réclamations</option>
                      {recls.filter((r) => r.statut !== 'cloturee' || r.ref === f.reclamationRef).map((r) => (
                        <option key={r.ref} value={r.ref}>{`${r.ref} · ${r.objet || r.plaignant || ''}`}</option>
                      ))}
                    </Selecteur>
                    {recls.length === 0 && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
                        Aucune réclamation lisible ici : enregistrez-en une nouvelle, ou laissez vide.
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
                    <div><label style={lbl} htmlFor="nc-recl-canal">Qui réclame ?</label>
                      <select id="nc-recl-canal" value={nouvelleRecl.canal} onChange={(e) => setNouvelleRecl({ ...nouvelleRecl, canal: e.target.value })} style={inputStyle}>
                        {REC_CANAUX.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                      </select></div>
                    {nouvelleRecl.canal !== 'patient' && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={lbl} htmlFor="nc-recl-corresp">Quel correspondant réclame ?</label>
                        <ChoixCorrespondant id="nc-recl-corresp" valeur={nouvelleRecl.correspondantCode}
                          onChange={(v) => setNouvelleRecl({ ...nouvelleRecl, correspondantCode: v })}
                          aide="Relié à l’annuaire : la réclamation apparaîtra sur sa fiche. Il sera aussi noté comme ayant signalé l’écart." />
                      </div>
                    )}
                    <div><label style={lbl} htmlFor="nc-recl-plaignant">Nom (facultatif)</label>
                      <input id="nc-recl-plaignant" value={nouvelleRecl.plaignant} onChange={(e) => setNouvelleRecl({ ...nouvelleRecl, plaignant: e.target.value })} style={inputStyle} /></div>
                    <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="nc-recl-objet">Objet de la réclamation</label>
                      <input id="nc-recl-objet" value={nouvelleRecl.objet} placeholder={f.titre || 'Par défaut : l’intitulé de la NC'}
                        onChange={(e) => setNouvelleRecl({ ...nouvelleRecl, objet: e.target.value })} style={inputStyle} /></div>
                    <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--text-muted)' }}>
                      Elle sera enregistrée au registre des réclamations avec la NC — accusé de réception tracé, description reprise de la NC — et les deux fiches seront reliées.
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── LE RATTACHEMENT EST UN CHOIX, PAS UNE DÉDUCTION (17/08/2026) ──
            Repris de la gommette flottante, qui ouvre désormais ce formulaire.

            On constate rarement un écart là où on le déclare : une anomalie de
            réactif se voit en consultant un planning, un défaut documentaire
            depuis le CQI. Pré-remplir la source avec l'écran d'origine était
            donc faux aussi souvent que juste — et personne ne revient sur une
            case qui paraît déjà remplie. L'écran d'origine n'est qu'une
            PROPOSITION à un clic ; sans clic, elle n'engage rien. */}
        {/* L'objet à relier, selon la nature : l'équipement en panne, le lot, le document… */}
        <ChoixObjets idBase="nc" nature={f.nature} objets={objets} onChange={(t, v) => setObjets((o) => ({ ...o, [t]: v }))} />
        {contexte?.source && natureContexte && !reclamationRef
          && (natureContexte !== f.nature || (!!contexte.equipementCode && objets.equipement !== contexte.equipementCode)) && (
          <div style={{ gridColumn: '1 / -1', fontSize: 12.5, color: 'var(--text-muted)', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '9px 12px', lineHeight: 1.5 }}>
            Vous déclarez depuis <strong style={{ color: 'var(--text-strong)' }}>{contexte.libelle || contexte.source}</strong>
            {contexte.equipementCode ? <> — appareil <strong style={{ color: 'var(--text-strong)' }}>{contexte.equipementCode}</strong></> : null}.
            {' '}
            <button type="button" onClick={() => choisirSource(contexte.source as string)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>
              Rattacher à cet écran
            </button>
          </div>
        )}

        <ChoixProbleme id="nc-probleme" groupes={groupes} valeur={probleme} onChange={choisirProbleme} />
        {/* La DESCRIPTION est le constat lui-même : elle était rangée en
            « précisions », alors que la fiche la lit en premier — et que choisir
            un problème y écrit une ligne qu'on ne voyait pas. */}
        <div style={{ gridColumn: '1 / -1' }}><label style={lbl} htmlFor="nc-description">Description — ce qui s’est passé</label>
          <textarea id="nc-description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
            style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} /></div>
        <div><label style={lbl} htmlFor="nc-detection">Date de détection</label>
          <input id="nc-detection" type="date" value={f.dateDetection} max={aujourdhuiIso()}
            onChange={(e) => setF({ ...f, dateDetection: e.target.value })} style={inputStyle} />
          {/* Le défaut reste VIDE, et le moteur retombe alors sur la date du
              jour. Pré-remplir ferait passer une hypothèse pour une saisie —
              or un écart se découvre souvent plusieurs jours après. */}
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
            Quand l’écart a été CONSTATÉ, si ce n’est pas aujourd’hui. À défaut, la date du jour.
          </div></div>
        <div><label style={lbl} htmlFor="nc-correction">Correction immédiate</label>
          <input id="nc-correction" value={f.correction} onChange={(e) => setF({ ...f, correction: e.target.value })} style={inputStyle} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
            Ce qui a été fait tout de suite : refaire le tube, rappeler le résultat, isoler le lot.
          </div></div>
      </div>

      {/* Les pièces, DÈS la déclaration : une capture d'écran du SIL, la photo
          d'un tube. Choisies ici, envoyées après la création (voir `pieces`). */}
      <div style={{ marginTop: 14, display: 'grid', gap: 6 }}
        onPaste={(e) => {
          const img = Array.from(e.clipboardData?.files || []).find((x) => x.type.startsWith('image/'));
          if (img) { e.preventDefault(); ajouterPiece(img, `capture-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`); }
        }}>
        <span style={lbl}>Pièces jointes (captures d’écran, photos, PDF) — facultatif</span>
        {pieces.map((p, i) => (
          <div key={`${p.nom}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <I n="doc" s={12} />
            <span>{p.nom}</span>
            <span style={{ color: 'var(--text-muted)' }}>{Math.max(1, Math.round(p.fichier.size / 1024))} Ko</span>
            <Bouton taille="sm" variante="discret" onClick={() => setPieces((l) => l.filter((_, j) => j !== i))}
              aria-label={`Retirer ${p.nom}`}>Retirer</Bouton>
          </div>
        ))}
        <label className="ged-btn" style={{ ...styleBouton('contour', 'sm'), cursor: 'pointer' }}>
          <I n="plus" s={12} />Joindre un fichier
          <input type="file" accept="image/*,application/pdf" hidden multiple
            onChange={(e) => { Array.from(e.target.files || []).forEach((x) => ajouterPiece(x, x.name)); e.target.value = ''; }} />
        </label>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Astuce : collez une capture d’écran (Ctrl/⌘ + V) dans cette zone. Dix pièces au plus ; elles sont jointes à la création.
        </span>
      </div>
        </>
      )}

      {etape === 'consequences' && (
        <>
      {/* La seule question de la fiche dont la réponse peut obliger à rappeler
          des comptes rendus déjà partis (§7.5). Elle est posée DÈS la
          déclaration, et « je ne sais pas encore » est une réponse recevable —
          elle se précisera au traitement. Ce qui ne l'est pas, c'est de clore
          une non-conformité sans jamais y avoir répondu. */}
      <div style={{ marginTop: 14, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 8 }}>
        <div style={ENTETE_I}><span style={{ ...lbl, marginBottom: 0 }}>Des résultats patients sont-ils concernés ?</span><PointDeRegle ancre="nc.impactResultats" /></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {IMPACTS_RESULTATS.map((o) => {
            const on = f.impactResultats === o.key;
            return (
              <button key={o.key} onClick={() => setF({ ...f, impactResultats: o.key })} aria-pressed={on}
                title={o.aide}
                style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer', border: `1.5px solid ${on ? o.fg : 'var(--border-default)'}`, background: on ? o.bg : 'var(--surface-card)', color: on ? o.fg : 'var(--text-muted)' }}>
                {o.label}
              </button>
            );
          })}
        </div>
        {/* ── LES DOSSIERS CONCERNÉS, UN PAR LIGNE ─────────────────────────
            HORS de la condition « des résultats sont concernés », comme sur la
            fiche, et c'est le cœur de l'arbitrage du 07/10/2026 : une
            prescription sans prélèvement ne produit AUCUN résultat et met
            pourtant N dossiers en cause. Les enfermer sous « Oui » aurait rendu
            le cas d'origine insaisissable. */}
        <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
          <span style={{ ...lbl, marginBottom: 0 }}>Dossiers patients concernés — facultatif</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Un numéro par ligne, même si aucun résultat n’a été rendu. C’est cette liste qui
            permet de dire au correspondant lesquels de ses dossiers sont en cause — et une
            seule non-conformité suffit alors pour dix dossiers.
          </span>
          <SaisieDossiers idBase="nc-dossiers" lignes={dossiers} saisie={saisieDossier}
            onSaisie={setSaisieDossier} onChange={setDossiers} />
        </div>
        {f.impactResultats === 'oui' && (
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
            <div><label style={lbl} htmlFor="nc-nbres">Combien, approximativement ?</label>
              {/* ⚠️ DÉRIVÉ dès qu'il y a des lignes : deux chiffres pour la même
                  chose, c'est la garantie qu'un des deux mente. Le moteur
                  applique la même règle (`compteRetenu`, `nc-dossiers.ts`) — la
                  valeur saisie serait écrasée, et l'écran afficherait donc autre
                  chose que ce qui s'enregistre. */}
              {dossiers.length > 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-body)', padding: '9px 0' }}>
                  <strong>{dossiers.length}</strong> — compté sur les dossiers listés ci-dessus.
                </div>
              ) : (
                <input id="nc-nbres" value={f.nbResultatsConcernes} onChange={(e) => setF({ ...f, nbResultatsConcernes: e.target.value })}
                  placeholder="Ex. 12 comptes rendus du 14 au 16/04" style={inputStyle} />
              )}</div>
            <div><label style={lbl} htmlFor="nc-rappel">Qu’a-t-on fait ?</label>
              <input id="nc-rappel" value={f.modaliteRappel} onChange={(e) => setF({ ...f, modaliteRappel: e.target.value })}
                placeholder="Rappel, correction, information du prescripteur…" style={inputStyle} /></div>
          </div>
        )}
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          « Je ne sais pas encore » est une réponse : elle se précise au traitement.
        </span>
      </div>

      {/* LA GRAVITÉ APRÈS L'IMPACT : l'aide sous les boutons la propose d'après
          la réponse à « des résultats patients sont-ils concernés ? ». */}
      <div style={{ marginTop: 14 }}>
        <div><div style={ENTETE_I}><label style={{ ...lbl, marginBottom: 0 }} htmlFor="nc-gravite">Gravité *</label><PointDeRegle ancre="nc.gravite" /></div>
          <div id="nc-gravite" style={{ display: 'flex', gap: 6 }}>
            {NC_GRAVITES.map((g) => {
              const on = f.gravite === g.key; const m = graviteMeta(g.key);
              return <button key={g.key} onClick={() => setF({ ...f, gravite: g.key })} aria-pressed={on} style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer', border: `1.5px solid ${on ? m.fg : 'var(--border-default)'}`, background: on ? m.bg : 'var(--surface-card)', color: on ? m.fg : 'var(--text-muted)' }}>{g.label}</button>;
            })}
          </div>
          <ExplicationNiveaux impact={f.impactResultats} gravite={f.gravite} onAppliquer={(g) => setF({ ...f, gravite: g })} /></div>
      </div>

      {/* QUI EST À L'ORIGINE (RQ, 07/10/2026) — proposé pour les secteurs où un
          tiers peut l'être. Sur une panne d'automate ou un contrôle interne, le
          champ n'aurait aucun sens et ferait du bruit. Une LISTE, jamais un
          champ libre : le relevé mensuel groupe par ce code. */}
      {(nat?.correspondant || f.nature === 'aqualifier' || !f.nature || f.canal === 'correspondant') && (
        <div style={{ marginTop: 14 }}>
          <label style={lbl} htmlFor="nc-correspondant">{QUESTION_CORRESPONDANT}</label>
          <ChoixCorrespondant id="nc-correspondant" valeur={f.correspondantCode}
            onChange={(v) => setF({ ...f, correspondantCode: v })} aide={AIDE_CORRESPONDANT} />
        </div>
      )}

      {/* Les rubriques du registre de maintenance, DÈS la déclaration d'une panne
          (13/10/2026) : elles ne se saisissaient qu'en corrigeant la fiche. */}
      {f.nature === 'equipement' && (
        <>
          <RubriquesPanne idBase="nc" sansCode valeurs={f} onChange={(champ, v) => setF({ ...f, [champ]: v })} />
          <LienHorsService code={objets.equipement || ''} />
        </>
      )}
        </>
      )}

      {etape === 'priseEnCharge' && (
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
        {/* Les DEUX PROCESSUS, pré-remplis : le concerné par la nature, celui de
            la détection CALCULÉ d'après le canal (RQ, 13/10/2026). Modifiables. */}
        <ChoixProcessus id="nc-processus" valeur={f.processus} onChange={choisirProcessus} procs={procs} requis={classe}
          aide={classe ? (processusManuel ? undefined : 'Proposé d’après la nature.') : 'Avec « Je ne sais pas », le responsable qualité le choisira.'} />
        <ChoixProcessus id="nc-processus-detection" libelle="Processus à l’origine de la détection"
          valeur={processusDetection} verrouille={f.canal === 'reclamation'} procs={procs}
          onChange={(v) => { setDetectionManuelle(true); setF({ ...f, processusDetection: v }); }}
          aide={f.canal === 'reclamation' ? 'Une réclamation est détectée par le SMQ.' : (detectionManuelle ? undefined : 'Calculé d’après la façon dont l’écart a été détecté.')} />
        <div><label style={lbl} htmlFor="nc-responsable">Responsable du traitement{classe ? ' *' : ''}</label>
          <Selecteur id="nc-responsable" value={f.responsable} onChange={(v) => setF({ ...f, responsable: v })} style={inputStyle}>
            <option value="">—</option>
            {(settings?.gedUsers || []).map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
          </Selecteur></div>
        <div><label style={lbl} htmlFor="nc-echeance">Échéance de traitement</label>
          <input id="nc-echeance" type="date" value={f.echeanceTraitement}
            onChange={(e) => setF({ ...f, echeanceTraitement: e.target.value })} style={inputStyle} />
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
            Facultative : aucun délai réglementaire ne court avant la prise en charge.
          </div></div>
        {f.nature !== 'document' && <div><label style={lbl} htmlFor="dec-doc">Document ou référence externe (facultatif)</label>
          <ChoixDocument id="dec-doc" valeur={f.docLie} onChange={(v) => setF({ ...f, docLie: v })} />
          <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
            Un seul ici. Les autres documents se lient depuis la fiche, une fois la NC ouverte.
          </p></div>}
        </div>
      )}

      {/* ⚠️ LE BOUTON N'EST PLUS GRISÉ PAR UN CHAMP MANQUANT (relevé SYS-8 du
          26/08/2026 : « aucun message, aucun champ signalé au clic »).
          Un bouton désactivé ne dit RIEN de ce qui manque, et il n'y a nulle
          part où cliquer pour l'apprendre : on relit le formulaire en
          cherchant ce qu'on a oublié. Or le socle `useFormulaire` existe
          précisément pour ça — il affiche un bandeau qui NOMME les champs
          manquants et emmène au premier. Le griser l'empêchait de servir.
          `submit()` appelle `verifier()` en première ligne : le clic ne
          produit aucune écriture tant que le formulaire est incomplet, il
          produit une EXPLICATION. Reste désactivé pendant l'enregistrement,
          qui est un autre motif — celui-là se voit. */}
      <PiedActions
        style={{ marginTop: 16 }}
        secondaires={navigationEtapes(etape, setEtape)}
        annuler={{ onClick: () => { setF(vide); setObjets({}); setModeRecl('existante'); setNouvelleRecl({ canal: 'patient', plaignant: '', objet: '', correspondantCode: '' }); setProcessusManuel(false); setDetectionManuelle(false); setPieces([]); setErreur(''); setProbleme(''); setDossiers([]); setSaisieDossier(''); setEtape('fait'); setAvisSource(''); onDone(); } }}
        principale={{ libelle: 'Créer', onClick: () => void submit(), disabled: estOccupe(), chargement: estOccupe('creer'), libelleChargement: 'Création…' }}
      />
    </div>
  );
}

/** Création de l'action corrective : l'échéance partait vide et en dur, ce qui
 *  rendait l'action impossible à compter en retard — définitivement, faute de
 *  formulaire d'édition côté plan d'actions. */
function CreerAction({ nc, responsableDefaut, dejaCreees, onFait, onCreee }: { nc: GedNC; responsableDefaut: string; dejaCreees: number; onFait: () => void; onCreee: (documentId: string) => void }) {
  const { smqData, settings, showToast, naviguerVers } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [echeance, setEcheance] = useState('');
  const [responsable, setResponsable] = useState(responsableDefaut);
  const [erreur, setErreur] = useState('');
  const { run, estOccupe } = useBusy();
  const gedUsers = settings?.gedUsers || [];

  // Au-delà de la première, le libellé dit qu'ajouter une action est délibéré —
  // et non le symptôme d'un clic précédent qui n'aurait pas abouti.
  if (!ouvert) {
    return (
      <Bouton taille="sm" onClick={() => { setResponsable(responsableDefaut); setOuvert(true); }}>
        <I n="plus" s={13} />{dejaCreees > 0 ? 'Ajouter une autre action' : 'Créer l’action au plan'}
      </Bouton>
    );
  }
  return (
    <div style={{ flex: '1 1 100%', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12 }}>
      <div style={{ flex: '1 1 160px' }}>
        <label style={lbl} htmlFor="nc-ac-ech">Échéance <span style={{ color: 'var(--red-600)' }}>*</span></label>
        <input id="nc-ac-ech" type="date" value={echeance} onChange={(e) => { setEcheance(e.target.value); if (erreur) setErreur(''); }}
          aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
      </div>
      <div style={{ flex: '1 1 180px' }}>
        <label style={lbl} htmlFor="nc-ac-resp">Responsable</label>
        <Selecteur id="nc-ac-resp" value={responsable} onChange={setResponsable} style={inputStyle}>
          <option value="">—</option>
          {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
        </Selecteur>
      </div>
      <PiedActions
        taille="sm"
        annuler={{ onClick: () => { setOuvert(false); setErreur(''); } }}
        principale={{
          libelle: 'Créer', libelleChargement: 'Création…', chargement: estOccupe('ac'), disabled: estOccupe(),
          onClick: () => void run('ac', async () => {
            if (!echeance) { setErreur('Une échéance est nécessaire pour suivre l’action.'); return; }
            // La réponse porte l'action créée : elle s'ouvre aussitôt pour être
            // formulée, l'intitulé venant d'être recopié de la NC.
            /* ⚠️ PAS DE `okToast` ICI, et c'est délibéré : le message par
               défaut disait « Action corrective créée au plan » sans nommer
               laquelle ni dire où elle est partie. La RQ l'a relevé le
               11/10/2026 depuis l'autre porte (les suggestions) : « je ne sais
               pas où il est apparu ou parti ». On attend donc la réponse pour
               annoncer le fait AVEC sa référence. */
            const data = await smqData<{ action?: { documentId?: string; ref?: string } }>(
              `ged/nc/${nc.documentId}/action`, { echeance, responsable });
            if (data) {
              setOuvert(false); setEcheance(''); onFait();
              const ref = String(data.action?.ref || '');
              showToast(
                ref ? `Plan d’actions : ${ref} enclenchée depuis ${nc.ref}.` : 'Action créée au plan d’actions.',
                'info',
                { label: 'Voir le plan d’actions', faire: () => naviguerVers('actions') },
              );
              if (data.action?.documentId) onCreee(data.action.documentId);
            }
          }),
        }}
      />
      <div style={{ flex: '1 1 100%' }}><FieldError>{erreur}</FieldError></div>
    </div>
  );
}

/** Ce que chaque étape engage — le cycle n'était représenté nulle part : on
 *  voyait une pastille d'état isolée et un bouton pointant vers un mot dont on
 *  ignorait la place dans la séquence. */
/* Le triage précède le dossier : `soumise` n'est PAS une étape de la barre de
   progression — tant qu'elle n'est pas retenue, il n'y a pas de dossier à
   faire avancer. */

/**
 * Barre de progression du dossier.
 *
 * Elle donne le référentiel qui manquait : quelles étapes existent, où l'on en
 * est, et quand chacune a été franchie. Les dates viennent du journal signé —
 * seule source qui les porte réellement — plutôt que de champs qui n'existent
 * pas.
 */
/**
 * Une action corrective, instruite DEPUIS la fiche NC.
 *
 * Elle n'y était qu'un lien : constater qu'une action bloque la clôture obligeait
 * à quitter la NC, agir au plan d'actions, puis y revenir — pour un geste qui
 * tient en deux champs. Les routes sont celles du plan d'actions
 * (`ged/actions/:id/plan` et `/status`), pas des jumelles : une action modifiée
 * ici franchit exactement les mêmes contrôles et signe les mêmes événements.
 *
 * Ce qui reste au plan d'actions : la vérification d'efficacité (elle clôt
 * l'action et se signe), le journal et les liens. Le bouton « ouvrir » y mène.
 */
function ActionLiee({ a, onOuvrir }: { a: GedAction; onOuvrir: (a: GedAction) => void }) {
  const { smq, settings, session } = useGed();
  const [edition, setEdition] = useState(false);
  const [echeance, setEcheance] = useState(a.echeance || '');
  const [responsable, setResponsable] = useState(a.responsable || '');
  /* CE QUE LE FORMULAIRE A LU, FIGÉ À L'OUVERTURE DE L'ÉDITION (16/10/2026).
     Envoyé sous `base` : le moteur n'écrit que ce qui a changé ICI, et refuse
     (409) un champ qu'un autre a changé entre-temps — même règle que `SuiviAction`
     au plan d'actions. Sans elle, replanifier l'échéance renvoyait aussi le
     responsable lu à l'ouverture, par-dessus celui qu'un collègue venait de fixer. */
  const [base, setBase] = useState<Record<string, string>>({ echeance: a.echeance || '', responsable: a.responsable || '' });
  const ouvrirEdition = () => {
    const lu = { echeance: a.echeance || '', responsable: a.responsable || '' };
    setEcheance(lu.echeance); setResponsable(lu.responsable);
    setBase(lu);
    setEdition(true);
  };
  const { run, estOccupe } = useBusy();
  const gedUsers = settings?.gedUsers || [];
  const estRQ = session.roles.includes('rq');
  // Même règle qu'au plan d'actions : le RQ, ou la personne qui en répond.
  const peutAgir = estRQ || a.responsable === session.key;
  const meta = vocabMeta(ACTION_STATES, a.statut);
  const enRetard = !!a.echeance && a.statut !== 'verifiee' && (joursDepuis(a.echeance) ?? -1) > 0;

  // Resynchronisation PENDANT LE RENDU (et non dans un effet) quand l'action
  // affichée change — même motif que `Replanifier` au plan d'actions : un effet
  // qui appelle setState déclenche un rendu en cascade (React 19).
  const [vue, setVue] = useState(a.documentId);
  if (vue !== a.documentId) {
    setVue(a.documentId);
    setEcheance(a.echeance || '');
    setResponsable(a.responsable || '');
  }

  const transition = (statut: string, okToast: string) =>
    run(statut, () => smq(`ged/actions/${a.documentId}/status`, { statut }, okToast));

  return (
    <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 12px', display: 'grid', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{a.ref}</span>
        <span style={{ display: 'inline-flex', padding: '2px 9px', borderRadius: 'var(--radius-pill)', background: meta.bg, color: meta.fg, fontSize: 11, fontWeight: 700 }}>{meta.label}</span>
        {enRetard && (
          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '2px 9px', background: 'var(--red-50)', color: 'var(--red-600)' }}>en retard</span>
        )}
        <span style={{ flex: 1 }} />
        <Bouton taille="sm" onClick={() => onOuvrir(a)} title="Ouvrir au plan d’actions (efficacité, journal, liens)">Ouvrir →</Bouton>
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)', lineHeight: 1.35 }}>{a.titre}</div>

      {!edition ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 12.5 }}>
          <span style={{ color: 'var(--text-muted)' }}>
            Responsable : <strong style={{ color: 'var(--text-strong)' }}>{a.responsable ? userName(settings, a.responsable) : '—'}</strong>
          </span>
          <span style={{ color: 'var(--text-muted)' }}>
            Échéance : <strong style={{ color: enRetard ? 'var(--red-600)' : 'var(--text-strong)' }}>{a.echeance ? fmtDate(a.echeance) : 'aucune'}</strong>
          </span>
          <span style={{ flex: 1 }} />
          {peutAgir && a.statut !== 'verifiee' && (
            <>
              <Bouton taille="sm" onClick={ouvrirEdition}>
                <I n="pen" s={12} />{a.echeance ? 'Replanifier' : 'Fixer une échéance'}
              </Bouton>
              {/* Étapes du cycle d'une action LIÉE, pas l'action principale de l'écran : en contour,
                  la seule action pleine de la fiche reste celle du formulaire ouvert (UI-CONVENTIONS §1). */}
              {/* `title` : ce que le geste AFFIRME, repris du parcours de
                  l'action (`lib/parcours-action.ts`) — « démarrer » et
                  « marquer réalisée » se ressemblaient trop pour qu'un libellé
                  seul suffise. La phrase en clair est au pied de la fiche. */}
              {a.statut === 'ouverte' && (
                <Bouton taille="sm" title={prochainGeste('ouverte')?.explique}
                  onClick={() => void transition('encours', 'Action démarrée.')} chargement={estOccupe('encours')} disabled={estOccupe()}>
                  Démarrer
                </Bouton>
              )}
              {a.statut === 'encours' && (
                <Bouton taille="sm" title={prochainGeste('encours')?.explique}
                  onClick={() => void transition('realisee', 'Action marquée réalisée — en attente de vérification d’efficacité.')} chargement={estOccupe('realisee')} disabled={estOccupe()}>
                  Marquer réalisée
                </Bouton>
              )}
              {/* La vérification d'efficacité SIGNE la clôture de l'action : elle
                  reste au plan d'actions, où le texte de vérification est saisi. */}
              {a.statut === 'realisee' && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {estRQ ? 'Vérifiez l’efficacité au plan d’actions pour clôturer.' : 'En attente de vérification par le RQ.'}
                </span>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 10 }}>
          <div style={{ flex: '1 1 150px' }}>
            <label style={lbl} htmlFor={`ac-ech-${a.documentId}`}>Échéance</label>
            <input id={`ac-ech-${a.documentId}`} type="date" value={echeance} onChange={(e) => setEcheance(e.target.value)} style={inputStyle} />
          </div>
          <div style={{ flex: '1 1 170px' }}>
            <label style={lbl} htmlFor={`ac-resp-${a.documentId}`}>Responsable</label>
            <Selecteur id={`ac-resp-${a.documentId}`} value={responsable} onChange={setResponsable} style={inputStyle}>
              <option value="">—</option>
              {gedUsers.map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
            </Selecteur>
          </div>
          <PiedActions
            taille="sm"
            annuler={{ onClick: () => { setEcheance(a.echeance || ''); setResponsable(a.responsable || ''); setEdition(false); } }}
            principale={{
              libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('plan'), disabled: estOccupe(),
              onClick: () => void run('plan', async () => {
                const ok = await smq(`ged/actions/${a.documentId}/plan`, { echeance, responsable, base }, 'Action replanifiée — événement tracé.');
                if (ok) setEdition(false);
              }),
            }}
          />
        </div>
      )}
    </div>
  );
}

/** Le DERNIER franchissement d'un type d'événement, pas le premier : un même type peut
 *  revenir (reprise après dérogation, réouverture) et le premier daterait un cycle antérieur. */
function dateEvenement(journal: { evType: string; payload: string; ts: string }[], evType: string, contient?: string): string | undefined {
  const l = journal.filter((e) => e.evType === evType && (!contient || (e.payload || '').includes(contient)));
  return l.length ? l[l.length - 1].ts : undefined;
}

/**
 * LE TRIAGE D'UNE NC SOUMISE (RQ, 27/08/2026).
 *
 * « Je voudrais valider les NC ou les annuler d'office dès qu'elles sont
 * soumises. » Une déclaration entrait droit au registre : le compteur d'écarts
 * montait avant que quiconque ait dit si c'en était un.
 *
 * ⚠️ QUI TRANCHE : LA RQ ET LE COORDINATEUR QUALITÉ (RQ, 07/10/2026). Règle et
 * motifs : `backend/src/nc-retenue.ts`. Ceci ANNULE la décision du 30/08, qui
 * ouvrait le triage au pilote du processus visé — un champ que le déclarant
 * remplit lui-même.
 *
 * ⚠️ CE PANNEAU N'A PLUS BESOIN DE SE GARDER LUI-MÊME, et c'est la conséquence
 * de l'autre moitié de la décision : le dossier d'une déclaration non retenue
 * ne s'ouvre QU'AU RQ ET AU CQ (`peutTrancherNC`, registre et lien profond).
 * Quiconque lit ce panneau a donc le droit d'agir. Le serveur refuse quand
 * même — l'URL reste tapable.
 *
 * ⚠️ LE REJET SUPPRIME. Ce qui n'a pas été retenu comme non-conformité ne
 * laisse pas un dossier vide au registre — c'est la même décision que la
 * suppression, prise plus tôt.
 *
 * ⚠️ LE MOTIF N'EST EXIGÉ QUE POUR LE REJET (RQ, 05/10/2026).
 *
 * Il l'était des deux côtés. Côté rejet l'argument tient toujours, et il est
 * le plus fort des deux : le rejet SUPPRIME la déclaration, et une déclaration
 * écartée sans raison dite apprend au déclarant à ne plus déclarer.
 *
 * Côté validation, non. Retenir une NC, c'est ouvrir son dossier — et c'est
 * l'analyse, écran suivant, qui dira pourquoi. Le motif de triage faisait
 * écrire deux fois la même chose pour avancer d'un écran, et une case
 * obligatoire qu'on remplit pour passer ne documente rien : elle apprend à
 * écrire « ok ».
 *
 * ⚠️ RETENIR RESTE CONFIRMÉ, SANS RIEN À ÉCRIRE. Ce n'est pas un retour
 * déguisé de l'obligation : une fois la NC retenue, elle est au registre et
 * n'en sort plus qu'au terme d'une analyse (le serveur refuse la suppression
 * aux stades « déclarée » et « analyse »). Un clic de travers n'a donc pas de
 * marche arrière — la confirmation nomme la NC et dit ce qui se passe, elle ne
 * demande pas de justification.
 */
function TriageNC({ nc }: { nc: GedNC }) {
  const { smq, refresh, session } = useGed();
  /* Voir une déclaration n'est pas la trancher (13/10/2026) : le CQ ouvre le
     dossier, mais retenir ou rejeter est au RQ — ou à qui les rôles fins
     l'accordent (acte C13). Le serveur fait foi ; ceci évite d'offrir des
     boutons qui seraient refusés. */
  const peutTrancher = peutTrancherNC(session.roles, session.actes);
  /* L'état ne vaut plus que pour le REJET : la validation n'a pas de panneau à
     ouvrir, elle passe par la confirmation de l'application. */
  const [rejet, setRejet] = useState(false);
  const [motif, setMotif] = useState('');
  const { run, estOccupe } = useBusy();
  const { demander, confirmation } = useConfirmation();
  const court = motif.trim().length < MOTIF_DEROGATION_MIN;

  /* La question se pose AVANT `run` : pendant qu'une confirmation est ouverte,
     rien n'est « en cours » — le bouton ne doit pas afficher « En cours… »
     tant que personne n'a répondu. */
  const retenir = async () => {
    const ok = await demander(
      `Retenir ${nc.ref} comme non-conformité ?\n\nSon dossier s’ouvre et elle est comptée au registre des écarts. `
      + 'Elle n’en sortira plus qu’au terme de son analyse : le rejet d’office, lui, n’est possible qu’avant.',
      { titre: 'Retenir la non-conformité', confirmer: 'Retenir et ouvrir le dossier' },
    );
    if (!ok) return;
    await run('tri', async () => {
      // Aucun `motif` dans le corps : le serveur ne l'exige plus pour `valider`.
      const fait = await smq(`ged/nc/${nc.documentId}/triage`, { decision: 'valider' },
        'Non-conformité retenue — le dossier est ouvert.', `Triage de ${nc.ref}`);
      if (fait) await refresh();
    });
  };

  if (!peutTrancher) {
    return (
      <div style={{ ...card, padding: '14px 16px', display: 'grid', gap: 6, borderInlineStart: '3px solid var(--amber-500)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <I n="alert-triangle" s={14} />
          <strong style={{ fontSize: 13 }}>Déclaration en attente de validation</strong>
        </div>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Elle n’est pas encore comptée comme un écart. Seul le responsable qualité la retient ou la rejette.
        </span>
      </div>
    );
  }

  return (
    <div style={{ ...card, padding: '14px 16px', display: 'grid', gap: 10, borderInlineStart: '3px solid var(--amber-500)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <I n="alert-triangle" s={14} />
        <strong style={{ fontSize: 13 }}>Déclaration à trancher</strong>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Elle n’est pas encore comptée comme un écart. La retenir ouvre le dossier ; la rejeter la supprime.
        </span>
      </div>

      {!rejet ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/* Retenir : un seul geste, rien à écrire — la confirmation suffit. */}
          <Bouton variante="primaire" taille="sm" chargement={estOccupe('tri')}
            libelleChargement="En cours…" onClick={retenir}>
            <I n="check" s={13} />Retenir la non-conformité
          </Bouton>
          <Bouton variante="danger" taille="sm" onClick={() => setRejet(true)}>
            <I n="x" s={13} />Rejeter d’office
          </Bouton>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 6 }}>
          <label style={lbl} htmlFor={`tri-${nc.ref}`}>
            Pourquoi elle est rejetée <span style={{ color: 'var(--red-600)' }}>*</span>
          </label>
          <textarea id={`tri-${nc.ref}`} value={motif} rows={2} onChange={(e) => setMotif(e.target.value)}
            placeholder="Ex. doublon de BIO24-NC-2026-031, déjà déclarée le même jour."
            style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
          {/* ⚠️ On ne promet PLUS que le motif est « communiqué au déclarant » :
              le rejet supprime la fiche, ses liens et son journal, et aucun
              courriel ne part. Le motif oblige à formuler la décision avant
              qu'il n'en reste rien — c'est tout ce qu'il fait, et c'est ce que
              la phrase dit maintenant. */}
          <span style={{ fontSize: 11.5, color: court ? 'var(--red-600)' : 'var(--text-muted)' }}>
            {motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — à dire au déclarant, rien ne le lui transmet.
          </span>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--red-600)' }}>
            Le rejet <strong>supprime définitivement</strong> la déclaration : la fiche, ses liens et son journal.
          </p>
          <PiedActions
            taille="sm"
            annuler={{ onClick: () => { setRejet(false); setMotif(''); } }}
            principale={{
              // Le rejet SUPPRIME : bouton danger (UI-CONVENTIONS §4).
              variante: 'danger',
              libelle: 'Confirmer le rejet et supprimer',
              libelleChargement: 'En cours…', chargement: estOccupe('tri'), disabled: court || estOccupe('tri'),
              onClick: () => run('tri', async () => {
                const ok = await smq(`ged/nc/${nc.documentId}/triage`, { decision: 'rejeter', motif: motif.trim() },
                  'Déclaration rejetée et supprimée.', `Triage de ${nc.ref}`);
                if (ok) await refresh();
              }),
            }}
          />
        </div>
      )}
      {confirmation}
    </div>
  );
}

/**
 * SUPPRIMER TOTALEMENT UNE NON-CONFORMITÉ (RQ, 27/08/2026).
 *
 * « Au lieu de me donner la main pour annuler une NC, me donner la main pour la
 * supprimer totalement. Elle disparaît totalement. Ceci n'est possible que si
 * l'analyse est déjà faite. »
 *
 * L'annulation laissait un dossier `annulee` au registre : visible, comptable,
 * et sans objet. La suppression retire la fiche, ses liens et sa chaîne de
 * journal.
 *
 * ⚠️ AUCUNE TRACE — c'est un choix de la RQ, tranché le 30/08/2026 après que je
 * lui aie signalé que l'ISO 15189 §8.7 attend qu'une non-conformité signalée
 * laisse une trace de son existence, et qu'un auditeur demandant « avez-vous
 * écarté des signalements ? » n'aurait pas de réponse dans l'outil.
 *
 * ⚠️ SEULEMENT UNE FOIS L'ANALYSE FAITE. Le serveur refuse aux stades
 * « déclarée » et « analyse » : on ne supprime pas une NC qu'on n'a pas
 * regardée, ce serait un classement sans suite déguisé. Le bouton dit alors
 * pourquoi, plutôt que d'échouer à l'appel.
 */
function SupprimerNC({ nc, liees }: { nc: GedNC; liees: GedAction[] }) {
  const { smq, refresh } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const bloquantes = liees.filter((a) => AC_BLOQUANTES.includes(a.statut));
  const analyseFaite = ['traitement', 'cloturee'].includes(nc.statut);

  if (!ouvert) {
    return (
      <Bouton variante="danger" taille="sm" onClick={() => setOuvert(true)} disabled={!analyseFaite}
        title={analyseFaite
          ? 'Supprimer définitivement cette non-conformité : la fiche, ses liens et son journal'
          : 'La suppression n’est possible qu’une fois l’analyse faite — menez-la à son terme, ou rejetez la déclaration au triage.'}
        style={{ marginInlineStart: 'auto' }}>
        <I n="trash" s={13} />Supprimer…
      </Bouton>
    );
  }

  return (
    <div style={{ flex: '1 1 100%' }}>
      <SuppressionRaisonnee
        titre="Supprimer définitivement cette non-conformité"
        objet={<><strong>{nc.ref}</strong> — {nc.titre}</>}
        nature="suppression"
        consequences={[
          ...bloquantes.map((a) => ({
            cle: a.ref,
            texte: `${a.ref} en découle et n’est pas soldée (${vocabMeta(ACTION_STATES, a.statut).label.toLowerCase()}).`,
            gravite: 'bloquant' as const,
            detail: 'Solder ou annuler cette action au plan d’abord : du travail planifié ne disparaît pas parce que son origine disparaît.',
          })),
          {
            cle: 'definitif',
            texte: 'La fiche, ses liens et son journal sont effacés — sans trace, et sans retour.',
            gravite: 'attention' as const,
            detail: 'La référence ne sera pas réattribuée, mais plus rien dans l’outil ne dira que cette non-conformité a existé. '
              + 'L’ISO 15189 §8.7 attend qu’un signalement laisse une trace : c’est une décision assumée de la responsable qualité.',
          },
          {
            cle: 'compteurs',
            texte: 'Elle sort des compteurs et des indicateurs de revue.',
            gravite: 'info' as const,
            detail: 'Elle n’y sera comptée ni comme ouverte, ni comme clôturée.',
          },
        ]}
        note={(
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={lbl} htmlFor={`suppr-${nc.ref}`}>
              Motif de la suppression <span style={{ color: 'var(--red-600)' }}>*</span>
            </label>
            <textarea id={`suppr-${nc.ref}`} value={motif} rows={2}
              onChange={(e) => setMotif(e.target.value)}
              placeholder="Ex. après analyse, le constat relevait d’un défaut de saisie et non d’un écart."
              style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
            {/* Le motif part au serveur, qui l'exige — mais il ne survivra PAS
                à la suppression, puisque le journal du dossier part avec lui.
                Le dire évite de croire qu'on écrit pour l'audit. */}
            <span>{motif.trim().length}/{MOTIF_DEROGATION_MIN} caractères minimum — exigé par le serveur, mais effacé avec le dossier.</span>
          </div>
        )}
        libelleAction="Supprimer définitivement"
        onConfirmer={async () => {
          if (motif.trim().length < MOTIF_DEROGATION_MIN) {
            return `Motif trop court : ${MOTIF_DEROGATION_MIN} caractères au minimum.`;
          }
          const ok = await smq(`ged/nc/${nc.documentId}`, { motif: motif.trim() },
            'Non-conformité supprimée.', `Suppression de ${nc.ref}`, 'DELETE');
          if (ok) await refresh();
          return ok ? '' : 'La suppression n’a pas abouti.';
        }}
        onAnnuler={() => { setOuvert(false); setMotif(''); }} />
    </div>
  );
}

/**
 * Clôture de la non-conformité — ISO 15189 §8.7.2.
 *
 * La règle et son bandeau vivent dans `components/Cloture.tsx`, partagés avec
 * l'audit et la réclamation qui portent la même exigence. Ce qui reste ici est
 * ce qui est PROPRE à la non-conformité : quelles actions bloquent, et ce
 * qu'on dit à qui ne peut pas déroger.
 *
 * `bloquantes` est recalculée à chaque rendu depuis les actions du magasin —
 * elle n'est écrite nulle part. Une action terminée dans le panneau ouvert
 * par-dessus (N2-4) fait disparaître son obstacle au rendu suivant.
 */
function ClotureNC({ nc, liees, estRQ, onOuvrirAction, onOuvrirAnalyse }: {
  nc: GedNC; liees: GedAction[]; estRQ: boolean; onOuvrirAction: (a: GedAction) => void;
  /** Amène à la rubrique CAPA quand c'est elle qui bloque. */
  onOuvrirAnalyse: () => void;
}) {
  const { smq } = useGed();
  const bloquantes = liees.filter((a) => AC_BLOQUANTES.includes(a.statut));
  // La rubrique CAPA du dossier — distincte des actions du plan. Le serveur
  // porte la même règle (`services/cloture.ts`) ; l'afficher ici évite au RQ de
  // découvrir l'exigence après avoir écrit un motif de dérogation inutile.
  const capaManquante = !String(nc.actionCorrective || '').trim();

  return (
    <BlocageCloture
      objet="la non-conformité"
      refDossier={nc.ref}
      estRQ={estRQ}
      blocages={[
        ...(capaManquante ? [{
          ref: '',
          libelle: 'action corrective non renseignée (étape 5, Traitement)',
          derogeable: false,
          onOuvrir: onOuvrirAnalyse,
        }] : []),
        ...bloquantes.map((a) => ({
          ref: a.ref,
          libelle: vocabMeta(ACTION_STATES, a.statut).label,
          onOuvrir: () => onOuvrirAction(a),
        })),
      ]}
      // Le libellé promettait « efficacité vérifiée » ; le seuil retenu est la
      // réalisation, la vérification pouvant suivre. On ne promet que ce qu'on tient.
      label="Clôturer la non-conformité"
      question={`Clôturer ${nc.ref} ? L’événement sera signé et le statut ne pourra plus redescendre.`}
      ancreRegle="nc.cloture"
      aide="Une non-conformité ne se clôture pas sans que l’action corrective ait été écrite, ni tant que les actions qui en découlent n’ont pas été menées (ISO 15189 §8.7.2). La vérification d’efficacité, elle, peut suivre la clôture."
      indiceNonRQ={capaManquante
        ? 'Renseignez l’action corrective à l’étape 5, Traitement — ou écrivez pourquoi aucune n’est nécessaire : aucune dérogation ne la remplace.'
        : 'Terminez ces actions, ou demandez une dérogation au responsable qualité.'}
      onCloturer={(motifDerogation, responsable) => smq(
        // `autorisation`, pas `responsable` : ce dernier nom désigne déjà, sur
        // cette même route, qui PREND EN CHARGE la NC.
        `ged/nc/${nc.documentId}/status`, { statut: 'cloturee', motifDerogation, autorisation: responsable },
        'NC clôturée — événement signé.')} />
  );
}

/**
 * Panne d'équipement — les rubriques propres au registre de maintenance.
 *
 * Le code cause (H/F/M/S) répond à « d'où vient la panne » : une fausse
 * manœuvre, une pièce morte, un entretien négligé, une mise à jour. Il ne
 * remplace PAS l'analyse de cause de la non-conformité, qui répond à une autre
 * question — pourquoi le système ne l'a pas empêchée. Les confondre reviendrait
 * à clore une NC sur « pièce défectueuse », ce qui n'a jamais rien corrigé.
 */
/**
 * Rubriques de la campagne d'évaluation externe, sur le même principe que la
 * panne d'équipement : le résultat hors limites EST la non-conformité, il n'a
 * pas de registre de traitement à part.
 *
 * Les valeurs sont RECOPIÉES de l'EEQ à la déclaration et non relues : une
 * campagne archivée ou corrigée ne doit pas réécrire après coup le constat sur
 * lequel une analyse de cause a été signée.
 */
function BandeauEeq({ nc, enPanneau = false }: { nc: GedNC; enPanneau?: boolean }) {
  const { eeqs, go } = useGed();
  const campagne = nc.eeqRef ? eeqs.find((x) => x.ref === nc.eeqRef) : undefined;
  const z = nc.eeqZscore;
  // Même code couleur que le registre : au-delà de 3, l'écart est avéré.
  const tonZ = z == null ? 'var(--text-strong)'
    : Math.abs(z) >= 3 ? 'var(--red-600)' : Math.abs(z) >= 2 ? 'var(--amber-600)' : 'var(--green-600)';
  return (
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '10px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Programme :</strong>{' '}
        {nc.eeqProgramme || <em style={{ color: 'var(--text-muted)' }}>non renseigné</em>}
        {nc.eeqOrganisme ? ` · ${nc.eeqOrganisme}` : ''}
      </span>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Paramètre :</strong>{' '}
        {nc.eeqAnalyte || <em style={{ color: 'var(--text-muted)' }}>non renseigné</em>}
      </span>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>z-score :</strong>{' '}
        {z == null ? <em style={{ color: 'var(--text-muted)' }}>—</em>
          : <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: tonZ }}>{z}</span>}
      </span>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Campagne :</strong>{' '}
        {nc.eeqDate ? fmtDate(nc.eeqDate) : <em style={{ color: 'var(--text-muted)' }}>date inconnue</em>}
      </span>
      {/* Retour au registre — la campagne peut avoir été retirée depuis.
          En panneau, ce bouton quitterait la superposition : on affiche la
          référence sans promettre un voyage (cf. `ActionDossier`). */}
      {campagne && (enPanneau
        ? <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-primary-active)' }}>{nc.eeqRef}</span>
        : <Bouton taille="sm" onClick={() => go('eeq')}
          title={`Ouvrir le registre EEQ (${nc.eeqRef})`}
          style={{ fontFamily: 'var(--font-mono)' }}>{nc.eeqRef} →</Bouton>
      )}
    </div>
  );
}

function BandeauPanne({ nc, enPanneau = false }: { nc: GedNC; enPanneau?: boolean }) {
  const { equips, ouvrirPanneau } = useGed();
  const eq = equips.find((x) => x.code === nc.equipementCode);
  const cause = nc.causeEquip ? CAUSES_PANNE[nc.causeEquip] : '';
  return (
    <div style={{ display: 'grid', gap: 8 }}>
    <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '10px 14px', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5 }}>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Équipement :</strong>{' '}
        {eq
          ? (enPanneau
            // En panneau, ouvrir la fiche d'équipement quitterait la
            // superposition : on nomme l'appareil, on n'y emmène pas.
            ? <span style={{ fontFamily: 'var(--font-mono)' }}>{nc.equipementCode} — {eq.nom}</span>
            // L'appareil en panne est l'objet LIÉ à la NC qu'on lit : sa fiche
            // s'ouvre PAR-DESSUS, la NC reste là où on l'a laissée.
            // `eq.documentId` peut manquer sur un équipement repris du tableur :
            // le panneau accepte aussi le CODE, qu'il sait retrouver.
            : <Bouton taille="sm" onClick={() => ouvrirPanneau({ type: 'equip', valeur: eq.documentId ?? eq.code, origine: nc.ref })}
              title={`Ouvrir la fiche ${eq.code}`}
              style={{ fontFamily: 'var(--font-mono)' }}>{nc.equipementCode} — {eq.nom} →</Bouton>
          )
          // L'appareil a pu être réformé et retiré du parc : la NC, elle, reste.
          : <span style={{ fontFamily: 'var(--font-mono)' }}>{nc.equipementCode} <em style={{ color: 'var(--text-muted)' }}>(hors parc)</em></span>}
      </span>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Cause :</strong>{' '}
        {cause ? <>{nc.causeEquip} — {cause}</> : <em style={{ color: 'var(--text-muted)' }}>non déterminée</em>}
      </span>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Arrêt :</strong>{' '}
        {nc.dureeArret || <em style={{ color: 'var(--text-muted)' }}>non renseigné</em>}
      </span>
      {nc.importe && (
        <span title="Ligne reprise de la fiche de vie papier : le statut « déclarée » signifie qu’aucune clôture n’a été prononcée dans l’outil, pas que la panne est en cours."
          style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)', cursor: 'help' }}>
          reprise du registre papier
        </span>
      )}
      <DeclarerCurative nc={nc} />
    </div>
    <ReparationsNC nc={nc} />
    </div>
  );
}

/** Les interventions de la fiche de vie faites POUR cette NC (RQ, 03/11/2026) :
 *  la panne et sa réparation se lisent ensemble, depuis l'une comme l'autre. */
function ReparationsNC({ nc }: { nc: GedNC }) {
  const { equips } = useGed();
  const eq = equips.find((x) => x.code === nc.equipementCode);
  const reparations = (eq?.interventions ?? []).filter((iv) => iv.ncRef === nc.ref)
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
  return (
    <>
    {reparations.length > 0 && (
      <div style={{ display: 'grid', gap: 4, fontSize: 12.5, padding: '0 14px' }}>
        {reparations.map((iv) => (
          <span key={iv.id}>
            <strong style={{ color: 'var(--text-strong)' }}>Maintenance {iv.nature === 'curative' ? 'curative ' : ''}du {fmtDate(iv.date)}</strong>
            {iv.modalite === 'telemaintenance' ? ' · télémaintenance' : iv.modalite === 'sur_site' ? ' · sur site' : ''}
            {iv.prestataire ? ` · ${iv.prestataire}` : ''}
            {iv.conforme === false ? <span style={{ color: 'var(--red-600)', fontWeight: 600 }}> · non conforme</span> : iv.conforme ? ' · conforme' : ''}
            {iv.commentaire ? <span style={{ color: 'var(--text-muted)' }}> — {iv.commentaire}</span> : null}
          </span>
        ))}
      </div>
    )}
    </>
  );
}

/** Déclarer la réparation SANS quitter la NC (RQ, 03/11/2026) : la fenêtre
 *  unique de saisie, ouverte sur « Curative », l'intervention gardant la
 *  référence de la NC. Dans le bandeau de l'appareil, sous l'intitulé, sur
 *  tous les onglets — en lecture, en correction comme en panneau. */
function DeclarerCurative({ nc }: { nc: GedNC }) {
  const { equips } = useGed();
  const [saisie, setSaisie] = useState(false);
  const eq = equips.find((x) => x.code === nc.equipementCode);
  if (!eq?.documentId || nc.statut === 'cloturee') return null;
  // Plein tant que rien n'est consigné : c'est la chose à faire ; discret ensuite.
  const dejaFaite = (eq.interventions ?? []).some((iv) => iv.ncRef === nc.ref);
  return (
    <>
      <Bouton taille="sm" variante={dejaFaite ? undefined : 'primaire'} onClick={() => setSaisie(true)} style={{ marginInlineStart: 'auto' }}
        title={`Consigner au registre de ${eq.code} la maintenance curative faite pour cette non-conformité`}>
        <I n="wrench" s={13} />{dejaFaite ? 'Déclarer une autre maintenance curative' : 'Déclarer la maintenance curative'}
      </Bouton>
      {saisie && (
        <SaisirMaintenance equipementFixe={eq.code} occurrences={[]} onAcquitter={() => undefined}
          typeInitial="curative" ncRef={nc.ref}
          preRempli={{ dureeArret: nc.dureeArret || '', commentaire: nc.correction || '' }}
          onFerme={() => setSaisie(false)} onFait={() => undefined} />
      )}
    </>
  );
}

/**
 * Impact sur les résultats rendus (§7.5).
 *
 * Le bandeau est toujours affiché, jamais masqué quand la réponse est « non » :
 * c'est justement l'information qu'un auditeur vient chercher, et une réponse
 * absente de l'écran se lit comme une question jamais posée.
 *
 * Il vire au rouge dans deux cas seulement : des résultats sont touchés sans
 * qu'on dise ce qu'on en a fait, ou la non-conformité est clôturée alors que la
 * question n'a jamais reçu de réponse.
 */
/**
 * LES DOSSIERS CONCERNÉS — des lignes, pas un compteur (RQ, 07/10/2026).
 *
 * « Je veux que ce soit une seule NC mais avec la possibilité de dire que
 * plusieurs dossiers sont impactés. Dans cet encart, je voudrais qu'on puisse
 * rajouter des lignes pour identifier chacun des dossiers impactés. »
 *
 * Le cas : un centre de dialyse adresse chaque mois des prescriptions sans
 * prélèvement. Une non-conformité par occurrence aurait fait dix dossiers à
 * analyser et clôturer pour un seul et même fait ; un compteur ne permet pas de
 * dire au correspondant LESQUELS de ses dossiers sont en cause.
 *
 * ⚠️ Le texte est LIBRE, par décision explicite. Le structurer aurait exigé un
 * lien au système informatique du laboratoire que la plateforme n'a pas : on
 * aurait obtenu trois champs dont deux restent vides, ce qui se lit comme une
 * saisie bâclée alors que c'est l'outil qui manque.
 *
 * ⚠️ La DATE de chaque ligne commande le relevé mensuel : une non-conformité
 * déclarée le 3 porte des dossiers jusqu'au 31, et c'est la ligne — non la
 * fiche — qui dit de quel mois chacun relève.
 */
/* `LigneDossier` vient de `lib/dossiers-saisie.ts` — même forme que
   `DossierConcerne` côté moteur. Une seconde définition ici aurait divergé le
   jour où l'une des deux gagne un champ. */

/**
 * LA SAISIE RAPIDE DES DOSSIERS, À LA DÉCLARATION (RQ, 11/10/2026).
 *
 * « Je tape un numéro et j'appuie sur entrée, puis un autre, puis un autre selon
 * le besoin. »
 *
 * ⚠️ POURQUOI PAS `EditeurDossiers` ICI. L'éditeur de la fiche pose un champ
 * texte ET un champ date par dossier : trois gestes par numéro, et une colonne
 * de dates vides au moment où personne ne les connaît encore. Au moment de
 * déclarer, on a les numéros sous les yeux et on les tape à la suite. La date se
 * précise ensuite sur la fiche, ligne par ligne — le moteur retombe d'ici là sur
 * la date de la non-conformité pour le relevé du correspondant.
 *
 * ⚠️ LA SAISIE EN COURS N'EST PAS PERDUE : `DeclareForm` rejoue
 * `ajouterDossiers` sur ce champ au moment de créer. Un numéro tapé sans
 * « Entrée » est la faute la plus probable de cette saisie, et un champ vidé par
 * l'envoi sans que rien n'arrive nulle part est une perte muette.
 */
function SaisieDossiers({ idBase, lignes, saisie, onSaisie, onChange }: {
  idBase: string; lignes: LigneDossier[]; saisie: string;
  onSaisie: (v: string) => void; onChange: (l: LigneDossier[]) => void;
}) {
  /* Le refus se DIT. Un numéro déjà listé qui disparaît du champ sans un mot se
     lit comme un bug, et on le retape. */
  const [refus, setRefus] = useState('');
  const entrer = () => {
    const r = ajouterDossiers(lignes, saisie);
    setRefus(phraseDoublons(r.doublons));
    // Champ vide (ou n'ayant porté que des séparateurs) : on ne touche à rien.
    if (!r.ajoutes.length && !r.doublons.length) return;
    if (r.ajoutes.length) onChange(r.lignes);
    onSaisie('');
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {lignes.length > 0 && (
        <ul aria-label="Dossiers ajoutés" style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {lignes.map((l) => (
            <li key={l.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-pill)', padding: '4px 5px 4px 11px', fontSize: 12.5, fontWeight: 600, color: 'var(--text-strong)' }}>
              {l.texte}
              {/* `aria-label` EN TOUTES LETTRES : « Retirer » seul, répété dix
                  fois, ne dit pas lequel part (convention des boutons, §6). */}
              <button type="button" title="Retirer de la liste"
                aria-label={`Retirer le dossier ${l.texte}`}
                onClick={() => { setRefus(''); onChange(retirerDossier(lignes, l.id)); }}
                style={{ display: 'grid', placeItems: 'center', width: 19, height: 19, padding: 0, border: 'none', borderRadius: '50%', background: 'var(--surface-sunken)', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <I n="x" s={11} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 240px' }}>
          <input id={`${idBase}-saisie`} value={saisie} maxLength={DOSSIER_TEXTE_MAX}
            aria-label="Numéro du dossier concerné — Entrée pour l’ajouter à la liste"
            placeholder="Ex. 2026-4411, puis Entrée"
            onChange={(e) => { onSaisie(e.target.value); if (refus) setRefus(''); }}
            onKeyDown={(e) => {
              /* ⚠️ `preventDefault` : « Entrée » dans un champ ajoute un dossier
                 et RIEN d'autre. Sans lui, la touche validerait le formulaire
                 englobant le jour où la déclaration passera dans un `<form>` —
                 on croirait avoir ajouté une ligne, on aurait créé la NC. */
              if (e.key === 'Enter') { e.preventDefault(); entrer(); }
            }}
            style={inputStyle} />
        </div>
        <Bouton taille="sm" onClick={entrer} disabled={!saisie.trim()}>
          <I n="plus" s={12} />Ajouter
        </Bouton>
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
        {lignes.length === 0
          ? 'Tapez un numéro et appuyez sur Entrée ; recommencez autant de fois qu’il y a de dossiers. Sans ligne, on sait combien sans savoir lesquels.'
          : `${lignes.length} dossier(s) dans la liste. Entrée pour le suivant ; la croix en retire un.`}
      </span>
      {/* Un collage se découpe : une colonne copiée du SIL ne doit pas devenir
          une ligne unique de dix numéros, qui ne compte que pour un. */}
      {refus && <span style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>{refus}</span>}
      {/* Un NOM au lieu d'un numéro partirait tel quel au correspondant (14/10/2026). */}
      {phraseNoms(lignes) && <span role="alert" style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>{phraseNoms(lignes)}</span>}
    </div>
  );
}

function EditeurDossiers({ idBase, lignes, onChange }: {
  idBase: string; lignes: LigneDossier[]; onChange: (l: LigneDossier[]) => void;
}) {
  /* L'identifiant est tiré d'un compteur local, PAS de l'index : retirer la
     deuxième ligne de trois ferait sinon glisser les clés et React réutiliserait
     le champ de la troisième pour la deuxième — on tape dans la mauvaise. */
  const suivant = React.useRef(1);
  const ajouter = () => {
    let id = `n${suivant.current}`;
    suivant.current += 1;
    while (lignes.some((l) => l.id === id)) { id = `n${suivant.current}`; suivant.current += 1; }
    onChange([...lignes, { id, texte: '' }]);
  };
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {lignes.map((l, i) => (
        <div key={l.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: '3 1 240px' }}>
            <input value={l.texte} aria-label={`Dossier concerné ${i + 1}`}
              id={`${idBase}-d-${l.id}`}
              placeholder="Ex. 2026-4411, ionogramme — un numéro, jamais un nom"
              onChange={(e) => onChange(lignes.map((x) => (x.id === l.id ? { ...x, texte: e.target.value } : x)))}
              style={inputStyle} />
          </div>
          <div style={{ flex: '1 1 150px' }}>
            <input type="date" value={l.date || ''} aria-label={`Date du dossier ${i + 1}`}
              onChange={(e) => onChange(lignes.map((x) => (x.id === l.id ? { ...x, date: e.target.value } : x)))}
              style={inputStyle} />
          </div>
          <Bouton taille="sm" aria-label={`Retirer le dossier ${i + 1}`}
            onClick={() => onChange(lignes.filter((x) => x.id !== l.id))}>Retirer</Bouton>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Bouton taille="sm" onClick={ajouter} style={{ width: 'fit-content' }}>
          <I n="plus" s={12} />Ajouter un dossier
        </Bouton>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {lignes.length === 0
            ? 'Une ligne par dossier. Sans ligne, on sait combien sans savoir lesquels.'
            : `${lignes.length} dossier(s) — la date de chaque ligne commande le relevé mensuel du correspondant.`}
        </span>
      </div>
      {phraseNoms(lignes) && <span role="alert" style={{ fontSize: 12.5, color: 'var(--amber-700)' }}>{phraseNoms(lignes)}</span>}
    </div>
  );
}

/** La question « des résultats patients sont-ils concernés ? » et ce qui en
 *  dépend — les dossiers, le nombre, ce qu'on a fait. Partagée par le bandeau
 *  (lecture, puis « Répondre ») et par la correction de la fiche, où elle
 *  s'affiche d'emblée comme la gravité (RQ, 03/11/2026). */
function ChampsImpact({ idBase, f, onChange, dossiers, onDossiers }: {
  idBase: string;
  f: { impactResultats: string; nbResultatsConcernes: string; modaliteRappel: string };
  onChange: (f: { impactResultats: string; nbResultatsConcernes: string; modaliteRappel: string }) => void;
  dossiers: LigneDossier[]; onDossiers: (l: LigneDossier[]) => void;
}) {
  return (
    <>
        <div style={ENTETE_I}><span style={{ ...lbl, marginBottom: 0 }}>Des résultats patients sont-ils concernés ?</span><PointDeRegle ancre="nc.impactResultats" /></div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {IMPACTS_RESULTATS.map((o) => {
            const on = f.impactResultats === o.key;
            return (
              <button key={o.key} onClick={() => onChange({ ...f, impactResultats: o.key })} aria-pressed={on} title={o.aide}
                style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer', border: `1.5px solid ${on ? o.fg : 'var(--border-default)'}`, background: on ? o.bg : 'var(--surface-card)', color: on ? o.fg : 'var(--text-muted)' }}>
                {o.label}
              </button>
            );
          })}
        </div>
        {/* LES DOSSIERS CONCERNÉS — hors de la condition « des résultats sont
            concernés », et c'est le cœur de l'arbitrage du 07/10/2026 : une
            prescription sans prélèvement ne produit AUCUN résultat et met
            pourtant N dossiers en cause. Les enfermer sous « Oui » aurait rendu
            le cas d'origine insaisissable. */}
        <div style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
          <span style={{ ...lbl, marginBottom: 0 }}>Dossiers concernés</span>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Une ligne par dossier, même si aucun résultat n’a été rendu. C’est cette liste qui
            permet de dire au correspondant lesquels de ses dossiers sont en cause.
          </span>
          <EditeurDossiers idBase={idBase} lignes={dossiers} onChange={onDossiers} />
        </div>
        {f.impactResultats === 'oui' && (
          <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 14px' }}>
            <div><label style={lbl} htmlFor={`${idBase}-nb`}>Combien, approximativement ?</label>
              {/* ⚠️ DÉRIVÉ dès qu'il y a des lignes : deux chiffres pour la même
                  chose, c'est la garantie qu'un des deux mente. Le serveur
                  applique la même règle (`nc-dossiers.ts`). */}
              {dossiers.filter((x) => x.texte.trim()).length > 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-body)', padding: '9px 0' }}>
                  <strong>{dossiers.filter((x) => x.texte.trim()).length}</strong> — compté sur les dossiers listés ci-dessus.
                </div>
              ) : (
                <input id={`${idBase}-nb`} value={f.nbResultatsConcernes}
                  onChange={(e) => onChange({ ...f, nbResultatsConcernes: e.target.value })}
                  placeholder="Ex. 12 comptes rendus du 14 au 16/04" style={inputStyle} />
              )}</div>
            <div><label style={lbl} htmlFor={`${idBase}-rap`}>Qu’a-t-on fait ?</label>
              <input id={`${idBase}-rap`} value={f.modaliteRappel}
                onChange={(e) => onChange({ ...f, modaliteRappel: e.target.value })}
                placeholder="Rappel, correction, information du prescripteur…" style={inputStyle} /></div>
          </div>
        )}
    </>
  );
}

function BandeauImpact({ nc, peutModifier }: { nc: GedNC; peutModifier: boolean }) {
  const { smq } = useGed();
  const impact = nc.impactResultats || 'inconnu';
  const meta = IMPACTS_RESULTATS.find((o) => o.key === impact) || IMPACTS_RESULTATS[2];
  const clot = nc.statut === 'cloturee';
  const alerte = (impact === 'oui' && !(nc.modaliteRappel || '').trim()) || (clot && impact === 'inconnu');
  const [edition, setEdition] = useState(false);
  const [f, setF] = useState({
    impactResultats: impact,
    nbResultatsConcernes: nc.nbResultatsConcernes || '',
    modaliteRappel: nc.modaliteRappel || '',
  });
  const [dossiers, setDossiers] = useState<LigneDossier[]>(nc.dossiersConcernes || []);
  /* CE QUE LE BANDEAU A LU EN S'OUVRANT (14/10/2026), envoyé sous `base` : le
     moteur n'écrit que ce qui a changé ICI. Sans elle, répondre « non » à
     l'impact renvoyait aussi la modalité de rappel et les dossiers lus à
     l'ouverture — et effaçait ceux qu'un collègue venait d'ajouter. Figée au
     clic « Répondre », pas au montage : le bandeau vit longtemps ouvert à
     l'écran sans formulaire. */
  const [baseImpact, setBaseImpact] = useState<Record<string, unknown> | null>(null);
  const ouvrirImpact = () => {
    const lu = { impactResultats: impact, nbResultatsConcernes: nc.nbResultatsConcernes || '', modaliteRappel: nc.modaliteRappel || '' };
    setF(lu);
    setDossiers(nc.dossiersConcernes || []);
    setBaseImpact({ ...lu, dossiersConcernes: (nc.dossiersConcernes || []).filter((x) => x.texte.trim()) });
    setEdition(true);
  };
  const { run, estOccupe } = useBusy();

  // Resynchronisation PENDANT LE RENDU quand la NC affichée change — un effet
  // qui appelle setState déclenche un rendu en cascade (React 19), et
  // `react-hooks/set-state-in-effect` est une ERREUR de lint dans ce module.
  const [vue, setVue] = useState(nc.documentId);
  if (vue !== nc.documentId) {
    setVue(nc.documentId);
    setEdition(false);
    setF({ impactResultats: impact, nbResultatsConcernes: nc.nbResultatsConcernes || '', modaliteRappel: nc.modaliteRappel || '' });
    setDossiers(nc.dossiersConcernes || []);
  }

  const cadre: React.CSSProperties = {
    background: alerte ? 'var(--red-50)' : 'var(--surface-sunken)',
    borderInlineStart: alerte ? '3px solid var(--red-500)' : undefined,
    borderRadius: 'var(--radius-md)', padding: '10px 14px',
    display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', fontSize: 12.5,
  };

  if (edition) {
    return (
      <div style={{ ...cadre, display: 'grid', gap: 10 }}>
        <ChampsImpact idBase={`imp-${nc.documentId}`} f={f} onChange={setF} dossiers={dossiers} onDossiers={setDossiers} />
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          {clot
            ? 'Le dossier est clôturé : la correction sera inscrite au journal signé, avec l’ancienne réponse en regard.'
            : 'Le changement est inscrit au journal, avec l’ancienne réponse en regard.'}
        </span>
        <PiedActions
          taille="sm"
          annuler={{ onClick: () => {
            setF({ impactResultats: impact, nbResultatsConcernes: nc.nbResultatsConcernes || '', modaliteRappel: nc.modaliteRappel || '' });
            setDossiers(nc.dossiersConcernes || []);
            setEdition(false);
          } }}
          principale={{
            libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('imp'), disabled: estOccupe(),
            onClick: () => void run('imp', async () => {
              const ok = await smq(`ged/nc/${nc.documentId}/treatment`,
                { ...f, dossiersConcernes: dossiers.filter((x) => x.texte.trim()), ...(baseImpact ? { base: baseImpact } : {}) },
                'Impact sur les résultats mis à jour — modification tracée.');
              if (ok) setEdition(false);
            }),
          }}
        />
      </div>
    );
  }

  return (
    <div style={cadre}>
      <span>
        <strong style={{ color: 'var(--text-strong)' }}>Résultats patients concernés :</strong>{' '}
        <span title={meta.aide} style={{ fontWeight: 700, color: meta.fg, cursor: 'help' }}>{meta.label}</span>
      </span>
      {nc.nbResultatsConcernes && <span>Nombre : <strong>{nc.nbResultatsConcernes}</strong></span>}
      {(nc.dossiersConcernes?.length ?? 0) > 0 && (
        <span style={{ flex: '1 1 100%' }}>
          <strong style={{ color: 'var(--text-strong)' }}>
            {nc.dossiersConcernes?.length} dossier(s) concerné(s) :
          </strong>
          <ul style={{ margin: '4px 0 0', paddingInlineStart: 18, display: 'grid', gap: 2 }}>
            {nc.dossiersConcernes?.map((dc) => (
              <li key={dc.id} style={{ fontSize: 12.5 }}>
                {dc.texte}{dc.date ? ` — ${fmtDate(dc.date)}` : ''}
              </li>
            ))}
          </ul>
        </span>
      )}
      {nc.modaliteRappel && <span style={{ flex: '1 1 200px' }}>Traitement : {nc.modaliteRappel}</span>}
      {impact === 'oui' && !(nc.modaliteRappel || '').trim() && (
        <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>
          Des résultats sont touchés et rien n’indique ce qui a été fait.
        </span>
      )}
      {/* Cette alerte NE BOUGE PAS : une NC scellée sur une question restée sans
          réponse doit rester signalée, y compris — et surtout — maintenant que
          la réponse peut être donnée après coup. */}
      {clot && impact === 'inconnu' && (
        <span style={{ color: 'var(--red-600)', fontWeight: 700 }}>
          Clôturée sans avoir répondu à cette question.
        </span>
      )}
      <span style={{ flex: 1 }} />
      {/* Un SEUL bouton « Modifier » sur la fiche, en haut du constat (demande
          de la RQ le 14/08/2026). Il y en avait deux, à deux endroits et sous
          deux noms — « Corriger les rubriques » et « Je sais maintenant » —
          pour un même geste : ouvrir la fiche et changer ce qui doit l'être.
          Deux libellés différents laissent croire à deux fonctions
          différentes ; celui-ci est devenu un simple raccourci vers le même
          formulaire, et il ne s'affiche plus que si la question est restée
          sans réponse — le seul cas où le rappel sert à quelque chose. */}
      {/* Une réponse DÉJÀ donnée se change dans la correction de la fiche
          (étape Conséquences), où la question s'affiche comme la gravité. */}
      {peutModifier && impact === 'inconnu' && (
        <Bouton taille="sm" onClick={ouvrirImpact}
          title={clot ? 'Répondre après coup — la correction sera journalisée' : impact === 'inconnu' ? 'Préciser la réponse' : 'Changer la réponse — la modification sera journalisée'}>
          <I n="pen" s={12} />{impact === 'inconnu' ? 'Répondre' : 'Modifier'}
        </Bouton>
      )}
    </div>
  );
}

/** Une section du dossier en LECTURE (13/10/2026) — même découpage que la
 *  déclaration. Montrée à qui ne peut pas corriger (et sur un dossier clôturé) ;
 *  aux autres, les sections 1 à 3 sont le formulaire lui-même (`CorrigerFiche`). */
function SectionDossier({ id, numero, titre, children }: {
  id: string; numero: string; titre: string; children: React.ReactNode;
}) {
  return (
    <section id={id} aria-label={titre} style={{ scrollMarginTop: 12, border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '14px 16px', display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span aria-hidden style={{ display: 'grid', placeItems: 'center', width: 22, height: 22, borderRadius: '50%', fontSize: 12, fontWeight: 800,
          border: '2px solid var(--color-primary)', color: 'var(--color-primary-active)' }}>{numero}</span>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>{titre}</span>
      </div>
      {children}
    </section>
  );
}

/** Des rubriques en deux colonnes — seules les RENSEIGNÉES sont montrées. */
function Rubriques({ items }: { items: { label: string; valeur: React.ReactNode }[] }) {
  const remplies = items.filter((r) => r.valeur !== null && r.valeur !== undefined && String(r.valeur).trim() !== '');
  if (!remplies.length) return null;
  return (
    <dl className="ged-2col" style={{ margin: 0, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 20px' }}>
      {remplies.map((r) => (
        <div key={r.label} style={{ minWidth: 0 }}>
          <dt style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>{r.label}</dt>
          <dd style={{ margin: 0, fontSize: 13, color: 'var(--text-body)', lineHeight: 1.45, overflowWrap: 'anywhere' }}>{r.valeur}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Oui / non d'un booléen facultatif — vide s'il n'a pas été répondu. */
const ouiNon = (v: unknown) => (v === true ? 'oui' : v === false ? 'non' : '');

/**
 * Désignation du DOCUMENT LIÉ — par recherche dans le fonds, jamais de mémoire.
 *
 * Le champ était un `<input>` libre avec « Ex. MO-BACTERIO-008 » en indication :
 * il fallait connaître le code par cœur et le taper sans faute. Une référence
 * saisie de mémoire est fausse une fois sur combien ? Personne ne le sait, et
 * c'est le problème — un `docLie` qui ne correspond à aucun document du fonds
 * ne casse rien, ne prévient rien, et ne se voit qu'au moment où l'on cherche
 * le document en question.
 *
 * La saisie LIBRE reste possible, et c'est délibéré : une NC peut viser un
 * document qui n'est pas encore au fonds (une notice fournisseur, un imprimé
 * externe). Ce qui change, c'est qu'elle n'est plus le seul chemin — et que
 * l'écran DIT quand la référence saisie ne correspond à aucun document connu,
 * au lieu de l'accepter en silence.
 */
/**
 * LES DOCUMENTS D'UNE NC — plusieurs, et cliquables (RQ, 27/08/2026).
 *
 * ── Les deux symptômes, une seule cause ─────────────────────────────────────
 *
 * « Quand je rajoute un document à une NC, il s'affiche comme si ce n'était pas
 * pris en compte, et en plus je ne peux pas lier d'autres documents. »
 *
 * Les deux venaient du même choix de modèle : `docLie` est UN champ TEXTE. Il
 * ne portait donc qu'une seule référence, et la fiche la rendait en gris, sans
 * lien — d'où l'impression, exacte, que rien n'avait été pris en compte : la
 * saisie était bien enregistrée, mais elle ne menait nulle part et ne servait à
 * rien d'autre qu'à être relue.
 *
 * ── Ce qui remplace ────────────────────────────────────────────────────────
 *
 * Le mécanisme de liens qui existe déjà (`ged_lien`), celui de l'onglet
 * « Liens » — multiple par construction, choisi dans une liste, et cliquable.
 * On l'amène ICI, dans la description, là où la question se pose : on lit une
 * NC, on veut voir la procédure qu'elle met en cause.
 *
 * ⚠️ `docLie` N'EST PAS SUPPRIMÉ, ET C'EST DÉLIBÉRÉ. Le champ accepte une
 * référence qui n'est dans aucun fonds — une notice fournisseur, un imprimé
 * externe — et cette latitude avait été demandée. Il devient donc ce qu'il
 * aurait dû être : la RÉFÉRENCE EXTERNE, à côté des documents du fonds, et
 * plus le seul emplacement disponible pour les deux usages.
 */
function DocumentsLies({ nc }: { nc: GedNC }) {
  const { liens, docs, creerLien, retirerLien, openDoc, session, smq } = useGed();
  const [ajout, setAjout] = React.useState(false);
  const [choix, setChoix] = React.useState('');
  const estRQ = session.roles.includes('rq');
  /* Le document choisi à la déclaration (`docLie`) se retire comme les autres, par les
     mêmes personnes que les rubriques du constat, et jamais sur un dossier clôturé. */
  const peutRetirerDocLie = nc.statut !== 'cloturee'
    && (estRQ || nc.responsable === session.key || nc.detectePar === session.key);
  /* `base` (16/10/2026) : la référence que la personne a SOUS LES YEUX au moment
     de cliquer. Si un collègue l'a remplacée entre-temps par un autre document,
     le moteur refuse (409) au lieu d'effacer celle-là sans que personne le voie. */
  const retirerDocLie = () => smq(`ged/nc/${nc.documentId}/fiche`, { docLie: '', base: { docLie: nc.docLie || '' } },
    'Document retiré de la non-conformité.', `Retrait du document lié de ${nc.ref}`);

  const lies = liens.filter((l) => l.sourceType === 'nc' && l.sourceRef === nc.ref && l.cibleType === 'document');
  const dejaLa = new Set(lies.map((l) => l.cibleRef));
  /* `docLie` compte comme déjà là : le proposer une seconde fois dans la liste
     inviterait à créer un doublon de ce qui est sous les yeux. */
  if (nc.docLie) dejaLa.add(nc.docLie);
  const proposables = docs.filter((d) => d.code && !dejaLa.has(d.code));
  const externe = nc.docLie && !docs.some((d) => d.code === nc.docLie) ? nc.docLie : '';
  const duFonds = nc.docLie && docs.some((d) => d.code === nc.docLie) ? nc.docLie : '';

  const titreDe = (code: string) => docs.find((d) => d.code === code)?.titre || '';

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>
        Documents liés
      </div>
      {!lies.length && !nc.docLie && (
        <span style={{ fontSize: 12.5, color: 'var(--text-faint)' }}>Aucun document lié.</span>
      )}
      {duFonds && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChipDoc code={duFonds} titre={titreDe(duFonds)} onOuvrir={() => openDoc(duFonds)} />
          {peutRetirerDocLie && (
            <ConfirmButton label="" titre="Retirer ce document" icon="trash" variant="outline"
              question={`Retirer le document ${duFonds} de cette non-conformité ?`} confirmer="Retirer"
              onConfirm={retirerDocLie} />
          )}
        </div>
      )}
      {lies.map((l) => (
        <div key={l.documentId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ChipDoc code={l.cibleRef} titre={titreDe(l.cibleRef)} onOuvrir={() => openDoc(l.cibleRef)} />
          {estRQ && (
            <ConfirmButton label="" titre="Retirer ce lien" icon="trash" variant="outline"
              question={`Retirer le lien vers ${l.cibleRef} ?`} confirmer="Retirer"
              onConfirm={() => retirerLien(l.documentId)} />
          )}
        </div>
      ))}
      {externe && (
        /* Rendu SANS lien, et dit comme tel : une référence externe n'est pas
           un document du fonds, et la faire ressembler à un lien mort serait
           pire que de l'annoncer. */
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span><span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{externe}</span>
            {' '}— référence externe, hors du fonds documentaire</span>
          {peutRetirerDocLie && (
            <ConfirmButton label="" titre="Retirer cette référence" icon="trash" variant="outline"
              question={`Retirer la référence « ${externe} » de cette non-conformité ?`} confirmer="Retirer"
              onConfirm={retirerDocLie} />
          )}
        </span>
      )}

      {estRQ && (ajout ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Selecteur value={choix} onChange={setChoix}
            aria-label="Document à lier" style={{ ...inputStyle, maxWidth: 420 }}>
            <option value="">Choisir un document…</option>
            {proposables.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.titre}</option>)}
          </Selecteur>
          <PiedActions
            taille="sm"
            annuler={{ onClick: () => { setAjout(false); setChoix(''); } }}
            principale={{
              libelle: 'Lier', disabled: !choix,
              onClick: async () => {
                const ok = await creerLien({ sourceType: 'nc', sourceRef: nc.ref, cibleType: 'document', cibleRef: choix, typeLien: 'concerne' });
                if (ok) { setChoix(''); setAjout(false); }
              },
            }}
          />
        </div>
      ) : (
        <Bouton taille="sm" onClick={() => setAjout(true)}>
          <I n="plus" s={12} />Lier un document
        </Bouton>
      ))}
    </div>
  );
}

/** Une puce de document : le code en évidence, le titre à côté, et ça s'ouvre. */
function ChipDoc({ code, titre, onOuvrir }: { code: string; titre: string; onOuvrir: () => void }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
      <Bouton taille="sm" onClick={onOuvrir} title={`Ouvrir ${code}`}
        style={{ fontFamily: 'var(--font-mono)' }}>{code} →</Bouton>
      {titre && <span style={{ fontSize: 12.5, color: 'var(--text-body)' }}>{titre}</span>}
    </span>
  );
}

function ChoixDocument({ valeur, onChange, id }: {
  valeur: string; onChange: (v: string) => void; id: string;
}) {
  const { docs } = useGed();
  const [q, setQ] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const qq = q.trim().toLowerCase();
  const trouves = useMemo(() => {
    if (!qq) return [];
    return docs
      .filter((d) => `${d.code} ${d.titre}`.toLowerCase().includes(qq))
      .slice(0, 8);
  }, [docs, qq]);
  const connu = valeur ? docs.find((d) => d.code === valeur) : null;

  return (
    <div style={{ display: 'grid', gap: 5 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <input id={id} value={valeur} onChange={(e) => onChange(e.target.value)}
          placeholder="Aucun document lié"
          style={{ ...inputStyle, fontFamily: 'var(--font-mono)', flex: '1 1 160px', minWidth: 140 }} />
        <Bouton taille="sm" onClick={() => { setOuvert((v) => !v); setQ(''); }} aria-expanded={ouvert}>
          <I n="search" s={13} />{ouvert ? 'Fermer' : 'Rechercher'}
        </Bouton>
      </div>

      {/* Ce que porte la référence saisie, DIT plutôt que deviné : le titre du
          document quand il est connu, un avertissement quand il ne l'est pas. */}
      {valeur && connu && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>{connu.titre}</span>
      )}
      {valeur && !connu && (
        <span style={{ fontSize: 12.5, color: 'var(--amber-700)', lineHeight: 1.45 }}>
          Aucun document du fonds ne porte ce code. C’est possible (notice fournisseur, imprimé
          externe) — mais si c’est une faute de frappe, le lien ne mènera nulle part.
        </span>
      )}

      {ouvert && (
        <div style={{ display: 'grid', gap: 6, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 10 }}>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par code ou par titre…"
            aria-label="Rechercher un document du fonds" style={inputStyle} />
          {!qq && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
              {docs.length} document(s) au fonds. Tapez quelques lettres du code ou du titre.
            </span>
          )}
          {qq && trouves.length === 0 && (
            <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun document ne correspond à « {q.trim()} ».</span>
          )}
          {trouves.map((d) => (
            <button key={d.code} type="button"
              onClick={() => { onChange(d.code); setOuvert(false); setQ(''); }}
              className="ged-btn"
              style={{ display: 'block', width: '100%', textAlign: 'start', fontFamily: 'var(--font-sans)', background: 'var(--surface-card)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '6px 9px', cursor: 'pointer' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{d.code}</span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--text-body)' }}>{d.titre}</span>
            </button>
          ))}
          {valeur && (
            <Bouton taille="sm" onClick={() => { onChange(''); setOuvert(false); }} style={{ justifySelf: 'start' }}>Retirer le lien</Bouton>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Correction des rubriques du CONSTAT, même sur une NC ancienne ou clôturée.
 *
 * ── Ce qui était verrouillé ─────────────────────────────────────────────────
 *
 * Tout, dès l'enregistrement de la déclaration. L'intitulé, la description, la
 * source, la gravité, le processus — et, pour une panne, l'appareil, le code
 * cause et la durée d'arrêt — n'avaient aucun écran d'édition. Une faute de
 * saisie faite au poste restait au registre pour la durée de vie de l'appareil.
 * Seules l'analyse et la CAPA se reprenaient, et uniquement avant clôture.
 *
 * ── Ce qu'on ouvre, et à quel prix ──────────────────────────────────────────
 *
 * Ces rubriques, à tout moment — le serveur journalise chaque changement avec
 * l'ANCIENNE VALEUR en face de la nouvelle, exactement comme la fiche
 * d'équipement (lot C8) dont ce mécanisme est repris. Après clôture, le RQ
 * seul, et l'événement est SIGNÉ : corriger un dossier scellé engage.
 *
 * ── Ce qui reste fermé ──────────────────────────────────────────────────────
 *
 * Le statut (il ne descend jamais), la référence, les dates d'enregistrement et
 * le déclarant — les rendre modifiables permettrait d'antidater un dossier.
 */
/**
 * LES SECTIONS 1, 2, 3 D'UNE NC DÉCLARÉE = LE FORMULAIRE DE SAISIE (RQ,
 * 13/10/2026 : « pas de bouton Modifier, exactement comme dans la saisie »).
 * La fiche ne passe plus par une lecture puis un « Modifier » : à qui peut
 * corriger, elle EST le formulaire, mêmes onglets, mêmes champs, même ordre.
 * L'onglet affiché est piloté par la frise du dossier (`etape`).
 */
function CorrigerFiche({ nc, etape, onEtape, onDirty, peutPrendreEnCharge = false, onSuite }: {
  nc: GedNC;
  /** L'onglet affiché — partagé avec les repères 1 à 3 de la frise. */
  etape: EtapeNC; onEtape: (e: EtapeNC) => void;
  /** Une saisie non enregistrée — la fenêtre ne se ferme pas sans le dire. */
  onDirty?: (d: boolean) => void;
  /** Mener le dossier : désigner le responsable et l'échéance (route `treatment`). */
  peutPrendreEnCharge?: boolean;
  /** Après la dernière section : passer à l'étape Analyse. */
  onSuite?: () => void;
}) {
  const { smq, settings } = useGed();
  const { run, estOccupe } = useBusy();
  const [erreur, setErreur] = useState('');
  const procs = (settings?.processus?.length ? settings.processus : DEFAULT_PROCESSUS).filter((p) => p.active);
  // Les valeurs restent des CHAÎNES ici : le formulaire manipule des clés de
  // vocabulaire, la validation appartient au serveur. Typer `gravite` par
  // l'union du modèle interdirait au menu d'écrire dedans.
  /** Les valeurs de la fiche telles qu'en base — le point de départ, et ce
   *  qu'« Annuler les modifications » restaure. */
  const depart = React.useMemo((): Record<string, string> => ({
    /* ⚠️ « À qualifier » et non « Pré-analytique » pour une NC sans source : le
       repli d'avant AFFIRMAIT un classement que personne n'avait fait, et
       l'enregistrement l'écrivait. Même règle qu'à la déclaration. */
    titre: nc.titre || '', description: nc.description || '', source: nc.source || 'aqualifier', canal: nc.canal || '',
    processusDetection: nc.processusDetection || '',
    correspondantSignaleur: nc.correspondantSignaleur || '',
    // La nature EFFECTIVE : la sienne, sinon celle de son ancien secteur (`natureDe`).
    nature: natureDe(nc),
    processus: nc.processus || '', gravite: nc.gravite || 'mineure', ncType: nc.ncType || 'interne',
    docLie: nc.docLie || '', correction: nc.correction || '', dateDetection: nc.dateDetection || '',
    equipementCode: nc.equipementCode || '', causeEquip: nc.causeEquip || '', dureeArret: nc.dureeArret || '',
    correspondantCode: nc.correspondantCode || '',
  }), [nc]);
  const [f, setF] = useState<Record<string, string>>(depart);
  /* La fiche bouge sous le formulaire (impact enregistré, pièce ajoutée, autre
     poste) : on SUIT tant que rien n'est saisi — sinon le formulaire se dirait
     « modifié » sans qu'on ait touché à rien. Une saisie en cours, elle, est
     gardée. Recalé pendant le rendu, pas dans un effet (règle de lint du module). */
  const [base, setBase] = useState(depart);
  /* ⚠️ CE QUE LA SAISIE A LU (14/10/2026) — envoyé au moteur sous `base` pour
     la fusion à trois voies. Ce n'est PAS `base` ci-dessus : celle-là suit la
     fiche même pendant une saisie, et s'en servir ferait passer toute rubrique
     non touchée (restée à l'ancienne valeur dans `f`) pour une modification —
     donc l'écrirait par-dessus celle d'un collègue. `luFiche` ne bouge que
     quand le formulaire repart de la fiche (rien de saisi, ou « Annuler les
     modifications »). Valeurs BRUTES de la fiche, sans les replis d'affichage
     (« mineure », « interne », nature déduite) : le moteur compare à la base. */
  const instantaneFiche = (x: GedNC): Record<string, unknown> => {
    const brut = x as unknown as Record<string, unknown>;
    return Object.fromEntries(['titre', 'description', 'correction', 'docLie', 'dateDetection', 'equipementCode',
      'dureeArret', 'source', 'gravite', 'ncType', 'causeEquip', 'correspondantCode', 'processusDetection',
      'correspondantSignaleur', 'nature', 'canal', 'probleme', 'processus'].map((k) => [k, brut[k] ?? '']));
  };
  const [luFiche, setLuFiche] = useState(() => instantaneFiche(nc));
  if (JSON.stringify(depart) !== JSON.stringify(base)) {
    setBase(depart);
    if (JSON.stringify(f) === JSON.stringify(base)) { setF(depart); setLuFiche(instantaneFiche(nc)); }
  }
  /* LE MÊME FORMULAIRE QUE LA DÉCLARATION (RQ, 13/10/2026) : deux étapes, mêmes
     champs dans le même ordre, même liste « Quel problème ? » liée au processus.
     On y retrouve ce qu'on a rempli — au lieu d'une autre présentation à
     réapprendre. Les rubriques qui se traitent AILLEURS dans la fiche (le
     responsable, l'échéance, l'impact sur les résultats, les dossiers et les
     pièces) n'y figurent pas : elles ont leur propre geste, et leur propre trace. */
  const setEtape = onEtape;
  /* RESPONSABLE ET ÉCHÉANCE, en « Prise en charge » (13/10/2026) — leur seul
     endroit. Ils passent par la route du TRAITEMENT, qui a ses propres droits
     (le RQ, le CQ, le responsable) ; les autres rubriques par celle de la fiche,
     ouverte aussi au déclarant. */
  const priseDepart = React.useMemo(() => ({ responsable: nc.responsable || '', echeanceTraitement: nc.echeanceTraitement || '' }), [nc]);
  const [prise, setPrise] = useState(priseDepart);
  const [basePrise, setBasePrise] = useState(priseDepart);
  // Même distinction que `luFiche` (14/10/2026) : ce que la saisie a lu.
  const [luPrise, setLuPrise] = useState(priseDepart);
  if (JSON.stringify(priseDepart) !== JSON.stringify(basePrise)) {
    setBasePrise(priseDepart);
    if (JSON.stringify(prise) === JSON.stringify(basePrise)) { setPrise(priseDepart); setLuPrise(priseDepart); }
  }
  /* L'IMPACT SUR LES RÉSULTATS, en « Conséquences », AFFICHÉ COMME LA GRAVITÉ
     (RQ, 03/11/2026) : il fallait cliquer « Modifier » sur un bandeau pour
     voir les boutons, alors que la gravité, juste dessous, se choisissait
     d'emblée. Il part par la route du TRAITEMENT (mêmes droits que la prise en
     charge), avec ce que la saisie a lu sous `base` (fusion à trois voies). */
  const impactDepart = React.useMemo(() => ({
    impactResultats: nc.impactResultats || 'inconnu', nbResultatsConcernes: nc.nbResultatsConcernes || '', modaliteRappel: nc.modaliteRappel || '',
  }), [nc]);
  const dossiersDepart = React.useMemo(() => nc.dossiersConcernes || [], [nc]);
  const [impact, setImpact] = useState(impactDepart);
  const [dossiers, setDossiers] = useState<LigneDossier[]>(dossiersDepart);
  const [baseImpact, setBaseImpact] = useState({ impactDepart, dossiersDepart });
  const luImpactDe = (i: typeof impactDepart, d: LigneDossier[]) => ({ ...i, dossiersConcernes: d.filter((x) => x.texte.trim()) });
  const [luImpact, setLuImpact] = useState(() => luImpactDe(impactDepart, dossiersDepart));
  if (JSON.stringify({ impactDepart, dossiersDepart }) !== JSON.stringify(baseImpact)) {
    setBaseImpact({ impactDepart, dossiersDepart });
    if (JSON.stringify({ impactDepart: impact, dossiersDepart: dossiers }) === JSON.stringify(baseImpact)) {
      setImpact(impactDepart); setDossiers(dossiersDepart); setLuImpact(luImpactDe(impactDepart, dossiersDepart));
    }
  }
  const impactModifie = peutPrendreEnCharge && (JSON.stringify(impact) !== JSON.stringify(impactDepart)
    || JSON.stringify(dossiers.filter((x) => x.texte.trim())) !== JSON.stringify(dossiersDepart.filter((x) => x.texte.trim())));
  const { settings: reglages } = useGed();
  /* Le problème déjà choisi à la déclaration (ou à une correction précédente). */
  const [probleme, setProbleme] = useState(nc.probleme || '');
  /* LES OBJETS DÉJÀ RELIÉS — relus dans les liens de la NC (et l'appareil d'une
     panne dans `equipementCode`). Un objet nouvellement choisi est RELIÉ à
     l'enregistrement ; un lien se retire depuis le bloc « Liens ». */
  const { liens, creerLien } = useGed();
  const objetsDepart = React.useMemo(() => {
    const o: Record<string, string> = {};
    for (const l of liens) {
      if (l.cibleType === 'nc' && l.cibleRef === nc.ref && !o[l.sourceType]) o[l.sourceType] = l.sourceRef;
      else if (l.sourceType === 'nc' && l.sourceRef === nc.ref && !o[l.cibleType]) o[l.cibleType] = l.cibleRef;
    }
    if (nc.equipementCode && !o.equipement) o.equipement = nc.equipementCode;
    return o;
  }, [liens, nc.ref, nc.equipementCode]);
  const [objets, setObjets] = useState<Record<string, string>>(objetsDepart);
  // Les liens bougent sous le formulaire (lien ajouté ici ou ailleurs) : on suit tant que rien n'est saisi.
  const [baseObjets, setBaseObjets] = useState(objetsDepart);
  if (JSON.stringify(objetsDepart) !== JSON.stringify(baseObjets)) {
    setBaseObjets(objetsDepart);
    if (JSON.stringify(objets) === JSON.stringify(baseObjets)) setObjets(objetsDepart);
  }
  const modifie = JSON.stringify(f) !== JSON.stringify(depart) || JSON.stringify(objets) !== JSON.stringify(objetsDepart)
    || (peutPrendreEnCharge && JSON.stringify(prise) !== JSON.stringify(priseDepart))
    || impactModifie
    || probleme !== (nc.probleme || '');
  useEffect(() => { onDirty?.(modifie); }, [modifie, onDirty]);
  const annulerModifications = () => { setF(depart); setLuFiche(instantaneFiche(nc)); setObjets(objetsDepart); setPrise(priseDepart); setLuPrise(priseDepart); setImpact(impactDepart); setDossiers(dossiersDepart); setLuImpact(luImpactDe(impactDepart, dossiersDepart)); setProbleme(nc.probleme || ''); setErreur(''); };
  const criteresPreana = useCriteresPreana(true);
  const { groupes, problemes, natureDu } = useListeProblemesNature(f.nature, f.canal, criteresPreana);
  /* Le secteur se DÉDUIT de la nature et du canal (`secteurDeNature`). Celui d'une
     fiche ancienne n'est recalculé QUE si l'un des deux change : on ne réécrit
     pas l'historique d'une NC parce qu'on corrige son intitulé. */
  const secteurCalcule = f.nature ? secteurDeNature(f.nature, f.canal) : 'aqualifier';
  const secteur = (f.nature === depart.nature && f.canal === depart.canal) ? (nc.source || secteurCalcule) : secteurCalcule;
  const classe = !!f.nature && f.nature !== 'aqualifier';
  const nat = natureMeta(f.nature);
  /* Le processus de DÉTECTION : le sien s'il est posé, sinon CALCULÉ. */
  const processusDetection = f.processusDetection || processusDetectionDe(f.canal, f.processus, procs);
  const choisirProcessus = (v: string) => setF((x) => ({ ...x, processus: v }));
  const choisirNature = (v: string) => {
    if (v !== f.nature) setProbleme('');
    // Un processus vide se remplit d'après la nature ; un processus déjà posé n'est pas écrasé.
    setF((x) => ({ ...x, nature: v, ...(!x.processus ? { processus: processusDeNature(v, procs) } : {}) }));
  };
  const choisirCanal = (v: string) => {
    const releve = NC_CANAUX.find((c) => c.key === v)?.releve;
    setF((x) => ({ ...x, canal: v, ...(releve ? { ncType: releve } : {}), ...(v === 'reclamation' ? { processusDetection: processusSMQ(procs) } : {}) }));
  };

  /* Choisir un problème écrit l'INTITULÉ (s'il est vide ou déjà celui de la liste),
     et rien d'autre. ⚠️ La DESCRIPTION n'est plus touchée (RQ, 13/10/2026 :
     « laisser vide le champ Description ») : elle y recevait une ligne
     « Problème : … » / « Situation type : … » qui ne disait rien de plus que
     l'intitulé — et le code choisi est désormais enregistré à part (`probleme`). */
  const choisirProbleme = (v: string) => {
    const ancien = problemes.find((c) => c.code === probleme);
    const c = problemes.find((x) => x.code === v);
    setProbleme(v);
    // Choisi dans la liste COMPLÈTE : le problème dit sa nature.
    const n = !f.nature || f.nature === 'aqualifier' ? natureDu.get(v) : undefined;
    if (n) choisirNature(n);
    setF((prev) => {
      const titreAuto = !prev.titre.trim() || (!!ancien && prev.titre === ancien.libelle);
      return { ...prev, titre: titreAuto ? (c ? c.libelle : '') : prev.titre };
    });
  };

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <RequiredHint />
      </div>
      {/* PAS DE SECONDE LIGNE D'ONGLETS (RQ, 13/10/2026) : sur la fiche, ce sont
          les repères 1 à 3 de la frise qui choisissent l'onglet (`etape`). */}

      {/* 1 — CE QUI S'EST PASSÉ : même ordre qu'à la déclaration. */}
      {etape === 'fait' && (
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          {/* L'intitulé en premier, comme à la déclaration (14/10/2026). */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl} htmlFor={`cf-titre-${nc.documentId}`}>Intitulé — le problème, en quelques mots <span style={{ color: 'var(--red-600)' }}>*</span></label>
            <input id={`cf-titre-${nc.documentId}`} value={f.titre}
              onChange={(e) => { setF({ ...f, titre: e.target.value }); if (erreur) setErreur(''); }}
              aria-invalid={!!erreur} style={{ ...inputStyle, borderColor: erreur ? 'var(--red-600)' : undefined }} />
            <FieldError>{erreur}</FieldError>
          </div>
          {/* Même ordre qu'à la déclaration : la nature, puis comment l'écart a été
              détecté, puis l'objet relié. Les processus sont en « Prise en charge ». */}
          <ChoixNature id={`cf-nature-${nc.documentId}`} valeur={f.nature} onChange={choisirNature} />
          <ChoixDetection id={`cf-canal-${nc.documentId}`} valeur={f.canal} onChange={choisirCanal} />
          {(f.canal === 'correspondant' || f.canal === 'reclamation' || !!f.correspondantSignaleur) && (
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl} htmlFor={`cf-signaleur-${nc.documentId}`}>Quel correspondant l’a signalé ?</label>
              <ChoixCorrespondant id={`cf-signaleur-${nc.documentId}`} valeur={f.correspondantSignaleur}
                onChange={(v) => setF({ ...f, correspondantSignaleur: v })}
                aide="Celui qui a prévenu le laboratoire (ou qui a réclamé). Distinct du correspondant à l’origine de l’erreur." />
            </div>
          )}
          <ChoixObjets idBase={`cf-${nc.documentId}`} nature={f.nature} objets={objets} onChange={(t, v) => setObjets((o) => ({ ...o, [t]: v }))} />
          <ChoixProbleme id={`cf-probleme-${nc.documentId}`} groupes={groupes} valeur={probleme} onChange={choisirProbleme} />
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl} htmlFor={`cf-desc-${nc.documentId}`}>Description — ce qui s’est passé</label>
            <textarea id={`cf-desc-${nc.documentId}`} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3}
              style={{ ...inputStyle, height: 'auto', padding: '10px 12px', resize: 'vertical' }} />
          </div>
          <div><label style={lbl} htmlFor={`cf-detection-${nc.documentId}`}>Date de détection</label>
            <input id={`cf-detection-${nc.documentId}`} type="date" value={jourIso(f.dateDetection)} max={aujourdhuiIso()}
              onChange={(e) => setF({ ...f, dateDetection: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
              Quand l’écart a été CONSTATÉ, si ce n’est pas le jour de la déclaration.
            </div></div>
          {/* La correction immédiate ne se modifie QU'ICI (13/10/2026) : l'onglet
              Traitement l'affiche, il ne la réécrit plus. */}
          <div><label style={lbl} htmlFor={`cf-corr-${nc.documentId}`}>Correction immédiate</label>
            <input id={`cf-corr-${nc.documentId}`} value={f.correction} onChange={(e) => setF({ ...f, correction: e.target.value })} style={inputStyle} />
            <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>
              Ce qui a été fait tout de suite : refaire le tube, rappeler le résultat, isoler le lot.
            </div></div>
          {/* Les pièces, à l'étape 1 comme à la saisie. Elles s'ajoutent et se
              retirent sur-le-champ (chacune est tracée), sans attendre « Enregistrer ». */}
          <div style={{ gridColumn: '1 / -1' }}><PiecesNC nc={nc} modifiable /></div>
        </div>
      )}

      {/* 2 — CONSÉQUENCES : l'impact (avec ses dossiers et la suite donnée)
          AVANT la gravité, comme à la déclaration — la gravité en découle. */}
      {etape === 'consequences' && (
        <div style={{ display: 'grid', gap: 14 }}>
          {/* Mêmes droits que la route `treatment` : le registre ou le
              responsable. Le seul déclarant, que le serveur refuserait, lit. */}
          {peutPrendreEnCharge ? (
            <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: 12, display: 'grid', gap: 8 }}>
              <ChampsImpact idBase={`cf-imp-${nc.documentId}`} f={impact} onChange={setImpact} dossiers={dossiers} onDossiers={setDossiers} />
              <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                Un changement de réponse est inscrit au journal, avec l’ancienne en regard.
              </span>
            </div>
          ) : <BandeauImpact nc={nc} peutModifier={false} />}
          <div><div style={ENTETE_I}><label style={{ ...lbl, marginBottom: 0 }} htmlFor={`cf-gravite-${nc.documentId}`}>Gravité *</label><PointDeRegle ancre="nc.gravite" /></div>
            <div id={`cf-gravite-${nc.documentId}`} style={{ display: 'flex', gap: 6 }}>
              {NC_GRAVITES.map((g) => {
                const on = f.gravite === g.key; const m = graviteMeta(g.key);
                return <button key={g.key} onClick={() => setF({ ...f, gravite: g.key })} aria-pressed={on}
                  style={{ fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 700, borderRadius: 'var(--radius-pill)', padding: '7px 14px', cursor: 'pointer', border: `1.5px solid ${on ? m.fg : 'var(--border-default)'}`, background: on ? m.bg : 'var(--surface-card)', color: on ? m.fg : 'var(--text-muted)' }}>{g.label}</button>;
              })}
            </div>
            <ExplicationNiveaux impact={impact.impactResultats} gravite={f.gravite} onAppliquer={(g) => setF({ ...f, gravite: g })} /></div>
          {/* « Qui est à l'origine » : proposé pour les mêmes secteurs qu'à la
              déclaration, et toujours si la fiche en porte déjà un. */}
          {(nat?.correspondant || f.nature === 'aqualifier' || f.canal === 'correspondant' || !!f.correspondantCode) && (
            <div>
              <label style={lbl} htmlFor={`cf-cor-${nc.documentId}`}>{QUESTION_CORRESPONDANT}</label>
              <ChoixCorrespondant id={`cf-cor-${nc.documentId}`} valeur={f.correspondantCode}
                onChange={(v) => setF({ ...f, correspondantCode: v })}
                aide={AIDE_CORRESPONDANT} />
            </div>
          )}
          {(f.nature === 'equipement' || !!f.causeEquip || !!f.dureeArret) && (
            <>
              <RubriquesPanne idBase={`cf-${nc.documentId}`} sansCode
                valeurs={{ equipementCode: f.equipementCode, causeEquip: f.causeEquip, dureeArret: f.dureeArret }}
                onChange={(champ, v) => setF({ ...f, [champ]: v })} />
              <LienHorsService code={objets.equipement || ''} />
            </>
          )}
        </div>
      )}

      {/* 3 — PRISE EN CHARGE. Le responsable et l'échéance se règlent ENSEMBLE
          dans l'onglet Analyse : un seul endroit pour chacun. */}
      {etape === 'priseEnCharge' && (
        <div className="ged-2col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <ChoixProcessus id={`cf-processus-${nc.documentId}`} valeur={f.processus} onChange={choisirProcessus} procs={procs} requis={classe}
            aide={classe ? undefined : 'Avec « Je ne sais pas », le responsable qualité le choisira.'} />
          <ChoixProcessus id={`cf-processus-detection-${nc.documentId}`} libelle="Processus à l’origine de la détection"
            valeur={processusDetection} verrouille={f.canal === 'reclamation'} procs={procs}
            onChange={(v) => setF({ ...f, processusDetection: v })}
            aide={f.canal === 'reclamation' ? 'Une réclamation est détectée par le SMQ.' : (f.processusDetection ? undefined : 'Calculé d’après la façon dont l’écart a été détecté.')} />
          <div><label style={lbl} htmlFor={`cf-doc-${nc.documentId}`}>Document ou référence externe (facultatif)</label>
            <ChoixDocument id={`cf-doc-${nc.documentId}`} valeur={f.docLie} onChange={(v) => setF({ ...f, docLie: v })} />
            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>
              Un seul ici. Les autres documents se lient dans le bloc « Documents liés » de la fiche.
            </p></div>
          {peutPrendreEnCharge ? (
            <>
              <div><label style={lbl} htmlFor={`cf-resp-${nc.documentId}`}>Responsable du traitement{classe ? ' *' : ''}</label>
                <Selecteur id={`cf-resp-${nc.documentId}`} value={prise.responsable} onChange={(v) => setPrise({ ...prise, responsable: v })} style={inputStyle}>
                  <option value="">—</option>
                  {(reglages?.gedUsers || []).map((u) => <option key={u.id} value={u.id}>{u.nom}</option>)}
                </Selecteur></div>
              <div><label style={lbl} htmlFor={`cf-ech-${nc.documentId}`}>Échéance de traitement</label>
                <input id={`cf-ech-${nc.documentId}`} type="date" value={jourIso(prise.echeanceTraitement)}
                  onChange={(e) => setPrise({ ...prise, echeanceTraitement: e.target.value })} style={inputStyle} /></div>
            </>
          ) : (
            <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
              Le responsable du traitement et l’échéance se règlent par qui mène le dossier.
            </p>
          )}
        </div>
      )}

      <div style={PIED_COLLE}>
      <PiedActions
        taille="sm"
        secondaires={[
          ...navigationEtapes(etape, setEtape),
          ...(etape === 'priseEnCharge' && onSuite ? [{ libelle: 'Suivant : Analyse →', onClick: onSuite }] : []),
        ]}
        annuler={{ libelle: 'Annuler les modifications', onClick: annulerModifications, disabled: !modifie || estOccupe() }}
        principale={{
          libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('fiche'), disabled: !modifie || estOccupe(),
          onClick: () => void run('fiche', async () => {
            if (!f.titre.trim()) { setEtape('fait'); setErreur('L’intitulé est obligatoire.'); return; }
            setErreur('');
            const corpsFiche: Record<string, unknown> = {
              ...f, source: secteur, processusDetection,
              ...(f.nature === 'equipement' ? { equipementCode: objets.equipement || f.equipementCode } : {}),
              probleme: probleme && probleme !== '__autre' ? probleme : '',
            };
            const ok = await smq(`ged/nc/${nc.documentId}/fiche`, { ...corpsFiche, base: luFiche },
              'Rubriques corrigées — modification tracée au journal.');
            if (!ok) return;
            // Le formulaire reste ouvert : ce qu'on vient d'écrire devient la
            // lecture de départ, sinon le prochain enregistrement prendrait
            // notre propre écriture pour celle d'un autre (409 à tort).
            setLuFiche((x) => ({ ...x, ...corpsFiche }));
            /* Les objets NOUVELLEMENT choisis sont reliés (le lien est tracé sur les deux fiches). */
            for (const [type, ref] of Object.entries(objets)) {
              if (!ref || ref === objetsDepart[type]) continue;
              await creerLien({ sourceType: type, sourceRef: ref, cibleType: 'nc', cibleRef: nc.ref,
                typeLien: type === 'equipement' && f.nature === 'equipement' ? 'cause' : 'concerne' });
            }
            const priseChangee = peutPrendreEnCharge && (prise.responsable !== (nc.responsable || '')
              || prise.echeanceTraitement !== (nc.echeanceTraitement || ''));
            if (priseChangee && !(await smq(`ged/nc/${nc.documentId}/treatment`, { ...prise, base: luPrise },
              'Prise en charge enregistrée — modification tracée au journal.'))) return;
            if (priseChangee) setLuPrise(prise);
            if (impactModifie) {
              const corpsImpact = luImpactDe(impact, dossiers);
              if (!(await smq(`ged/nc/${nc.documentId}/treatment`, { ...corpsImpact, base: luImpact },
                'Impact sur les résultats mis à jour — modification tracée au journal.'))) return;
              setLuImpact(corpsImpact);
            }
          }),
        }}
      />
      </div>
    </div>
  );
}

/** Onglets du dossier. L'ordre suit l'instruction : on lit le fait, on analyse,
 *  on agit, on rattache, on vérifie la trace. */
type OngletNC = OngletParcours;
/* Les valeurs ADMISES dans l'adresse — une adresse tronquée ne doit pas ouvrir
   un onglet vide. */
const ONGLETS_NC: readonly OngletNC[] = ['constat', 'analyse', 'traitement', 'cloture', 'liens'];

/**
 * Le DOSSIER d'une non-conformité — exporté sous le nom `NCDossier`.
 *
 * Exporté parce que la fiche s'ouvre désormais aussi EN PANNEAU par-dessus
 * l'objet qui la cite (un audit, une action, une réclamation), au lieu de
 * changer d'écran. Même raison qu'`ActionDossier` : une seconde fiche de NC
 * recopiée aurait divergé de celle-ci au premier correctif.
 *
 * `enPanneau` ne change rien au contenu : il retire le cadre de carte, que le
 * panneau fournit déjà, et il neutralise les liens qui feraient QUITTER
 * l'écran — le panneau a son propre bouton « Ouvrir en pleine page ».
 */
export function NCDossier({ n, enPanneau = false }: { n: GedNC; enPanneau?: boolean }) {
  return <NCDetail nc={n} enPanneau={enPanneau} />;
}


/** Déclôture d'une non-conformité — réservée au RQ et au CQ, motivée, signée.
 *
 *  ⚠️ OUVERTE AU COORDINATEUR QUALITÉ LE 08/10/2026 (`peutDecloturerNC`). Elle
 *  ne l'est PAS au responsable du dossier, qui est pourtant celui qui le mène :
 *  mener un dossier n'est pas défaire ce qui a été scellé.
 *
 *  Jumelle EXACTE de la déclôture des réclamations (14/08/2026), et c'est
 *  voulu : deux dossiers scellés qui se rouvriraient par deux gestes différents
 *  donneraient deux journaux qu'un évaluateur ne pourrait pas comparer.
 *
 *  La NC revient à « traitement » et non à « déclarée » : l'analyse a bien eu
 *  lieu, revenir en deçà effacerait un fait. Ce qu'on rouvre, c'est la suite. */
function DeclotureNC({ nc }: { nc: GedNC }) {
  const { smq } = useGed();
  const [ouvert, setOuvert] = useState(false);
  const [motif, setMotif] = useState('');
  const { run, estOccupe } = useBusy();
  // 20, comme `MOTIF_DEROGATION_MIN` au serveur : un minimum plus bas ici
  // laisserait le refus arriver APRÈS le clic, sur un dossier scellé.
  const MINI = 20;
  const assez = motif.trim().length >= MINI;

  return (
    <>
      <Bouton taille="sm" onClick={() => setOuvert(true)}
        title="Rouvrir ce dossier scellé. Réservé au responsable qualité et au coordinateur qualité, motif obligatoire, événement signé au journal.">
        <I n="pen" s={12} />Déclôturer pour corriger
      </Bouton>
      {ouvert && (
        <Modale
          titre={`Déclôturer ${nc.ref}`}
          sousTitre="Le dossier revient à « traitement ». L’événement est signé au journal."
          onClose={() => setOuvert(false)}
          largeur={560}
          pied={(
            <PiedActions
              style={{ width: '100%' }}
              annuler={{ onClick: () => setOuvert(false) }}
              principale={{
                libelle: 'Déclôturer', libelleChargement: 'Déclôture…', chargement: estOccupe('decl'), disabled: !assez || estOccupe(),
                onClick: () => void run('decl', async () => {
                  const ok = await smq(`ged/nc/${nc.documentId}/status`,
                    { statut: 'traitement', motifDerogation: motif.trim() },
                    'Non-conformité déclôturée — événement signé.');
                  if (ok) { setOuvert(false); setMotif(''); }
                }),
              }}
            />
          )}>
          <div style={{ display: 'grid', gap: 10 }}>
            <label style={lbl} htmlFor="decl-nc-motif">Motif de la déclôture</label>
            <textarea id="decl-nc-motif" rows={3} value={motif} onChange={(e) => setMotif(e.target.value)}
              placeholder="Ce qui justifie de rouvrir un dossier scellé — l’écart s’est reproduit, l’action s’est révélée insuffisante, une rubrique est fausse…"
              style={{ ...inputStyle, height: 'auto', resize: 'vertical' }} />
            {/* Un bouton grisé sans phrase se lit comme une panne : le refus est
                NOMMÉ, avec ce qui manque exactement. */}
            <FieldError>
              {assez ? '' : `Motif trop court : ${MINI} caractères au minimum (${motif.trim().length} saisi${motif.trim().length > 1 ? 's' : ''}).`}
            </FieldError>
          </div>
        </Modale>
      )}
    </>
  );
}

function NCDetail({ nc, enModale = false, enPanneau = false, onDirty, onPanneau }: {
  nc: GedNC; enModale?: boolean;
  /** Ouvert en panneau superposé : cadre retiré (comme `enModale`) ET liens
   *  sortants neutralisés, car on ne quitte pas une superposition. */
  enPanneau?: boolean;
  onDirty?: (d: boolean) => void;
  /** Un panneau est ouvert PAR-DESSUS ce dossier — la fenêtre qui le porte doit
   *  alors relâcher la touche Échap. Voir `FenetreNC`. */
  onPanneau?: (ouvert: boolean) => void;
}) {
  const { smq, settings, session, events, loadJournal, signalerLecture, actions, liens, ouvrirPanneau } = useGed();
  const nomsTiers = useNomsContacts([nc.correspondantCode || '', nc.correspondantSignaleur || ''].filter(Boolean));
  /* `correction` n'est plus dans ce brouillon (13/10/2026) : la correction
     immédiate se modifie dans le constat seul. `echeanceTraitement` y entre :
     elle se règle avec le responsable, dans l'onglet Analyse. */
  /* Responsable et échéance ne sont plus dans ce brouillon (13/10/2026) : ils se
     règlent en « Prise en charge », section 3 du constat — leur seul endroit. */
  type Traitement = { analyse: AnalyseCapa; actionCorrective: string };
  const traitementDe = (n: GedNC): Traitement => ({
    analyse: depuisNc(n), actionCorrective: n.actionCorrective,
  });
  const [w, setW] = useState<Traitement>(() => traitementDe(nc));
  const [edition, setEdition] = useState(false);
  /* L'onglet vit dans l'ADRESSE (relevé P0.8) : on envoie « l'analyse de
     BIO24-NC-2026-014 », pas « la NC, va voir l'onglet Analyse ». */
  const [ongletAdresse, setOnglet] = useOngletAdresse<OngletNC>(nc.statut === 'cloturee' ? 'constat' : etapeCourante(nc.statut), ONGLETS_NC);
  /* « liens » n'est plus un onglet (13/10/2026) : c'est un bloc du constat
     (27/10/2026 ; à la clôture auparavant). L'adresse reste VALIDE — un lien
     publié est un contrat — et ouvre le constat, descendu jusqu'au bloc. */
  const onglet: Exclude<OngletNC, 'liens'> = ongletAdresse === 'liens' ? 'constat' : ongletAdresse;
  useEffect(() => {
    if (ongletAdresse !== 'liens') return;
    requestAnimationFrame(() => document.getElementById('nc-liens')?.scrollIntoView({ block: 'start' }));
  }, [ongletAdresse]);
  // Action tout juste créée, à formuler sans quitter la fiche NC.
  const [acARemplir, setAcARemplir] = useState<string | null>(null);
  // Correction des rubriques du constat (N2-1), ouverte à la demande.
  /** LA SECTION DU CONSTAT AFFICHÉE (RQ, 13/10/2026 : « 1, 2 et 3 dans des
   *  onglets, comme ça on ne scrolle pas »). Les repères 1 à 3 de la frise en
   *  sont les onglets. */
  const [sectionConstat, setSectionConstat] = useState<EtapeNC>('fait');
  /** L'étape du constat en cours de correction, ou `null` (lecture). */
  /** Une saisie en cours dans le formulaire des sections 1 à 3. */
  const [ficheModifiee, setFicheModifiee] = useState(false);
  // Reprise de l'analyse / CAPA sur un dossier DÉJÀ CLÔTURÉ (N2-1) — RQ seul.
  const [corrigeAnalyse, setCorrigeAnalyse] = useState(false);
  // Action ouverte PAR-DESSUS ce dossier (N2-4) — voir `FenetreAction`.
  const [acOuverte, setAcOuverte] = useState<string | null>(null);
  const { run, estOccupe } = useBusy();
  useEffect(() => { void loadJournal(nc.ref); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [nc.documentId, nc.statut]);
  /* Ouvrir le dossier VAUT prise en charge, pour le RQ et son suppléant : le
     moteur décide (`ged/nc/:id/lu`), l'écran ne fait que signaler l'ouverture.
     ⚠️ Dépendance sur `documentId` SEUL, pas sur `statut` : le statut change
     précisément quand la bascule réussit, et l'inclure relancerait l'appel
     juste après — une seconde écriture pour rien, et une ligne de journal de
     plus à chaque consultation si jamais la garde d'état venait à céder. */
  useEffect(() => { void signalerLecture('nc', nc.documentId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [nc.documentId]);

  const estRQ = session.roles.includes('rq');
  /* ⚠️ `tientLeRegistreNC` ET NON `estRQ` (08/10/2026) : le coordinateur
     qualité mène le cycle au même titre que le RQ. Laisser `estRQ` ici aurait
     grisé la clôture pour Asma alors que le serveur l'accepte — un bouton
     désactivé qui ne dit pas pourquoi se lit comme une panne. */
  const canManage = tientLeRegistreNC(session.roles) || nc.responsable === session.key;
  /**
   * Qui peut CORRIGER les rubriques du constat.
   *
   * Plus large que `canManage`, et volontairement : c'est le technicien de
   * paillasse qui saisit une panne au poste, et une NC déclarée n'a pas encore
   * de responsable. Lui refuser la correction de sa propre faute de frappe
   * garantissait un registre faux. Après clôture, le RQ seul — le serveur
   * applique la même règle et fait foi.
   */
  /* CLÔTURÉE = FERMÉE (17/08/2026). Le RQ pouvait encore corriger sous le
     sceau ; le serveur le refuse maintenant, et l'écran doit dire la même
     chose — une commande offerte puis refusée après le clic est pire que pas
     de commande. On déclôture, on corrige, on reclôture. */
  const peutCorriger = nc.statut !== 'cloturee'
    && (estRQ || nc.responsable === session.key || nc.detectePar === session.key);
  // Même critère que le serveur (origine + origineRef) : un écart ferait
  // diverger le blocage de clôture affiché et celui réellement appliqué.
  const linked = actions.filter((a) => a.origine === 'nc' && a.origineRef === nc.ref);
  /**
   * N2-4 — l'action s'ouvre PAR-DESSUS le dossier, qui reste en place dessous.
   *
   * Le clic naviguait vers le registre des actions : on perdait la NC qu'on
   * était en train d'instruire, et il fallait la retrouver pour reprendre. Le
   * panneau est réversible et ne REMPLACE pas le dossier — la fiche de NC reste
   * une vue à part entière, avec son URL, partageable et imprimable ; c'est un
   * choix du projet qu'un empilement de modales aurait défait.
   */
  /*
   * ⚠️ Quand la NC est ELLE-MÊME dans le panneau générique (12/08/2026), sa
   * `FenetreAction` locale — couche 150 — se rendrait SOUS ce panneau, en
   * couche 300 : le clic paraîtrait sans effet. L'action passe alors par la
   * pile du panneau, qui empile proprement et offre son propre « Retour ».
   */
  const ouvrirAction = (a: GedAction) => {
    if (enPanneau) ouvrirPanneau({ type: 'action', valeur: a.documentId, origine: nc.ref });
    else setAcOuverte(a.documentId);
  };
  // Une analyse déjà saisie s'affiche en lecture ; tant qu'il n'y a rien, le
  // formulaire est ouvert d'emblée — on ne demande pas de cliquer « Modifier »
  // sur une fiche vide.
  const aAnalyse = !!(nc.causes || '').trim();
  const aTraitement = !!(nc.actionCorrective || '').trim();
  const lectureAnalyse = aAnalyse && !edition;
  const lectureTraitement = aTraitement && !edition;
  /** Instruction ORDINAIRE du dossier : le responsable, tant qu'il est ouvert. */
  const instruit = canManage && nc.statut !== 'cloturee';
  /* ⚠️ CE QUE LA NC FAIT PROPOSER — et qui ne s'écrit nulle part (11/10/2026).
     Calculé à l'affichage, du module miroir surveillé par
     `backend/tests/proposition-nc-parite.test.js` : le stocker aurait créé une
     troisième vérité, qui se périmerait au premier changement de gravité. */
  const propositionAction = React.useMemo(() => {
    const p = actionProposee(nc.gravite, nc.impactResultats);
    return p.proposer ? p : null;
  }, [nc.gravite, nc.impactResultats]);
  const journal = events[nc.ref] || [];
  // Compteur d'onglet : les liens se lisent dans les DEUX sens, donc on compte
  // aussi ceux qui pointent vers cette NC (cf. composant Liens).
  const nbLiens = liens.filter((l) => (l.sourceType === 'nc' && l.sourceRef === nc.ref) || (l.cibleType === 'nc' && l.cibleRef === nc.ref)).length;

  /* Le brouillon `w` suit la NC tant que personne n'y a tapé : une correction des
     rubriques du constat (qui touche `correction`) ou une prise en charge ne doit
     pas laisser un brouillon périmé — l'enregistrer écraserait la valeur neuve. */
  const [baseW, setBaseW] = useState<Traitement>(() => traitementDe(nc));
  /* ⚠️ CE QUE LA SAISIE A LU (14/10/2026), envoyé sous `base` pour la fusion à
     trois voies du moteur. Pas `baseW` : celle-ci suit la NC PENDANT la saisie,
     et l'action corrective tapée sur l'ancienne version passerait alors pour
     une réponse à la nouvelle — celle d'un collègue serait écrasée sans un mot.
     `luW` est figée à l'ouverture de l'édition (et quand le brouillon repart de
     la NC), en valeurs BRUTES : le moteur compare à la base, pas à l'écran. */
  const luDe = (n: GedNC): Record<string, unknown> => {
    const brut = n as unknown as Record<string, unknown>;
    return { actionCorrective: brut.actionCorrective ?? '', analyse: brut.analyse ?? null, causes: brut.causes ?? '' };
  };
  const [luW, setLuW] = useState(() => luDe(nc));
  const [editionVue, setEditionVue] = useState(edition);
  if (editionVue !== edition) {
    setEditionVue(edition);
    if (edition) setLuW(luDe(nc));
  }
  const neuve = traitementDe(nc);
  if (JSON.stringify(neuve) !== JSON.stringify(baseW)) {
    setBaseW(neuve);
    if (JSON.stringify(w) === JSON.stringify(baseW)) { setW(neuve); setLuW(luDe(nc)); }
  }

  const enregistrerTraitement = () => run('trait', async () => {
    const corps = {
      actionCorrective: w.actionCorrective,
      ...(sontEgales(w.analyse, baseW.analyse) ? {} : { analyse: w.analyse }),
      base: luW,
    };
    const ok = await smq(`ged/nc/${nc.documentId}/treatment`, corps,
      corrigeAnalyse ? 'Correction enregistrée — événement signé au journal.' : 'Enregistré.');
    if (ok) { setEdition(false); setCorrigeAnalyse(false); }
  });

  // Saisie en cours non enregistrée — remontée au parent pour qu'une fermeture
  // de la fenêtre ne l'efface pas en silence. Dans l'ancienne disposition en
  // deux colonnes, la fiche restait à l'écran : le risque n'existait pas.
  const saisieEnCours = JSON.stringify(w) !== JSON.stringify(baseW);
  useEffect(() => { onDirty?.(saisieEnCours || ficheModifiee); }, [saisieEnCours, ficheModifiee, onDirty]);
  // Les deux fenêtres écoutent Échap sur `window`, et `stopPropagation` n'arrête
  // PAS les autres écouteurs du même élément : sans ce signal, une seule touche
  // fermait le panneau ET le dossier dessous — c'est-à-dire exactement la perte
  // de contexte que N2-4 vient corriger.
  useEffect(() => { onPanneau?.(!!acOuverte); }, [acOuverte, onPanneau]);

  return (
    <div style={enModale || enPanneau
      // En fenêtre, le cadre est celui de la fenêtre : reprendre `card` ici
      // empilerait une carte dans une carte (double bordure, double ombre).
      ? { padding: '4px 2px', display: 'grid', gap: 14 }
      : { ...card, padding: 20, display: 'grid', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13.5, fontWeight: 700, color: 'var(--color-primary-active)' }}>{nc.ref}</span>
        <LienFiche vue="nc" reference={nc.ref} quoi="cette non-conformité" />
        <BoutonJournal reference={nc.ref} quoi="de cette non-conformité" evenements={journal} />
        <Pill meta={graviteMeta(nc.gravite)} />
        <Pill meta={vocabMeta(NC_STATES, nc.statut)} />
        <Anciennete nc={nc} />
      </div>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-strong)' }}>{nc.titre}</h2>
      {/* L'APPAREIL EN CAUSE, SOUS L'INTITULÉ, SUR TOUS LES ONGLETS (RQ,
          03/11/2026) : la curative s'y déclare. Le bandeau n'existait qu'en
          lecture, à l'étape Conséquences : qui corrigeait la fiche ne le voyait
          jamais, et le bouton perdu dans l'en-tête passait inaperçu. Il
          n'apparaît que si la NC désigne un appareil. */}
      {nc.equipementCode && <BandeauPanne nc={nc} enPanneau={enPanneau} />}
      {/* UNE SEULE FRISE (RQ, 03/10/2026) : chaque étape est un état ET un onglet.
          Elle remplace la frise d'états et les onglets de contenu, qui disaient
          deux circuits différents. Les onglets restent cliquables — on relit une
          étape passée, on prépare la suivante — mais l'étape EN COURS est marquée,
          et chaque pied d'étape propose la suivante. */}
      <div style={FRISE_COLLEE}>
      <FriseEtapes statut={nc.statut} actif={onglet}
        repereActif={onglet === 'constat' ? ({ fait: 1, consequences: 2, priseEnCharge: 3 } as const)[sectionConstat] : undefined}
        onChange={(k, ancre) => {
          setOnglet(k);
          // Les trois repères du constat ouvrent CHACUN sa section (onglet).
          const sec = ({ 'nc-sec-fait': 'fait', 'nc-sec-consequences': 'consequences', 'nc-sec-priseEnCharge': 'priseEnCharge' } as const)[ancre as 'nc-sec-fait'];
          if (sec) setSectionConstat(sec);
        }}
        dates={{
          constat: nc.dateDeclaration,
          analyse: dateEvenement(journal, 'PriseEnCharge'),
          traitement: dateEvenement(journal, 'ActionDefinie', 'traitement lancé'),
          cloture: nc.dateCloture,
        }} />
      </div>

      <PanneauDossier id="nc" actif={onglet}>

      <ConsigneEtape cle={onglet} statut={nc.statut} reste={resteAFaire(onglet, nc, linked.filter((a) => AC_BLOQUANTES.includes(a.statut)).length)} />

      {/* À QUI PEUT CORRIGER : les sections 1 à 3 SONT le formulaire de saisie,
          sans détour par une lecture et un « Modifier » (RQ, 13/10/2026). Les
          autres — et tout le monde sur un dossier clôturé — les lisent. */}
      {onglet === 'constat' && peutCorriger && (
        <CorrigerFiche key={nc.documentId} nc={nc} etape={sectionConstat} onEtape={setSectionConstat}
          peutPrendreEnCharge={canManage} onDirty={setFicheModifiee} onSuite={() => setOnglet('analyse')} />
      )}

      {/* Le triage passe AVANT tout le reste du dossier : tant qu'elle n'est
          pas retenue, il n'y a pas de dossier à lire. */}
      {nc.statut === 'soumise' && <TriageNC nc={nc} />}

      {onglet === 'constat' && (
        <>
      {/* ── LE CONSTAT EN TROIS SECTIONS, À LA SUITE (RQ, 13/10/2026) ───────
          « Que ce soit la même interface que la déclaration. » Les trois étapes
          du formulaire, dans le même ordre, lues d'un coup sur une page — et
          chacune se corrige là où elle s'affiche, dans la même mise en page qu'à
          la déclaration. Remplace le bloc « Le constat » et « Toutes les
          rubriques », qui reprenaient les mêmes informations dans un autre
          ordre. Rien n'est masqué : chaque rubrique renseignée a sa section. */}
      {!peutCorriger && sectionConstat === 'fait' && <SectionDossier id="nc-sec-fait" numero="1" titre="Ce qui s’est passé">
        <Rubriques items={[
          { label: 'De quoi s’agit-il', valeur: natureMeta(natureDe(nc))?.label || 'à préciser' },
          { label: 'Comment il a été détecté', valeur: libelleCanal(nc.canal) },
          { label: 'Écart relevé par', valeur: NC_TYPES.find((t) => t.key === nc.ncType)?.label || nc.ncType },
          { label: 'Signalée par le correspondant', valeur: nc.correspondantSignaleur ? nomsTiers.get(nc.correspondantSignaleur)?.nom || nc.correspondantSignaleur : nc.correspondantSignaleur },
          { label: 'Équipement concerné', valeur: nc.equipementCode },
          { label: 'Date de détection', valeur: nc.dateDetection ? fmtDate(nc.dateDetection) : '' },
          { label: 'Détectée par', valeur: nc.detectePar ? userName(settings, nc.detectePar) : '' },
          { label: 'Déclarée le', valeur: nc.dateDeclaration ? fmtDate(nc.dateDeclaration) : '' },
          { label: 'Échantillon concerné', valeur: nc.echantillonRef },
          { label: 'Fiche d’examen visée', valeur: nc.ficheCode },
          { label: 'Motif de refus', valeur: nc.motifRefus },
          { label: 'Décision sur l’échantillon', valeur: nc.decisionEchantillon },
          { label: 'Prescripteur informé', valeur: ouiNon(nc.prescripteurInforme) },
        ]} />
        {/* `pre-wrap` : une description en plusieurs paragraphes se lit comme
            elle a été écrite. */}
        <div>
          <div style={{ ...lbl, marginBottom: 2 }}>Description</div>
          {nc.description
            ? <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{nc.description}</p>
            : <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-faint)', fontStyle: 'italic' }}>Aucune description : l’intitulé dit tout ce que le déclarant a écrit.</p>}
        </div>
        <div>
          <div style={{ ...lbl, marginBottom: 2 }}>Correction immédiate</div>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: nc.correction ? 'var(--text-body)' : 'var(--text-faint)', fontStyle: nc.correction ? 'normal' : 'italic', whiteSpace: 'pre-wrap' }}>
            {nc.correction || 'Non renseignée.'}
          </p>
        </div>
        {nc.source === 'eeq' && (nc.eeqProgramme || nc.eeqAnalyte) && <BandeauEeq nc={nc} enPanneau={enPanneau} />}
        <PiecesNC nc={nc} modifiable={peutCorriger} />
        <ConversationNC nc={nc} />
      </SectionDossier>}

      {/* LES LIENS SOUS LA DESCRIPTION (RQ, 27/10/2026) — ils étaient en bloc à
          la clôture (13/10/2026), loin de ce qu'ils disent : l'équipement, le
          lot, le document que la NC met en cause se lisent avec le constat. Sous
          la section 1, en lecture comme en saisie. L'adresse « …?onglet=liens »
          déjà partagée ouvre cette section et descend jusqu'au bloc. */}
      {sectionConstat === 'fait' && (
        <section id="nc-liens" aria-label="Liens" style={{ border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', padding: '12px 14px', display: 'grid', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-strong)' }}>Liens{nbLiens > 0 ? ` (${nbLiens})` : ''}</span>
          <Liens type="nc" refCourante={nc.ref} />
        </section>
      )}

      {!peutCorriger && sectionConstat === 'consequences' && <SectionDossier id="nc-sec-consequences" numero="2" titre="Conséquences">
        {/* §7.5 — l'impact se précise QUAND on sait : son bandeau se modifie à
            tout moment, et la modification est journalisée. */}
        <BandeauImpact nc={nc} peutModifier={peutCorriger} />
        <Rubriques items={[
          { label: 'Gravité', valeur: graviteMeta(nc.gravite).label },
          { label: 'Erreur imputée au correspondant', valeur: nc.correspondantCode ? nomsTiers.get(nc.correspondantCode)?.nom || nc.correspondantCode : nc.correspondantCode },
          { label: 'Détectée après rendu du résultat', valeur: ouiNon(nc.detecteeApresRendu) },
          { label: 'Résultats rappelés', valeur: ouiNon(nc.resultatsRappeles) },
          // Sans appareil désigné, le bandeau de panne ne s'affiche pas : ses
          // autres rubriques se lisent donc ici.
          ...(nc.equipementCode ? [] : [
            { label: 'Code cause (équipement)', valeur: nc.causeEquip },
            { label: 'Durée d’arrêt', valeur: nc.dureeArret },
          ]),
          ...((nc.source === 'eeq' && (nc.eeqProgramme || nc.eeqAnalyte)) ? [] : [
            { label: 'Programme EEQ', valeur: nc.eeqProgramme },
            { label: 'Z-score EEQ', valeur: nc.eeqZscore === null || nc.eeqZscore === undefined ? '' : String(nc.eeqZscore) },
          ]),
        ]} />
      </SectionDossier>}

      {!peutCorriger && sectionConstat === 'priseEnCharge' && <SectionDossier id="nc-sec-priseEnCharge" numero="3" titre="Prise en charge">
        <Rubriques items={[
          { label: 'Processus concerné par la NC', valeur: nc.processus || 'à qualifier' },
          { label: 'Processus à l’origine de la détection', valeur: nc.processusDetection },
          { label: 'Responsable du traitement', valeur: nc.responsable ? userName(settings, nc.responsable) : 'non désigné' },
          { label: 'Échéance de traitement', valeur: nc.echeanceTraitement ? fmtDate(nc.echeanceTraitement) : 'aucune' },
          { label: 'Clôturée le', valeur: nc.dateCloture ? fmtDate(nc.dateCloture) : '' },
        ]} />
        <DocumentsLies nc={nc} />
      </SectionDossier>}

          {/* Le formulaire porte déjà « ← / → » entre ses trois onglets : on n'y
              ajoute que le passage à l'analyse, depuis la dernière section. */}
          {/* En correction, le passage à l'Analyse est DANS le pied collé du
              formulaire (`onSuite`) : deux pieds collés se recouvriraient. */}
          {peutCorriger ? null
          : sectionConstat !== 'priseEnCharge' ? (
            <div style={{ ...PIED_COLLE, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              {sectionConstat === 'consequences' && (
                <Bouton taille="sm" type="button" onClick={() => setSectionConstat('fait')}>
                  <I n="chevron-left" s={12} />Précédent : Ce qui s’est passé
                </Bouton>
              )}
              <span style={{ flex: 1 }} />
              <Bouton variante="primaire" type="button" onClick={() => setSectionConstat(sectionConstat === 'fait' ? 'consequences' : 'priseEnCharge')}>
                Suivant : {sectionConstat === 'fait' ? 'Conséquences' : 'Prise en charge'}<I n="chevron-right" s={13} />
              </Bouton>
            </div>
          ) : <NavEtapes cle="constat" onAller={setOnglet} />}
        </>
      )}

      {/* ── ÉTAPE 4 — ANALYSE : pourquoi est-ce arrivé ? ───────────────────── */}
      {onglet === 'analyse' && (
        <>
          {!instruit || lectureAnalyse ? (
            <div style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
              <AnalyseCauseLecture analyse={nc.analyse} causes={nc.causes} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Responsable : <strong style={{ color: 'var(--text-strong)' }}>{nc.responsable ? userName(settings, nc.responsable) : '—'}</strong>
                  {' · '}Échéance : <strong style={{ color: 'var(--text-strong)' }}>{nc.echeanceTraitement ? fmtDate(nc.echeanceTraitement) : '—'}</strong>
                  {' · '}<span>se règlent en « Prise en charge » (constat).</span>
                  {nc.statut === 'cloturee' && ' · dossier clôturé'}
                </span>
                <span style={{ flex: 1 }} />
                {instruit && (
                  <Bouton taille="sm" onClick={() => { setW(traitementDe(nc)); setEdition(true); }}>
                    <I n="pen" s={13} />Modifier
                  </Bouton>
                )}
              </div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <AnalyseCauseEditeur valeur={w.analyse} onChange={(analyse) => setW({ ...w, analyse })} />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <span style={{ flex: '1 1 220px', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  Responsable : <strong style={{ color: 'var(--text-strong)' }}>{nc.responsable ? userName(settings, nc.responsable) : '—'}</strong>
                  {' — '}il se désigne en « Prise en charge » (constat, section 3).
                </span>
                <PiedActions
                  taille="sm"
                  annuler={aAnalyse ? { onClick: () => { setW(traitementDe(nc)); setEdition(false); } } : undefined}
                  principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('trait'), disabled: estOccupe(), onClick: () => void enregistrerTraitement() }}
                />
              </div>
            </div>
          )}
          <NavEtapes cle="analyse" onAller={setOnglet}
            transition={nc.statut === 'analyse' && canManage ? {
              libelle: 'Passer au traitement', occupe: estOccupe('traitement'),
              faire: () => void run('traitement', async () => {
                const ok = await smq(`ged/nc/${nc.documentId}/status`, { statut: 'traitement' }, 'Traitement lancé.', `Changement d’état de ${nc.ref}`);
                if (ok) setOnglet('traitement');
              }),
            } : null} />
        </>
      )}

      {/* ── ÉTAPE 5 — PLAN D'ACTIONS : que fait-on pour que cela ne se reproduise pas ? ── */}
      {onglet === 'traitement' && (
        <>
          {!instruit || lectureTraitement ? (
            <div style={{ display: 'grid', gap: 10, background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px' }}>
              <div>
                <div style={lbl}>Correction immédiate</div>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: nc.correction ? 'var(--text-body)' : 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                  {nc.correction || 'Non renseignée.'}
                </p>
              </div>
              <div>
                <div style={lbl}>Action corrective (<Help aide={AIDE_CAPA}>CAPA</Help>)<PointDeRegle ancre="nc.actionCorrective" /></div>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: nc.actionCorrective ? 'var(--text-body)' : 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                  {nc.actionCorrective || 'Non renseignée.'}
                </p>
              </div>
              {instruit && (
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Bouton taille="sm" onClick={() => { setW(traitementDe(nc)); setEdition(true); }}>
                    <I n="pen" s={13} />Modifier
                  </Bouton>
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {/* La correction immédiate se LIT ici et se modifie dans le constat
                  (« Modifier ») — elle se saisissait aux deux endroits, sous deux
                  noms, par deux routes (13/10/2026). */}
              <div>
                <div style={lbl}>Correction immédiate</div>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.55, color: nc.correction ? 'var(--text-body)' : 'var(--text-muted)', whiteSpace: 'pre-wrap' }}>
                  {nc.correction || 'Non renseignée.'}
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)' }}>Elle se modifie dans le constat (bouton « Modifier »).</p>
              </div>
              <div>
                <label style={lbl} htmlFor="cf-action">{ACTION_CORRECTIVE.libelle} (<Help aide={AIDE_CAPA}>CAPA</Help>)<PointDeRegle ancre="nc.actionCorrective" /></label>
                <p style={{ margin: '0 0 6px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-muted)' }}>{ACTION_CORRECTIVE.explication}</p>
                <textarea id="cf-action" value={w.actionCorrective} onChange={(e) => setW({ ...w, actionCorrective: e.target.value })} rows={3}
                  style={{ ...inputStyle, height: 'auto', padding: '9px 12px', resize: 'vertical' }} />
              </div>
              <PiedActions
                taille="sm"
                annuler={aTraitement ? { onClick: () => { setW(traitementDe(nc)); setEdition(false); } } : undefined}
                principale={{ libelle: 'Enregistrer', libelleChargement: 'Enregistrement…', chargement: estOccupe('trait'), disabled: estOccupe(), onClick: () => void enregistrerTraitement() }}
              />
            </div>
          )}

          {!instruit && (
        <div style={{ display: 'grid', gap: 8 }}>
          {linked.length === 0
            ? <EmptyState titre="Aucune action corrective rattachée." indice="Les actions créées à l’étape Traitement apparaissent ici." />
            : linked.map((a) => <ActionLiee key={a.ref} a={a} onOuvrir={ouvrirAction} />)}
        </div>
      )}
          {instruit && (
          <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>
                Plan d’actions — {linked.length} action(s)
              </span>
              {linked.length > 0 && (
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  · {linked.filter((a) => AC_BLOQUANTES.includes(a.statut)).length} non réalisée(s)
                </span>
              )}
            </div>
            {/* Instruites SUR PLACE : échéance, responsable et avancement se
                règlent ici. Constater qu'une action bloque la clôture obligeait
                sinon à quitter la fiche, agir au plan d'actions, puis revenir —
                pour un geste qui tient en deux champs. */}
            {linked.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {linked.map((a) => <ActionLiee key={a.ref} a={a} onOuvrir={ouvrirAction} />)}
              </div>
            )}
            {/* Informer plutôt que plafonner : plusieurs actions pour une même NC
                sont légitimes (§8.7), c'est leur invisibilité qui produisait des
                doublons. */}
            {linked.length > 3 && (
              <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.45 }}>
                {linked.length} actions pour une même non-conformité — vérifiez qu’il ne s’agit pas de doublons.
              </p>
            )}
            {/* ⚠️ LA PROPOSITION, À LA PLACE DE L'ACTION OUVERTE D'OFFICE
                (arbitrage de la RQ, 11/10/2026).

                Jusqu'au 11/10, une action s'ouvrait toute seule à la
                déclaration dès que des résultats patients étaient déclarés
                concernés. Résultat en base : 17 actions sur 23 portaient le
                même intitulé, sans échéance et sans titulaire choisi — donc
                aucune signalée en retard, aucune avancée. L'automatisme
                n'avait pas remplacé la décision, il l'avait rendue invisible.

                L'encart POSE donc la question là où le dossier s'instruit, et
                le bouton juste en dessous est la confirmation : il exige une
                échéance, ce que l'ouverture d'office ne faisait jamais.

                ⚠️ Il ne s'affiche QUE tant qu'aucune action n'est rattachée.
                Le laisser ensuite en ferait un reproche permanent, et c'est
                ainsi qu'on apprend à ne plus lire les encarts. */}
            {propositionAction && linked.length === 0 && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
                background: 'var(--amber-50)', borderRadius: 'var(--radius-md)', padding: '10px 13px' }}>
                <p style={{ margin: 0, flex: '1 1 280px', fontSize: 12.5, lineHeight: 1.5, color: 'var(--amber-700)' }}>
                  <strong>Un plan d’action est attendu.</strong> {propositionAction.motif}
                  {' '}Rien n’est créé sans vous : le bouton ci-dessous demande son échéance et son responsable.
                </p>
              </div>
            )}
            <div>
              <CreerAction nc={nc} responsableDefaut={nc.responsable} dejaCreees={linked.length} onFait={() => undefined} onCreee={setAcARemplir} />
            </div>
          </div>
          )}
          <NavEtapes cle="traitement" onAller={setOnglet} />
        </>
      )}

      {/* ── ÉTAPE 6 — CLÔTURE ─────────────────────────────────────────────── */}
      {onglet === 'cloture' && (
        <>
          {nc.statut === 'traitement' && canManage ? (
            <ClotureNC nc={nc} liees={linked} estRQ={estRQ} onOuvrirAction={ouvrirAction}
              onOuvrirAnalyse={() => setOnglet('traitement')} />
          ) : (
            <div style={{ background: 'var(--surface-sunken)', borderRadius: 'var(--radius-md)', padding: '12px 14px', fontSize: 13.5, lineHeight: 1.55, color: 'var(--text-body)' }}>
              {nc.statut === 'cloturee'
                ? `Dossier clôturé${nc.dateCloture ? ` le ${fmtDate(nc.dateCloture)}` : ''} — l’événement est signé au journal.`
                : nc.statut === 'traitement'
                  ? 'Seuls le responsable du dossier et la RQ peuvent le clôturer.'
                  : 'La clôture s’ouvre une fois le dossier passé au traitement (bouton « Passer au traitement » en bas de l’étape 4, Analyse).'}
            </div>
          )}
          {/* Reprendre un dossier scellé passe par la déclôture motivée (RQ ou
              CQ depuis le 08/10/2026) : trois actes lisibles au journal. */}
          {peutDecloturerNC(session.roles) && nc.statut === 'cloturee' && <DeclotureNC nc={nc} />}
          <NavEtapes cle="cloture" onAller={setOnglet} />
        </>
      )}


      </PanneauDossier>

      {/* BARRE D'ACTION PERSISTANTE — la transition possible à l'instant T, au
          même endroit quel que soit l'onglet consulté. Elle se déplaçait
          verticalement selon la longueur du contenu : cause silencieuse de clic
          manqué, et surtout d'action qu'on ne trouve plus après avoir fait
          défiler. `sticky` la maintient visible quand la section est longue. */}
      {/* PLUS COLLÉE (03/11/2026) : le pied d'étape l'est désormais, et deux
          barres collées en bas se recouvriraient. Elle ferme le dossier, au
          bout du contenu — la suppression n'a pas à rester sous les yeux. */}
      {canManage && (estRQ || nc.statut === 'cloturee' || nc.statut === 'annulee') && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--border-subtle)', paddingTop: 10 }}>
          {/* ⚠️ « Prendre en charge » a été RETIRÉ le 26/08/2026 (décision
              d'Inès) : ouvrir une NC déclarée, quand on est le RQ ou son
              suppléant, EST la prise en charge — la confirmer d'un clic
              n'apportait rien, et les NC lues sans clic restaient « déclarées ».
              Ce que le bouton faisait EN PLUS de la transition — désigner un
              responsable — n'est pas perdu : `ged/nc/:id/lu` inscrit le lecteur,
              et la réaffectation à un tiers reste possible dans l'onglet
              « Analyse & CAPA », qui la trace. */}
          {nc.statut === 'cloturee' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}><I n="check" s={14} />Non-conformité clôturée le {fmtDate(nc.dateCloture)}</span>}
          {nc.statut === 'annulee' && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}><I n="alert" s={14} />Non-conformité annulée — saisie erronée. Le motif est au journal.</span>}
          {/* Le rattrapage se propose tant que le dossier n'est pas scellé, et
              seulement au RQ : c'est lui qui répond de ce qui sort du registre. */}
          {estRQ && nc.statut !== 'cloturee' && nc.statut !== 'annulee' && (
            <SupprimerNC nc={nc} liees={linked} />
          )}
        </div>
      )}

      <ActionRedaction documentId={acARemplir} onClose={() => setAcARemplir(null)} />
      <FenetreAction documentId={acOuverte} refNC={nc.ref} onClose={() => setAcOuverte(null)} />
    </div>
  );
}

/**
 * N2-4 — l'action corrective, ouverte PAR-DESSUS le dossier de non-conformité.
 *
 * ── Ce qui n'allait pas ─────────────────────────────────────────────────────
 *
 * Cliquer une action depuis une NC faisait `go('actions')` : on quittait le
 * registre des non-conformités, on atterrissait dans le plan d'actions, et le
 * dossier qu'on instruisait disparaissait. Pour reprendre, il fallait revenir,
 * retrouver la NC dans 138 lignes, rouvrir le bon onglet. Le geste le plus
 * fréquent du traitement d'une NC — « pourquoi cette action bloque-t-elle ma
 * clôture ? » — coûtait donc la perte de son contexte.
 *
 * ── Pourquoi un panneau, et pas une navigation ──────────────────────────────
 *
 * Le panneau se SUPERPOSE : la fiche de NC reste montée dessous, avec son
 * onglet, son brouillon d'analyse et son mode édition intacts. Le fermer rend
 * exactement l'état d'avant — il n'y a pas de retour à reconstituer, parce
 * qu'on n'est jamais parti.
 *
 * ── Ce que ce panneau NE fait PAS ───────────────────────────────────────────
 *
 * Il ne remplace pas le registre des actions, et il ne fait pas de la fiche
 * d'action une modale. Le plan d'actions reste la vue de plein droit de
 * l'action — partageable, imprimable, sélectionnable. Ce panneau est un aller
 * simple depuis la NC vers ce qui la bloque, et le lien « ouvrir au plan
 * d'actions » y reste, pour qui veut vraiment y aller.
 *
 * Techniquement : `zIndex` supérieur à celui de la fenêtre de NC, et l'action
 * est relue du magasin à chaque rendu (et non figée à l'ouverture) — la
 * démarrer ici met à jour le bandeau de blocage derrière, sans rien recharger.
 */
function FenetreAction({ documentId, refNC, onClose }: { documentId: string | null; refNC: string; onClose: () => void }) {
  const { actions, ouvrirDepuis } = useGed();
  const a = actions.find((x) => x.documentId === documentId) || null;
  const boite = React.useRef<HTMLDivElement | null>(null);
  /* ⚠️ Plus de `useEscape` appelant `onClose` : il contournerait l'alerte de
     saisie non enregistrée. `useSortieFenetre` écoute Échap par la MÊME porte
     que la croix — c'est tout l'intérêt d'une porte unique. */
  const sortie = useSortieFenetre({ boite, onClose });
  if (!a) return null;

  return (
    <div {...sortie.proprietesVoile} role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div ref={boite} {...sortie.proprietesBoite} role="dialog" aria-modal="true" aria-label={`Action corrective ${a.ref}`}
        style={{ width: '100%', maxWidth: 900, maxHeight: '88vh', background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '12px 20px', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-sunken)' }}>
          {/* On DIT ce qui est resté ouvert dessous : sans cette ligne, la
              superposition se lit comme une navigation de plus. */}
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
            Ouverte depuis la non-conformité{' '}
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--color-primary-active)' }}>{refNC}</span>,
            qui reste ouverte dessous.
          </span>
          <span style={{ flex: 1 }} />
          <Bouton taille="sm" onClick={() => ouvrirDepuis(refNC, { view: 'actions', cle: 'selectedAction', valeur: a.documentId })}
            title="Ouvrir l’action au plan d’actions — le retour à la non-conformité reste proposé">Ouvrir au plan d’actions →</Bouton>
          <Bouton taille="sm" onClick={sortie.demanderSortie} aria-label="Fermer et revenir à la non-conformité"><I n="x" s={14} /></Bouton>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 20px' }}>
          {/* `key` : changer d'action REMONTE le dossier — onglet et saisie de
              vérification repartent de zéro, sans effet de resynchronisation. */}
          <ActionDossier key={a.documentId} a={a} enPanneau />
        </div>
      </div>
    </div>
  );
}

/** Fiche NC en fenêtre — le registre reste lisible dessous, en pleine largeur.
 *
 *  `key={nc.documentId}` sur `NCDetail` : changer de NC REMONTE la fiche, ce
 *  qui remet à zéro son onglet, son brouillon et son mode édition — sans effet
 *  de bord, là où un `useEffect` provoquait un rendu en cascade. */
/**
 * ⚠️ LA FICHE S'OUVRE EN FENÊTRE, ET C'EST VOULU (arbitrage SYS-5 du
 * 27/08/2026). Le relevé du 26/08 y voyait une incohérence — six autres
 * registres ouvrent leur fiche à côté d'une liste de 380 px. Mais la NC est le
 * seul dont le registre soit un tableau de 8 colonnes triables : le réduire à
 * une colonne étroite ferait perdre le tri, les pastilles de gravité et la
 * colonne « résultats » que l'on vient d'y faire apparaître (BLOQ-05).
 *
 * Ne pas « harmoniser » ce point sans rouvrir l'arbitrage : la règle, son
 * pourquoi et sa vérification sur les 18 écrans concernés sont dans
 * backend/tests/qualite-patrons-fiche.test.js.
 *
 * Aucun écran étranger n'ouvre cette fenêtre par-dessus lui : tous les points
 * d'entrée (recherche, fiche équipement, lien partageable) font `go('nc')`
 * d'abord. La fenêtre se pose donc toujours sur son propre registre.
 */
function FenetreNC({ nc, onClose }: { nc: GedNC; onClose: () => void }) {
  const [saisieEnCours, setSaisieEnCours] = useState(false);
  // Un panneau d'action est ouvert par-dessus : ni Échap ni un clic hors cadre
  // ne doivent atteindre cette fenêtre, sinon le dossier disparaît sous le
  // panneau qu'on vient à peine d'ouvrir depuis lui.
  const [panneau, setPanneau] = useState(false);
  const { demander, confirmation } = useConfirmation();
  const fermer = React.useCallback(async () => {
    if (panneau) return;
    if (saisieEnCours && !(await demander('Une analyse en cours de saisie n’est pas enregistrée. Fermer la fiche ?', { confirmer: 'Fermer la fiche' }))) return;
    onClose();
  }, [panneau, saisieEnCours, onClose, demander]);
  useEscape(!panneau, fermer);
  /* `fermer()` porte déjà sa propre garde de saisie en cours : ici le hook ne
     sert qu'à rendre le voile inerte et à le faire tressaillir. */
  const refusNc = useRefusVoile();

  return (
    <>
    <div {...refusNc.proprietesVoile} role="presentation"
      style={{ position: 'fixed', inset: 0, zIndex: COUCHES.MODALE, background: VOILE, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div {...refusNc.proprietesBoite} role="dialog" aria-modal="true" aria-label={`Non-conformité ${nc.ref}`}
        /* ⚠️ HAUTEUR FIXE DE NOUVEAU (RQ, 13/10/2026 : « que la fenêtre garde
           toujours la même taille, peu importe le contenu »). La raison de
           l'arbitrage ci-dessous — l'onglet « Liens », trop court — n'existe plus :
           les liens sont un bloc de la clôture. Le contenu défile DANS la fenêtre.
           Historique : HAUTEUR MAXIMALE, ET NON HAUTEUR FIXE (relevé SYS-6 du 26/08/2026 :
           « ~500 px de vide sur l'onglet Liens »).
           `height` forçait la fenêtre à 880 px quel que soit son contenu : sur
           l'onglet « Liens », qui tient souvent en trois lignes, la moitié basse
           était un rectangle blanc — et un grand vide se lit comme un contenu
           qui n'a pas chargé.
           `maxHeight` garde le plafond — au-delà, c'est le contenu qui défile,
           jamais la page — et laisse la fenêtre se refermer sur ce qu'elle
           porte. `minHeight` évite l'inverse : une fenêtre si courte qu'on ne
           la voit pas apparaître. */
        style={{ width: '100%', maxWidth: 1040, height: 'min(880px, 92vh)', background: 'var(--surface-card)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '10px 14px 0' }}>
          <Bouton taille="sm" onClick={fermer} aria-label="Fermer la fiche"><I n="x" s={14} /></Bouton>
        </div>
        {/* Le défilement vit ICI, pas sur la page : la fenêtre garde sa taille
            quel que soit l'onglet ouvert, et le registre ne bouge pas dessous. */}
        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 20px' }}>
          <NCDetail key={nc.documentId} nc={nc} enModale onDirty={setSaisieEnCours} onPanneau={setPanneau} />
        </div>
      </div>
    </div>
    {confirmation}
    </>
  );
}

export function NC({ vue }: { vue?: 'stats' } = {}) {
  const { ncs, actions, selectedNC, select, settings, loading, booted, smqFiltre, session, naviguerVers } = useGed();
  /* ⚠️ LES RÉFÉRENCES DE NC QUI PORTENT DÉJÀ UNE ACTION AU PLAN (11/10/2026).
     C'est ce qui remplace le rappel que l'action d'office produisait toute
     seule : la colonne « Plan d'action » doit pouvoir dire « attendu », et pour
     cela il faut savoir lesquelles en ont une. Un `Set` plutôt qu'un `find` par
     ligne — le registre compte quarante lignes et le plan en compte six, mais
     c'est un balayage par cellule qui se paierait à chaque tri.
     ⚠️ On ne compte PAS les actions annulées : une action défaite n'engage plus
     personne, et la NC redevient sans plan — même raisonnement que sur les
     suggestions (`actionAnnulee`, controllers/suggestion.ts). */
  const ncAvecAction = React.useMemo(
    () => new Set((actions || [])
      .filter((a) => a.origine === 'nc' && a.statut !== 'annulee')
      .map((a) => String(a.origineRef || ''))),
    [actions],
  );
  /* LES STATISTIQUES (Ines, 20/10/2026) ont leur adresse (`/statistiques-nc`, où mène la tâche « Relevé mensuel à
     adresser ») : passer d'un onglet à l'autre change d'adresse. */
  const tab: 'reg' | 'stats' = vue === 'stats' ? 'stats' : 'reg';
  const setTab = (t: 'reg' | 'stats') => { if (t !== tab) naviguerVers(t === 'stats' ? 'statsnc' : 'nc'); };
  // Un sous-onglet = une arrivée : on remonte (règle du 24/08/2026, cf. le crochet).
  useRemonteEnHaut(tab);
  const [declare, setDeclare] = useState(false);
  /** La NC qu'on vient de créer — surlignée quelques secondes, le temps de la
   *  retrouver dans une liste triée où elle ne se range pas en tête. */
  const [recente, setRecente] = useState<string | null>(null);
  const [brouillon, setBrouillon] = useState(false);
  const [q, setQ] = useState('');
  // Filtre demandé par le compteur qui nous a amenés ici — lu au montage
  // seulement (`go` l'efface, donc jamais de filtre hérité d'une visite passée).
  const [filtre, setFiltre] = useState(smqFiltre ?? 'toutes');
  /** Une déclaration qu'on n'a pas le droit d'ouvrir (le RQ et le CQ l'ouvrent ; seul le RQ la tranche). */
  const verrouillee = (n: GedNC) => ncSoumise(n) && !peutOuvrirNCNonRetenue(session.roles);
  const MOTIF_VERROU = 'Déclaration pas encore retenue comme non-conformité : son dossier ne s’ouvre '
    + 'qu’au responsable qualité et au coordinateur qualité, qui décident d’abord si c’en est une.';
  /* ⚠️ LE LIEN PROFOND AUSSI (07/10/2026). La fiche s'ouvre par URL
     (`/non-conformites/:ref`) autant que par clic : ne fermer que la ligne
     aurait laissé la porte de derrière ouverte, et c'est celle qu'on garde
     dans un onglet ou qu'on colle dans un message. */
  const sel = (() => {
    const n = ncs.find((x) => x.documentId === selectedNC) || null;
    return n && verrouillee(n) ? null : n;
  })();
  const selVerrouille = !!selectedNC && !sel && ncs.some((n) => n.documentId === selectedNC);
  const enChargement = !booted || (loading && ncs.length === 0);

  const ouvertes = ncs.filter(ncOuverte);
  const critiques = ncs.filter((n) => n.gravite === 'critique' && n.statut !== 'cloturee');
  const cloturees = ncs.filter((n) => n.statut === 'cloturee');
  const seuilAnciennete = useSeuilAnciennete();
  const anciennes = ouvertes.filter((n) => (joursDepuis(n.dateDeclaration) ?? 0) > seuilAnciennete);

  const kpis = [
    { v: ncs.length, l: 'Total', k: 'toutes' },
    { v: ouvertes.length, l: 'Ouvertes', k: 'ouvertes' },
    { v: critiques.length, l: 'Critiques ouvertes', k: 'critiques', rouge: true },
    { v: cloturees.length, l: 'Clôturées', k: 'cloturees' },
  ];

  const listeFiltree = useMemo(() => {
    const base = filtre === 'ouvertes' ? ouvertes
      : filtre === 'critiques' ? critiques
        : filtre === 'cloturees' ? cloturees
          : filtre === 'anciennes' ? anciennes
            : ncs;
    const qq = q.trim().toLowerCase();
    const cherche = qq ? base.filter((n) => `${n.ref} ${n.titre} ${n.processus} ${n.docLie} ${userName(settings, n.responsable)}`.toLowerCase().includes(qq)) : base;
    // Les plus graves et les plus anciennes d'abord.
    const rangGravite = (g: string) => (g === 'critique' ? 0 : g === 'majeure' ? 1 : 2);
    return [...cherche].sort((a, b) => {
      const oa = a.statut === 'cloturee' ? 1 : 0, ob = b.statut === 'cloturee' ? 1 : 0;
      if (oa !== ob) return oa - ob;
      const ga = rangGravite(a.gravite), gb = rangGravite(b.gravite);
      if (ga !== gb) return ga - gb;
      return (a.dateDeclaration || '').localeCompare(b.dateDeclaration || '');
    });
  }, [ncs, filtre, q, settings, ouvertes, critiques, cloturees, anciennes]);

  /* L'export de référence part du REGISTRE et porte la liste FILTRÉE : filtrer
     « critiques ouvertes » puis exporter ne rend plus les douze NC sans le dire
     (relevé B-4). Les colonnes sont fixes — l'ordre ni la largeur réglés à
     l'écran n'y changent rien. */
  const exporterRegistre = () => telechargerExport<GedNC>({
    registre: 'nc',
    libelleRegistre: 'Registre des non-conformités',
    auteur: session.name || session.key,
    lignes: listeFiltree,
    colonnes: [
      { libelle: 'Référence', valeur: (n) => n.ref },
      { libelle: 'Titre', valeur: (n) => n.titre },
      { libelle: 'Source', valeur: (n) => n.source },
      { libelle: 'Canal de détection', valeur: (n) => n.canal || '' },
      { libelle: 'Processus', valeur: (n) => n.processus },
      { libelle: 'Gravité', valeur: (n) => n.gravite },
      // Les VALEURS restent les clés (interne / externe) : un tableur qui les filtre ne casse pas.
      { libelle: 'Écart relevé par (interne / externe)', valeur: (n) => n.ncType },
      { libelle: 'Statut', valeur: (n) => n.statut },
      { libelle: 'Déclarée le', valeur: (n) => (n.dateDeclaration ? fmtDate(n.dateDeclaration) : '') },
      { libelle: 'Clôturée le', valeur: (n) => (n.dateCloture ? fmtDate(n.dateCloture) : '') },
    ],
    filtres: [
      ...(filtre !== 'toutes' ? [`Famille : ${({ ouvertes: 'ouvertes', critiques: 'critiques', anciennes: `ouvertes depuis plus de ${seuilAnciennete} j`, cloturees: 'clôturées' } as Record<string, string>)[filtre] ?? filtre}`] : []),
      ...(q.trim() ? [`Recherche « ${q.trim()} »`] : []),
    ],
  });

  return (
    // `minmax(0, 1fr)` et non le `1fr` implicite (RQ, 19/08/2026).
    //
    // Un élément de grille vaut `min-width: auto` par défaut : il refuse de
    // descendre sous sa largeur minimale de contenu. La colonne unique de cette
    // grille se dimensionnait donc sur le plus large de ses enfants — le
    // registre — et TOUS les autres s'étiraient avec : le bandeau de titre, les
    // tuiles de comptage et la barre de filtres dépassaient de 62 px à droite,
    // ce qui faisait sortir le bouton « Déclarer une NC » du cadre.
    //
    // Le `0` du minmax autorise la colonne à se réduire ; le registre, lui, a
    // déjà son propre défilement horizontal et n'a pas besoin de pousser ses
    // voisins pour être lisible.
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 18 }}>
      <TitrePage eyebrow={<>Amélioration continue<Norme art="7.5" /></>} titre="Non-conformités"
        sousTitre={<span style={{ fontStyle: 'italic' }}>« Tout ce qui ne va pas, on l’écrit. »</span>}
        actions={
          <Bouton variante="primaire" onClick={() => { setDeclare(true); setTab('reg'); select('selectedNC', null); }}>
            <I n="plus" s={15} />Déclarer une NC{!declare && brouillon ? ' (brouillon conservé)' : ''}
          </Bouton>
        } />

      <Segment ariaLabel="Vue" valeur={tab} onChange={setTab}
        options={[{ valeur: 'reg' as const, libelle: 'Registre & traitement' }, { valeur: 'stats' as const, libelle: 'Statistiques' }]} />

      {tab === 'stats' ? <StatistiquesNC /> : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12 }}>
            {enChargement ? <SkeletonTiles n={4} /> : kpis.map((k) => {
              const on = filtre === k.k;
              return (
                <button key={k.k} onClick={() => setFiltre(k.k)} className="ged-btn" aria-pressed={on} title={`Filtrer sur « ${k.l} »`}
                  style={{ ...card, padding: '14px 16px', textAlign: 'start', cursor: 'pointer', font: 'inherit', fontFamily: 'var(--font-sans)', borderColor: on ? 'var(--cyan-200)' : undefined, background: on ? 'var(--cyan-50)' : 'var(--surface-card)' }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 24, fontWeight: 600, color: k.rouge && k.v > 0 ? 'var(--red-600)' : 'var(--navy-900)', lineHeight: 1 }}>{k.v}</div>
                  <div style={{ fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginTop: 6 }}>{k.l}</div>
                </button>
              );
            })}
          </div>

          <DeclareForm
            visible={declare}
            onDirty={setBrouillon}
            onDone={(ref) => {
              setDeclare(false);
              /* ⚠️ SURLIGNAGE COURT, pas un état durable. La ligne créée se
                 range à sa place dans le tri (13ᵉ position sur le registre
                 réel) : sans repère, on ne sait pas laquelle vient d'apparaître.
                 Six secondes suffisent à la retrouver ; au-delà, un surlignage
                 qui reste devient une décoration qu'on cesse de voir. */
              if (ref) {
                setRecente(ref);
                window.setTimeout(() => setRecente((r) => (r === ref ? null : r)), 6000);
              }
            }}
            onOuvrir={(ref) => {
              const cible = ncs.find((n) => n.ref === ref);
              if (cible) select('selectedNC', cible.documentId);
            }}
          />

          {/* Le registre occupe TOUTE la largeur et se lit en tableau : le
              découpage précédent (liste de 360 px + fiche à droite) montrait
              trois informations par ligne sur un registre qui en compte huit,
              et donnait à la fiche une colonne trop étroite pour ses onglets.
              La fiche s'ouvre désormais en fenêtre, au-dessus du registre. */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Bouton taille="sm" type="button" onClick={exporterRegistre} disabled={!listeFiltree.length}
                title="Exporte les non-conformités affichées dans le registre, avec la date, l’auteur et les filtres en tête.">
                <I n="diff" s={13} />Exporter (CSV)
              </Bouton>
            </div>
            <FilterBar
              q={q} onQ={setQ} placeholder="Rechercher une non-conformité…"
              chips={[
                { k: 'toutes', label: 'Toutes', n: ncs.length },
                { k: 'ouvertes', label: 'Ouvertes', n: ouvertes.length },
                { k: 'critiques', label: 'Critiques', n: critiques.length },
                { k: 'anciennes', label: `Ouvertes > ${seuilAnciennete} j`, n: anciennes.length },
                { k: 'cloturees', label: 'Clôturées', n: cloturees.length },
              ]}
              actif={filtre} onChip={setFiltre}
              total={ncs.length} affiches={listeFiltree.length}
              onReset={() => { setQ(''); setFiltre('toutes'); }} />
            <div style={{ ...card, overflow: 'hidden' }}>
              {/* L3 (10/10/2026) : niveau AVANCÉ — « Référence » épinglée et
                  obligatoire, filtres par colonne avec leurs libellés, états
                  intégrés. L'export de référence reste le bouton du registre
                  (L-1) : un seul export. */}
              {(
                <Tableau
                  id="nc"
                  ajusterLargeur
                  niveau="avance"
                  chargement={enChargement}
                  vide={{
                    titre: ncs.length === 0 ? 'Aucune non-conformité enregistrée.' : 'Aucune non-conformité ne correspond à ces critères.',
                    indice: ncs.length === 0 ? 'Utilisez « Déclarer une NC » — une référence est générée automatiquement.' : 'Élargissez la recherche ou revenez à « Toutes ».',
                    action: ncs.length > 0
                      ? <Bouton variante="discret" taille="sm" onClick={() => { setQ(''); setFiltre('toutes'); }}>Réinitialiser les filtres</Bouton>
                      : undefined,
                  }}
                  /* ⚠️ LE REGISTRE DÉBORDAIT LA PAGE À DROITE (relevé SYS-10 du
                     26/08/2026, colonne « Ancienneté » coupée à 1400 px).
                     Le mode défilement existait, il n'était pas activé ici.
                     Il fait défiler le tableau DANS SA PROPRE BOÎTE : la page,
                     elle, ne part jamais de côté — un défilement horizontal de
                     page emmène l'en-tête et la barre latérale avec lui, et
                     l'on ne sait plus où l'on est.
                     ⚠️ Contrepartie assumée : l'en-tête collant se cale sur la
                     boîte et non sur la page (CSS ne permet pas `overflow-x:
                     auto` avec `overflow-y: visible`). Dans un tableau qui
                     défile, c'est d'ailleurs le comportement attendu. */
                  defilementHorizontal
                  lignes={listeFiltree}
                  cleLigne={(n) => n.documentId}
                  /* La rangée n'est plus focalisable ni annoncée comme bouton :
                     c'est la référence, en première cellule, qui est un vrai
                     `<a href>` (`CelluleLien`). Deux commandes imbriquées se
                     tabuleraient et s'annonceraient deux fois. */
                  ligneProps={(n) => {
                    const style = {
                      // Le liseré de gravité borde la LIGNE : c'est ce qui fait
                      // qu'un registre trié par date laisse quand même voir les
                      // critiques au premier coup d'œil.
                      borderInlineStart: `3px solid ${graviteMeta(n.gravite).fg}`,
                      ...(n.ref === recente
                        ? { background: 'var(--amber-50)', boxShadow: 'inset 0 0 0 1px var(--amber-300)' }
                        : null),
                    };
                    /* ⚠️ LA LIGNE RESTE VISIBLE, ET C'EST VOULU. La masquer
                       priverait le déclarant de la seule preuve que sa
                       déclaration est arrivée — et une déclaration qu'on croit
                       perdue, on ne la refait pas. Ce qui disparaît, c'est le
                       GESTE : plus de curseur, plus de rôle `button`, plus de
                       tabulation, et l'infobulle dit pourquoi. Laisser la
                       rangée cliquable pour qu'elle échoue au serveur aurait
                       été la pire des trois options. */
                    if (verrouillee(n)) {
                      return { title: MOTIF_VERROU, style: { ...style, cursor: 'default', opacity: 0.72 } };
                    }
                    return ligneOuvrable(
                      () => { select('selectedNC', n.documentId); setDeclare(false); },
                      `Ouvrir ${n.ref}`,
                      style,
                      true,
                    );
                  }}
                  colonnes={[
                    {
                      cle: 'ref', libelle: 'Référence', valeur: (n) => n.ref, epinglee: true, obligatoire: true,
                      style: { fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary-active)', whiteSpace: 'nowrap' },
                      /* La RÉFÉRENCE porte le lien : c'est elle qu'on copie,
                         qu'on cite et qu'on colle dans un courriel.
                         ⚠️ Sauf sur une déclaration pas encore retenue : un
                         `<a href>` laissé en place serait focalisable au
                         clavier et ouvrirait par l'URL ce que le clic refuse.
                         La référence reste LISIBLE — c'est elle qu'on cite au
                         RQ pour demander où en est sa déclaration. */
                      rendu: (n) => (verrouillee(n)
                        ? <span>{n.ref}</span>
                        : <CelluleLien href={cheminDe('nc', n.ref)}>{n.ref}</CelluleLien>),
                    },
                    {
                      cle: 'titre', libelle: 'Intitulé', valeur: (n) => n.titre,
                      style: { whiteSpace: 'normal', fontWeight: 600, color: 'var(--text-strong)', lineHeight: 1.35, minWidth: 260 },
                      rendu: (n) => n.titre,
                    },
                    {
                      /* ⚠️ « Des résultats patients sont concernés » n'était
                         visible que dans la FICHE (relevé BLOQ-05) : un bandeau
                         rouge qu'il fallait ouvrir la NC pour voir. Au registre,
                         rien — ni colonne, ni pastille, ni compteur. La seule
                         question qui peut obliger à rappeler des comptes rendus
                         déjà partis ne se voyait pas dans la liste qu'on
                         parcourt. */
                      cle: 'resultats', libelle: 'Résultats',
                      valeur: (n) => (n.impactResultats === 'oui' ? 0 : n.impactResultats === 'inconnu' ? 1 : 2),
                      type: 'liste', libelleValeur: (v) => ({ 0: 'résultats concernés', 1: 'à statuer', 2: 'non concernés' } as Record<string, string>)[v] ?? v,
                      rendu: (n) => (n.impactResultats === 'oui' ? (
                        <span title="Des résultats patients sont concernés (§7.5)"
                          style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--red-600)', border: '1.5px solid var(--red-600)', borderRadius: 'var(--radius-pill)', padding: '1px 7px', whiteSpace: 'nowrap' }}>résultats</span>
                      ) : n.impactResultats === 'inconnu' ? (
                        <span title="La question « des résultats patients sont-ils concernés ? » n’a pas encore de réponse"
                          style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--amber-600)', cursor: 'help' }}>à statuer</span>
                      ) : <span style={{ color: 'var(--text-muted)' }}>—</span>),
                    },
                    {
                      /* ⚠️ « MAJEURE SANS PLAN D'ACTION » SE VOIT ICI, ET NULLE
                         PART AILLEURS (arbitrage de la RQ, 11/10/2026).

                         Jusqu'au 11/10, une action s'ouvrait d'office : la
                         question « cette NC a-t-elle une suite ? » ne se posait
                         donc jamais, puisque la réponse était fabriquée. Elle a
                         été fabriquée 17 fois, sans échéance et sans titulaire,
                         et aucune de ces 17 suites n'a avancé.

                         En retirant l'automatisme, on a retiré le rappel avec.
                         Il revient ici — à l'endroit où le registre se parcourt,
                         et SANS créer de tâche de plus : une pastille se lit en
                         passant, une tâche opposable s'éteint par lassitude.
                         Même doctrine que la colonne « Résultats » juste
                         au-dessus, née du même relevé.

                         ⚠️ La pastille ne porte AUCUN jugement sur les
                         mineures : le §8.7 n'attend une action que sur ce qui
                         le justifie, et faire clignoter tout le registre
                         apprendrait seulement à ne plus le regarder. */
                      cle: 'plan', libelle: 'Plan d’action',
                      valeur: (n) => (ncAvecAction.has(n.ref) ? 0 : planAttendu(n) ? 1 : 2),
                      type: 'liste',
                      libelleValeur: (v) => ({ 0: 'au plan', 1: 'attendu', 2: 'sans objet' } as Record<string, string>)[v] ?? v,
                      rendu: (n) => {
                        if (ncAvecAction.has(n.ref)) {
                          return (
                            <span title="Une action du plan est rattachée à cette non-conformité"
                              style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--green-600)' }}>au plan</span>
                          );
                        }
                        if (planAttendu(n)) {
                          return (
                            <span title="Non-conformité majeure ou critique sans aucune action au plan — le §8.7 attend une action, ou la raison écrite de ne pas en ouvrir. Elle se propose à l’étape Traitement de la fiche."
                              style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--amber-700)', border: '1.5px solid var(--amber-600)', borderRadius: 'var(--radius-pill)', padding: '1px 7px', whiteSpace: 'nowrap', cursor: 'help' }}>attendu</span>
                          );
                        }
                        return <span style={{ color: 'var(--text-muted)' }}>—</span>;
                      },
                    },
                    {
                      cle: 'gravite',
                      libelle: 'Gravité',
                      // Trié par SÉVÉRITÉ, pas par ordre alphabétique : « critique »
                      // avant « mineure » est ce qu'on attend d'un tri de gravité,
                      // et l'alphabet donnerait exactement l'inverse.
                      valeur: (n) => RANG_GRAVITE[String(n.gravite)] ?? 99,
                      type: 'liste', libelleValeur: (v) => NC_GRAVITES.find((g) => String(RANG_GRAVITE[g.key]) === v)?.label ?? v,
                      rendu: (n) => <Pill meta={graviteMeta(n.gravite)} />,
                    },
                    {
                      cle: 'statut', libelle: 'Statut', valeur: (n) => n.statut,
                      type: 'liste', libelleValeur: (v) => NC_STATES[v]?.label ?? v,
                      rendu: (n) => <Pill meta={vocabMeta(NC_STATES, n.statut)} />,
                    },
                    {
                      cle: 'processus', libelle: 'Processus', valeur: (n) => n.processus || '', type: 'liste',
                      style: { color: 'var(--text-muted)' },
                      rendu: (n) => n.processus || '—',
                    },
                    {
                      cle: 'responsable', libelle: 'Responsable', type: 'liste',
                      valeur: (n) => (n.responsable ? userName(settings, n.responsable) : ''),
                      style: { color: 'var(--text-muted)', whiteSpace: 'nowrap' },
                      rendu: (n) => (n.responsable ? userName(settings, n.responsable) : '—'),
                    },
                    {
                      cle: 'declaree', libelle: 'Déclarée le',
                      // La date BRUTE (AAAA-MM-JJ) et non l'affichage JJ/MM :
                      // trier sur le texte affiché classerait le 02/01 avant le
                      // 30/12 de l'année précédente.
                      valeur: (n) => n.dateDeclaration || '', type: 'date',
                      style: { color: 'var(--text-muted)', whiteSpace: 'nowrap' },
                      rendu: (n) => fmtDate(n.dateDeclaration),
                    },
                    {
                      cle: 'anciennete', libelle: 'Ancienneté', type: 'nombre',
                      valeur: (n) => joursOuverts(n),
                      rendu: (n) => <Anciennete nc={n} />,
                    },
                  ]}
                />
              )}
            </div>
          </div>

          {sel && <FenetreNC nc={sel} onClose={() => select('selectedNC', null)} />}
          {/* ⚠️ ON NE REFERME PAS EN SILENCE. Arriver par un lien partagé sur
              une déclaration non retenue et ne rien voir se lit comme « la NC
              a été supprimée » — alors qu'elle attend simplement une décision.
              Le refus se dit, et il nomme qui décide. */}
          {selVerrouille && (
            <div style={{ ...card, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'baseline', borderInlineStart: '3px solid var(--amber-500)' }}>
              <I n="alert-triangle" s={14} />
              <div style={{ fontSize: 12.5, color: 'var(--text-body)', lineHeight: 1.45 }}>
                <strong style={{ color: 'var(--text-strong)' }}>Dossier pas encore ouvert.</strong>{' '}
                {MOTIF_VERROU} Vous serez informé de la décision.
              </div>
              <span style={{ flex: 1 }} />
              <Bouton taille="sm" onClick={() => select('selectedNC', null)}>Revenir au registre</Bouton>
            </div>
          )}
        </>
      )}
    </div>
  );
}
